/**
 * EditorialArticlePage, LWD Immersive Editorial System
 *
 * Template router + full block renderer with cinematic media blocks.
 *
 * Block types supported:
 *   paragraph, heading, subheading, quote, pull_quote, divider
 *   image              , inline constrained image
 *   full_bleed_image   , static edge-to-edge editorial pause
 *   parallax_image     , full-width with subtle scroll depth (JS, mobile-safe)
 *   diptych            , two-image editorial spread
 *   film_strip         , horizontal scroll gallery, touch-native
 *   ambient_video      , muted autoplay cinematic loop with sound toggle
 *
 * Templates:
 *   cinematic (default), 100svh hero, huge type, dark / light toggle
 *   cover / editorial_opener / portrait / documentary_spread, (coming)
 */

import { useState, useEffect, useRef, useCallback, useMemo, createContext, useContext } from 'react';
import { usePrerenderReady } from '../../utils/prerenderReady';
import { Helmet } from 'react-helmet-async';
import { resolveIdentity } from '../../seo/identityEngine.js';
import JsonLd from '../../components/seo/JsonLd.jsx';
import { buildArticleSchema, buildFaqPageSchema } from '../../utils/structuredData.js';
import {
  DARK, LIGHT, GOLD, FD, FU, FS,
  EDITORIAL_CATEGORIES, resolveTagCategory,
} from './editorialTheme.js';
import { supabase } from '../../lib/supabaseClient.js';
import ProfileLightbox from '../../components/profile/Lightbox.jsx';
import { Masthead } from './EditorialHomePage.jsx';
import { NewsletterEditorial } from './EditorialMagazineHome.jsx';
import { useEditorialLights } from './useEditorialLights.js';
import DirectoryNav from './DirectoryNav.jsx';
import GalleryLightbox from './GalleryLightbox.jsx';
import EditorialSectionBlock from './EditorialSectionBlock.jsx';
import VTSectionBlock from './VTSectionBlock.jsx';
import { safeHtml } from '../../utils/safeHtml.js';
import { track } from '../../utils/track.js';
import { subscribeNewsletter } from '../../services/newsletterService.js';
import { fetchRelatedForPost, listingUrl, listingImage } from '../../services/relatedContentService.js';
import ProductCard from '../Magazine/components/ProductCard.jsx';
import ShopProductsBlock from './ShopProductsBlock.jsx';
import { StyleAdvice } from '../Magazine/components/FashionModules.jsx';
import AdminImageOverlay from '../../components/admin/AdminImageOverlay.jsx';
import VenueEnquireCard from '../../components/cards/editorial/VenueEnquireCard';
import { imgUrl } from '../../utils/imgUrl.js';
import { isPubliclyRoutable } from '../../utils/publicRoutability.js';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://luxuryweddingdirectory.com';

// Show the full-height newsletter block at the foot of an article. Hidden (it
// dominated the end of the piece); replaced by the slide-out NewsletterDrawer
// below. The newsletter still appears on the homepage and magazine home. Set
// true to restore the inline block.
const SHOW_ARTICLE_FOOT_NEWSLETTER = false;

// ── NewsletterDrawer ──────────────────────────────────────────────────────────
// A slim vertical tab on the right edge ("The Weekly Note") that slides out a
// compact signup panel on click. Replaces the full-height article-foot block so
// the newsletter stays one tap away without dominating the page. Fixed-position
// (z-index above the sticky cover-hero); closes on backdrop click or Escape;
// respects prefers-reduced-motion. Uses the real subscribeNewsletter pipeline.
function NewsletterDrawer() {
  const [open, setOpen]     = useState(false);
  const [email, setEmail]   = useState('');
  const [hp, setHp]         = useState('');
  const [status, setStatus]   = useState('idle'); // idle | loading | done | error
  const [err, setErr]         = useState('');
  const [isDesktop, setIsDesktop] = useState(false);
  const [scrolled, setScrolled]   = useState(false);

  const PAPER = '#FAF8F4';
  const INK   = '#181410';
  const MUTE  = 'rgba(24,20,16,0.62)';
  const reduce = typeof window !== 'undefined' && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Desktop only, the drawer is hidden on mobile.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(min-width: 768px)');
    const apply = () => setIsDesktop(mq.matches);
    apply();
    mq.addEventListener ? mq.addEventListener('change', apply) : mq.addListener(apply);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', apply) : mq.removeListener(apply); };
  }, []);

  // Fade the tab in once the reader has scrolled past the hero (~0.8 viewport).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onScroll = () => setScrolled(window.scrollY > window.innerHeight * 0.8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (status === 'loading') return;
    setStatus('loading'); setErr('');
    const { ok, error } = await subscribeNewsletter({ email, source: 'article-drawer', honeypot: hp });
    if (ok) { setStatus('done'); try { track('newsletter_subscribe', { source: 'article-drawer' }); } catch { /* noop */ } }
    else    { setStatus('error'); setErr(error || 'Something went wrong. Please try again.'); }
  };

  if (!isDesktop) return null; // not on mobile

  return (
    <>
      {/* Edge tab, fades in after the reader scrolls past the hero */}
      <button
        onClick={() => setOpen(true)}
        aria-label="Open the weekly newsletter"
        style={{
          position: 'fixed', left: 0, top: '50%', zIndex: 120,
          transform: scrolled ? 'translateY(-50%) translateX(0)' : 'translateY(-50%) translateX(-10px)',
          opacity: scrolled ? 1 : 0, pointerEvents: scrolled ? 'auto' : 'none',
          transition: reduce ? 'none' : 'opacity 0.5s ease, transform 0.5s ease',
          display: open ? 'none' : 'flex', flexDirection: 'column', alignItems: 'center', gap: 11,
          background: '#14110d', color: '#f6f1e8',
          border: '1px solid rgba(201,169,110,0.42)', borderLeft: 'none',
          borderRadius: '0 3px 3px 0', padding: '20px 11px', cursor: 'pointer',
          writingMode: 'vertical-rl', textOrientation: 'mixed',
          fontFamily: FU, fontSize: 10, fontWeight: 600, letterSpacing: '0.26em', textTransform: 'uppercase',
          boxShadow: '0 2px 18px rgba(0,0,0,0.18)',
        }}
      >
        <span aria-hidden style={{ width: 6, height: 6, background: GOLD, transform: 'rotate(45deg)' }} />
        The Weekly Note
        <span aria-hidden style={{ width: 6, height: 6, background: GOLD, transform: 'rotate(45deg)' }} />
      </button>

      {/* Backdrop */}
      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(10,8,6,0.42)', zIndex: 119 }}
        />
      )}

      {/* Panel */}
      <aside
        aria-hidden={!open}
        style={{
          position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 121,
          width: 'clamp(300px, 92vw, 404px)', background: PAPER, color: INK,
          boxShadow: '12px 0 44px rgba(0,0,0,0.20)',
          transform: open ? 'translateX(0)' : 'translateX(-102%)',
          transition: reduce ? 'none' : 'transform 440ms cubic-bezier(0.22,0.7,0.2,1)',
          display: 'flex', flexDirection: 'column',
          padding: 'clamp(28px,4vw,44px)', overflowY: 'auto',
        }}
      >
        <button
          onClick={() => setOpen(false)}
          aria-label="Close"
          style={{
            position: 'absolute', top: 16, right: 18, background: 'none', border: 'none',
            cursor: 'pointer', color: MUTE, fontSize: 22, lineHeight: 1, padding: 6,
          }}
        >×</button>

        <div style={{ fontFamily: FU, fontSize: 10, fontWeight: 600, letterSpacing: '0.3em', textTransform: 'uppercase', color: GOLD, margin: '6px 0 18px' }}>
          Weekly Correspondence
        </div>
        <h2 style={{ fontFamily: FD, fontStyle: 'italic', fontWeight: 400, fontSize: 'clamp(26px,3.4vw,34px)', lineHeight: 1.12, color: INK, margin: '0 0 18px' }}>
          A weekly note from the editorial desk.
        </h2>
        <p style={{ fontFamily: FU, fontWeight: 300, fontSize: 14, lineHeight: 1.7, color: MUTE, margin: '0 0 22px' }}>
          Notes from the road, rare hotel openings worth the journey, and photographs that never make it into the magazine. Sent on Sundays, never more than once a week.
        </p>

        <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 26px' }}>
          {['First access to cover stories, ahead of release', 'Editor-vetted addresses, before they are public', 'Private invitations to LWD gatherings'].map(t => (
            <li key={t} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 12, fontFamily: FU, fontSize: 13, lineHeight: 1.55, color: INK }}>
              <span aria-hidden style={{ width: 5, height: 5, background: GOLD, transform: 'rotate(45deg)', marginTop: 7, flexShrink: 0 }} />
              {t}
            </li>
          ))}
        </ul>

        {status === 'done' ? (
          <div style={{ fontFamily: FD, fontStyle: 'italic', fontSize: 18, lineHeight: 1.5, color: INK, borderTop: `1px solid ${GOLD}55`, paddingTop: 22 }}>
            Thank you. The next note will find you on Sunday.
          </div>
        ) : (
          <form onSubmit={submit}>
            <input
              type="text" value={hp} onChange={e => setHp(e.target.value)}
              tabIndex={-1} autoComplete="off" aria-hidden="true"
              style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
            />
            <input
              type="email" required value={email} onChange={e => setEmail(e.target.value)}
              placeholder="Your email"
              style={{
                width: '100%', background: 'none', border: 'none', borderBottom: `1px solid ${GOLD}88`,
                padding: '10px 2px', fontFamily: FD, fontStyle: 'italic', fontSize: 18, color: INK,
                outline: 'none', marginBottom: 22,
              }}
            />
            <button
              type="submit" disabled={status === 'loading'}
              style={{
                width: '100%', background: GOLD, color: '#1b160e', border: 'none', cursor: 'pointer',
                padding: '14px 18px', fontFamily: FU, fontSize: 11, fontWeight: 700,
                letterSpacing: '0.26em', textTransform: 'uppercase',
                opacity: status === 'loading' ? 0.7 : 1,
              }}
            >
              {status === 'loading' ? 'Sending' : 'Send me the Edit'}
            </button>
            {status === 'error' && (
              <div style={{ fontFamily: FU, fontSize: 11, color: '#a3403a', marginTop: 12 }}>{err}</div>
            )}
            <div style={{ fontFamily: FD, fontStyle: 'italic', fontSize: 12, color: 'rgba(24,20,16,0.42)', marginTop: 16 }}>
              We never share your details. Unsubscribe at any time.
            </div>
          </form>
        )}
      </aside>
    </>
  );
}

// ── helpers ──────────────────────────────────────────────────────────────────

function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}
function slugifyLabel(str) {
  return (str || '').toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
}
// Derive issue label from publish date: Jan–Jun → Spring · Summer, Jul–Dec → Autumn · Winter
function getIssueLabel(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  const y = d.getFullYear();
  return d.getMonth() < 6 ? `Spring · Summer ${y}` : `Autumn · Winter ${y}`;
}
function getCategoryLabel(post) {
  if (post.category_label) return post.category_label;
  const tagCat = (post.tags || []).map(resolveTagCategory).find(Boolean);
  const found  = EDITORIAL_CATEGORIES.find(c => c.id === tagCat);
  return found ? found.label : (post.tags && post.tags[0]) || null;
}
function getCategoryAccent(post) {
  const labelSlug = slugifyLabel(post.category_label);
  const tagCat    = (post.tags || []).map(resolveTagCategory).find(Boolean);
  const id        = EDITORIAL_CATEGORIES.find(c => c.id === labelSlug)?.id || tagCat;
  return EDITORIAL_CATEGORIES.find(c => c.id === id)?.accent || GOLD;
}

// ── ArticleSaveButton, bookmark icon, localStorage-backed save state ─────────
// SVG bookmark shape (hollow / filled). Appears in the sticky meta panel and
// optionally inline in article headers. Slug is the save key.

const SAVED_KEY = 'lwd_saved_articles';

function useSavedArticle(slug) {
  const [saved, setSaved] = useState(() => {
    if (!slug || typeof window === 'undefined') return false;
    try {
      const list = JSON.parse(localStorage.getItem(SAVED_KEY) || '[]');
      return list.includes(slug);
    } catch { return false; }
  });

  const toggle = useCallback(() => {
    if (!slug) return;
    setSaved(prev => {
      const next = !prev;
      try {
        const list = JSON.parse(localStorage.getItem(SAVED_KEY) || '[]');
        const updated = next
          ? [...new Set([...list, slug])]
          : list.filter(s => s !== slug);
        localStorage.setItem(SAVED_KEY, JSON.stringify(updated));
      } catch {}
      return next;
    });
  }, [slug]);

  return [saved, toggle];
}

function BookmarkIcon({ filled, size = 18, color = 'currentColor' }) {
  return (
    <svg
      width={size} height={size}
      viewBox="0 0 18 22"
      fill={filled ? color : 'none'}
      stroke={color}
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: 'block', transition: 'fill 0.2s, stroke 0.2s' }}
    >
      {/* Classic bookmark, rectangle with V-notch at bottom */}
      <path d="M3 2h12a1 1 0 0 1 1 1v16.5l-7-4-7 4V3a1 1 0 0 1 1-1z" />
    </svg>
  );
}

function ArticleSaveButton({ slug, size = 18, C, inline = false }) {
  const [saved, toggle] = useSavedArticle(slug);
  const [pressed, setPressed] = useState(false);

  const handleClick = (e) => {
    e.stopPropagation();
    setPressed(true);
    setTimeout(() => setPressed(false), 200);
    toggle();
  };

  return (
    <button
      onClick={handleClick}
      aria-label={saved ? 'Remove from saved' : 'Save article'}
      title={saved ? 'Saved' : 'Save to reading list'}
      style={{
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: inline ? '4px' : '8px',
        color: saved ? GOLD : (C?.muted || 'rgba(30,28,22,0.4)'),
        transform: pressed ? 'scale(0.84)' : 'scale(1)',
        transition: 'color 0.2s, transform 0.15s',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        lineHeight: 1,
      }}
    >
      <BookmarkIcon filled={saved} size={size} color={saved ? GOLD : (C?.muted || 'rgba(30,28,22,0.4)')} />
    </button>
  );
}

// ── StickyArticleMeta, fixed left-gutter panel, visible while scrolling body ─
// Appears once the hero scrolls out of view; disappears when the article footer
// comes into view. Sits in the natural left gutter of the 1080px content column.
// Only renders on screens wide enough to have a real gutter (>= 1380px).
// Uses position:fixed so it never affects document layout or full-bleed blocks.

function StickyArticleMeta({ post, cat, label, author, slug, C }) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Only show on wide screens with enough left gutter
    const mq = window.matchMedia('(min-width: 1380px)');
    if (!mq.matches) return;

    // Observe a sentinel at the hero/body boundary, when it leaves the top of
    // the viewport, reveal the panel. Using a sentinel (not the hero element)
    // is essential for the sticky cover-hero: a pinned hero never leaves the
    // viewport, so observing it directly would never trigger. The sentinel sits
    // in normal flow and scrolls away for every hero type.
    const hero = document.querySelector('#lwd-hero-sentinel')
      || document.querySelector('.lwd-hero, .lwd-vt-hero, .lwd-split-hero, .lwd-min-hero');
    if (!hero) return;

    const obs = new IntersectionObserver(
      ([entry]) => setShow(!entry.isIntersecting),
      { threshold: 0, rootMargin: '-80px 0px 0px 0px' }
    );
    obs.observe(hero);

    const onResize = () => {
      if (!window.matchMedia('(min-width: 1380px)').matches) setShow(false);
    };
    window.addEventListener('resize', onResize, { passive: true });

    return () => {
      obs.disconnect();
      window.removeEventListener('resize', onResize);
    };
  }, []);

  if (!show) return null;

  const catLine = [label, cat].filter(Boolean).join(' · ');
  const date    = fmtDate(post.published_at || post.created_at);

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'fixed',
        // Position in the left gutter: content is max 1080px centered,
        // so left edge of gutter = calc(50vw - 540px). We sit inside that.
        left:     'calc(50vw - 690px)',
        top:      96,
        width:    140,
        zIndex:   90,
        opacity:  show ? 1 : 0,
        transform: show ? 'translateX(0)' : 'translateX(-12px)',
        transition: 'opacity 0.35s ease, transform 0.35s ease',
        pointerEvents: show ? 'auto' : 'none',
        // Fade out near screen edge
        maskImage: 'linear-gradient(to right, transparent 0%, black 18%, black 100%)',
        WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 18%, black 100%)',
      }}
    >
      {/* Category */}
      {catLine && (
        <div style={{
          fontFamily: FU, fontSize: 8, fontWeight: 700,
          letterSpacing: '0.26em', textTransform: 'uppercase',
          color: GOLD, marginBottom: 10,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {catLine}
        </div>
      )}

      {/* Gold rule */}
      <div style={{ width: 24, height: 1, background: GOLD, opacity: 0.5, marginBottom: 10 }} />

      {/* Date */}
      {date && (
        <div style={{
          fontFamily: FU, fontSize: 9, letterSpacing: '0.12em',
          color: C.muted, marginBottom: 6, lineHeight: 1.5,
        }}>
          {date}
        </div>
      )}

      {/* Author */}
      {author && (
        <div style={{
          fontFamily: FU, fontSize: 9, letterSpacing: '0.10em',
          color: C.subtle, marginBottom: 14, lineHeight: 1.5,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {author}
        </div>
      )}

      {/* Save bookmark */}
      <div style={{ marginLeft: -8 }}>
        <ArticleSaveButton slug={slug} size={16} C={C} />
      </div>

      {/* Reading progress, compact vertical line */}
      <ReadingProgressLine C={C} />
    </div>
  );
}

// Thin vertical reading-progress line used inside the sticky meta panel
function ReadingProgressLine({ C }) {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    const tick = () => {
      const el  = document.documentElement;
      const top = el.scrollTop || document.body.scrollTop;
      const max = el.scrollHeight - el.clientHeight;
      setPct(max > 0 ? Math.min(100, (top / max) * 100) : 0);
    };
    window.addEventListener('scroll', tick, { passive: true });
    tick();
    return () => window.removeEventListener('scroll', tick);
  }, []);

  return (
    <div style={{ marginTop: 16, position: 'relative', height: 80, width: 1 }}>
      <div style={{
        position: 'absolute', inset: 0,
        background: C.border,
      }} />
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0,
        height: `${pct}%`,
        background: GOLD,
        transition: 'height 0.1s linear',
      }} />
    </div>
  );
}

// ── Reading progress bar ─────────────────────────────────────────────────────

function ReadingProgress() {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    const tick = () => {
      const el  = document.documentElement;
      const top = el.scrollTop || document.body.scrollTop;
      const max = el.scrollHeight - el.clientHeight;
      setPct(max > 0 ? Math.min(100, (top / max) * 100) : 0);
    };
    window.addEventListener('scroll', tick, { passive: true });
    return () => window.removeEventListener('scroll', tick);
  }, []);
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, zIndex: 200,
      height: 2, width: `${pct}%`,
      background: `linear-gradient(to right, ${GOLD}99, ${GOLD})`,
      transition: 'width 0.08s linear',
      pointerEvents: 'none',
    }} />
  );
}

// ── useParallax, RAF-based, disabled on touch devices ────────────────────────
// Container must be overflow:hidden with a fixed height.
// The image inside should be height: ~130% to allow movement room.

function useParallax(speed = 0.25) {
  const imgRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    // Disable on touch, parallax causes jank on iOS
    if ('ontouchstart' in window) return;

    const img = imgRef.current;
    if (!img) return;

    const update = () => {
      const rect   = img.parentElement?.getBoundingClientRect();
      if (!rect) return;
      const viewH  = window.innerHeight;
      const centre = rect.top + rect.height / 2;
      const offset = (centre - viewH / 2) * speed;
      img.style.transform = `translateY(${offset.toFixed(1)}px)`;
    };

    const onScroll = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        update();
        rafRef.current = null;
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    update();
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [speed]);

  return imgRef;
}

// ── Caption, shared caption component ────────────────────────────────────────

function Caption({ text, C }) {
  if (!text) return null;
  return (
    <p style={{
      fontFamily: FU,
      fontSize: 10,
      letterSpacing: '0.14em',
      color: C.subtle,
      margin: '10px auto 0',
      maxWidth: 860,
      padding: '0 clamp(24px, 5vw, 60px)',
      textAlign: 'center',
    }}>
      {text}
    </p>
  );
}

// ── Block: ParallaxImage ──────────────────────────────────────────────────────

function ParallaxImageBlock({ block, C }) {
  const src = block.url || block.src || block.image_url;
  const imgRef = useParallax(0.22);
  const [loaded, setLoaded] = useState(false);

  if (!src) return null;

  return (
    <figure style={{ margin: '4em 0', padding: 0 }}>
      <div style={{
        position: 'relative',
        width: '100vw',
        marginLeft: '50%',
        transform: 'translateX(-50%)',
        height: 'clamp(320px, 52vw, 680px)',
        overflow: 'hidden',
        background: C.card,
      }}>
        <img
          ref={imgRef}
          src={imgUrl(src, { width: 1600, resize: 'contain' })}
          alt={block.alt || block.caption || ''}
          loading="lazy"
          onLoad={() => setLoaded(true)}
          style={{
            position: 'absolute',
            top: '-15%',
            left: 0,
            width: '100%',
            height: '130%',
            objectFit: 'cover',
            objectPosition: block.focal_point || 'center center',
            opacity: loaded ? 1 : 0,
            transition: 'opacity 0.7s ease',
            willChange: 'transform',
          }}
        />
      </div>
      <Caption text={block.caption} C={C} />
    </figure>
  );
}

// Exact top/bottom margin for an image-family figure, given the adjacency
// flags stamped onto `block` (see the .lwd-block-list render loop below) —
// `base` is that block type's own normal spacing (e.g. '4em', '2.6em') used
// on whichever side isn't tight against another image block. Set on both
// sides independently; does not rely on CSS margin-collapse or `:has()`.
const tightMargin = (block, base) => ({
  marginTop: block.__tightTop ? 3 : base,
  marginBottom: block.__tightBottom ? 3 : base,
});

// ── Block: FullBleedImage ─────────────────────────────────────────────────────

function FullBleedImageBlock({ block, C }) {
  const src = block.url || block.src || block.image_url;
  const [loaded, setLoaded] = useState(false);
  // Seed orientation from the build-time classification (block.orientation) so a
  // portrait renders as a contained centred portrait IMMEDIATELY, no banner flash
  // while waiting for the browser to detect it. onLoad still corrects if absent/wrong.
  const [orient, setOrient] = useState(
    block.orientation === 'portrait' ? 'portrait'
    : (block.orientation === 'landscape' || block.orientation === 'panorama') ? 'landscape'
    : null);
  const layout = block.layout || 'crop';
  const isParallax = layout === 'parallax';
  const isNatural  = layout === 'natural';
  const imgRef = useParallax(isParallax ? 0.22 : 0);
  const openLightbox = useArticleLightbox();

  if (!src) return null;

  const handleLoad = (e) => {
    setLoaded(true);
    // `block.forceWide`: an editorial override for a portrait-dimensioned source photo whose
    // CONTENT reads wide (a long table, a room), not the source's actual pixel orientation.
    // Without this guard, onLoad recalculates from the real decoded image and silently
    // overwrites the override back to 'portrait' the moment it finishes loading.
    if (block.forceWide) return;
    const im = e?.currentTarget;
    if (im?.naturalWidth && im?.naturalHeight) {
      setOrient(im.naturalHeight > im.naturalWidth ? 'portrait' : 'landscape');
    }
  };

  // GUARD (image orientation rule): a full-bleed / hero / wide-banner slot is landscape-only.
  // A portrait image must never be sliced to fill it. If one lands here, render it as a
  // contained, centred editorial portrait at its natural ratio (full subject visible), never
  // cropped. Capped at 1080px, the same column width as DualImageBlock/GridGalleryBlock/
  // ContainedImageBlock, so a portrait reads at the same scale as the paired landscape grids
  // around it rather than shrinking to an orphaned 560px column.
  if (orient === 'portrait' && layout === 'crop') {
    return (
      <figure style={{ ...tightMargin(block, '4em'), padding: '0 clamp(20px,4vw,56px)', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <img
          src={imgUrl(src, { width: 1100, resize: 'contain' })}
          alt={block.alt || block.caption || ''}
          loading="lazy"
          onClick={() => openLightbox(block.__lightboxStart ?? 0)}
          style={{
            display: 'block', width: 'auto', height: 'auto',
            maxWidth: 'min(100%, 1080px)', maxHeight: 'clamp(440px, 80vh, 880px)',
            objectFit: 'contain', cursor: 'pointer',
          }}
        />
        <Caption text={block.caption} C={C} />
      </figure>
    );
  }

  return (
    <figure style={{ ...tightMargin(block, '4em'), padding: 0 }}>
      <div style={{
        position: 'relative',
        width: '100vw',
        marginLeft: '50%',
        transform: 'translateX(-50%)',
        ...(isNatural
          ? { lineHeight: 0 }
          : {
              height: isParallax
                ? `clamp(320px, ${block.heightVw || 52}vw, ${block.maxHeight || 680}px)`
                : `clamp(280px, ${block.heightVw || 52}vw, ${block.maxHeight || 989}px)`, // taller banner is the house standard
              overflow: 'hidden',
              background: C.card,
            }
        ),
      }}>
        <img
          ref={isParallax ? imgRef : undefined}
          src={imgUrl(src, { width: 1600, resize: 'contain' })}
          alt={block.alt || block.caption || ''}
          loading="lazy"
          onLoad={handleLoad}
          onClick={() => openLightbox(block.__lightboxStart ?? 0)}
          style={{
            display: 'block',
            width: '100%',
            opacity: loaded ? 1 : 0,
            transition: 'opacity 0.6s ease',
            cursor: 'pointer',
            ...(isNatural
              ? { height: 'auto' }
              : {
                  position: isParallax ? 'absolute' : 'relative',
                  top: isParallax ? '-15%' : undefined,
                  left: isParallax ? 0 : undefined,
                  height: isParallax ? '130%' : '100%',
                  objectFit: 'cover',
                  objectPosition: block.focal_point || 'center center',
                  willChange: isParallax ? 'transform' : undefined,
                }
            ),
          }}
        />
      </div>
      <Caption text={block.caption} C={C} />
    </figure>
  );
}

// ── Block: ContainedImage ─────────────────────────────────────────────────────
// A single image constrained to the same 1080px text-column width as body
// paragraphs, the magazine-style contained single image, NOT full-bleed.
// Reads its source the same way FullBleedImageBlock does (url/src/image_url).

function ContainedImageBlock({ block, C }) {
  const src = block.url || block.src || block.image_url;
  const openLightbox = useArticleLightbox();
  if (!src) return null;
  return (
    <figure style={{ ...tightMargin(block, '2.6em'), marginLeft: 'auto', marginRight: 'auto', maxWidth: 1080, padding: '0 clamp(20px,4vw,56px)' }}>
      <img
        // resize:'contain' is REQUIRED, without it the Supabase transform defaults to
        // resize=cover, which pins width to 1080 but keeps the SOURCE height, delivering a
        // distorted 1080×(fullHeight) image that stretches under width:100%/height:auto.
        src={imgUrl(src, { width: 1080, resize: 'contain' })}
        alt={block.alt || block.caption || ''}
        loading="lazy"
        onClick={() => openLightbox(block.__lightboxStart ?? 0)}
        style={{ width: '100%', height: 'auto', display: 'block', cursor: 'pointer' }}
      />
      <Caption text={block.caption} C={C} />
    </figure>
  );
}

// ── Block: DualImage ─────────────────────────────────────────────────────────
// Extracted from the inline renderBlock switch so we can use useState for
// per-image error handling. When a source image returns 404 or fails to load
// the failing panel is silently removed and the remaining image expands to
// fill the full-width grid column, no broken icon, no visible alt text.

function DualImageBlock({ block, C }) {
  const srcA  = block.imageA?.src  || (typeof block.imageA === 'string' ? block.imageA : '');
  const srcB  = block.imageB?.src  || (typeof block.imageB === 'string' ? block.imageB : '');
  const srcC  = block.imageC?.src  || (typeof block.imageC === 'string' ? block.imageC : '');
  const altA  = block.imageA?.alt  || block.altA || '';
  const altB  = block.imageB?.alt  || block.altB || '';
  const altC  = block.imageC?.alt  || block.altC || '';
  const pathA = typeof block.imageA === 'object' && block.imageA?.src ? 'imageA.src' : 'imageA';
  const pathB = typeof block.imageB === 'object' && block.imageB?.src ? 'imageB.src' : 'imageB';
  const pathC = typeof block.imageC === 'object' && block.imageC?.src ? 'imageC.src' : 'imageC';
  const [failedA, setFailedA] = useState(false);
  const [failedB, setFailedB] = useState(false);
  const [failedC, setFailedC] = useState(false);
  const openLightbox = useArticleLightbox();

  // HOUSE DUAL STANDARD (fixed visual grammar): a side-by-side is always images of the SAME
  // orientation in ONE shared frame, equal height, object-fit:cover. The frame is chosen from the
  // images' orientation (detected on load), NOT from whatever ratio the block requested:
  //   both portrait  → 2:3 cells
  //   both landscape → 3:2 cells
  //   mixed          → do not force a wrong frame; contain both in one neutral frame (equal height,
  //                    nothing sliced) and flag the block until the content is replaced.
  // `natural` fit is allowed ONLY for a lone image; multi-image groups are always cover + fixed frame.
  const [orA, setOrA] = useState(null);
  const [orB, setOrB] = useState(null);
  const [orC, setOrC] = useState(null);
  const noteOrient = (setter) => (e) => {
    const im = e?.currentTarget;
    if (im?.naturalWidth && im?.naturalHeight) setter(im.naturalHeight > im.naturalWidth ? 'P' : 'L');
  };

  const showA = Boolean(srcA) && !failedA;
  const showB = Boolean(srcB) && !failedB;
  const showC = Boolean(srcC) && !failedC;

  if (!showA && !showB && !showC) return null;

  // Local position within THIS block's own filtered [A,B,C] sequence, matching
  // getBlockImageUrls' filter order exactly, so block.__lightboxStart + posX
  // always lands on the right image in the article-wide list.
  let __dualPos = 0;
  const posA = showA ? __dualPos++ : -1;
  const posB = showB ? __dualPos++ : -1;
  const posC = showC ? __dualPos++ : -1;

  const shown = [showA, showB, showC].filter(Boolean).length;
  const cols  = `repeat(${shown || 1}, 1fr)`;
  const isMulti = shown > 1;

  const orients   = [showA && orA, showB && orB, showC && orC].filter(Boolean);
  const allLoaded = (!showA || orA) && (!showB || orB) && (!showC || orC);
  const allLand   = orients.length > 0 && orients.every(o => o === 'L');
  const allPort   = orients.length > 0 && orients.every(o => o === 'P');
  const isMixed   = isMulti && allLoaded && !allLand && !allPort;

  const useNatural = block.fit === 'natural' && !isMulti; // natural only for a lone image
  // Frame: 3:2 for landscape pairs, 2:3 for portrait pairs, neutral square while loading or for mixed.
  const frameRatio = !allLoaded ? (block.ratio || '2/3')
    : isMixed ? '1/1'
    : allLand ? '3/2'
    : '2/3';
  const fitMode = isMixed ? 'contain' : 'cover';

  const imgStyle = useNatural
    ? { width: '100%', height: 'auto', display: 'block' }
    : { width: '100%', height: '100%', objectFit: fitMode, display: 'block' };
  // Per-image focal control: each image may set `focus` (e.g. 'center 20%').
  // Falls back to the block default, then a top-weighted bridal default.
  const posOf = (img) => (img && typeof img === 'object' && (img.focus || img.focal)) || block.objectPosition || 'center 22%';
  const wrapperStyle = useNatural
    ? { width: '100%', overflow: 'hidden' }
    : { width: '100%', maxWidth: block.frameWidth || undefined, margin: block.frameWidth ? '0 auto' : undefined, aspectRatio: frameRatio, overflow: 'hidden', background: isMixed ? (C?.card || '#efe9df') : undefined };

  return (
    // Root is a <figure>, matching every other image block (GridGalleryBlock,
    // contained_image, full_bleed_image) — this was the one block type still
    // using a plain <div> with its own bespoke margin, which meant a themed
    // photo group with mixed portrait/landscape pairs (dual_image next to
    // grid_gallery) never collapsed to the shared 3px "one continuous grid"
    // rule (.lwd-block-list figure + figure): only figure-to-figure pairs got
    // it, so a real gallery alternated between a tight 3px gap and a loose
    // 44-76px gap depending on which pairs happened to be portrait vs
    // landscape. Real production bug, found 2026-09-12 on a 12-photo mixed-
    // orientation gallery. Fixed by unifying the tag + base margin instead of
    // adding a second CSS selector for div-based blocks.
    <figure style={{ ...tightMargin(block, '4em') }}>
    <div data-lwd-dual={isMixed ? 'mixed-orientation' : (allLand ? 'landscape' : 'portrait')} style={{
      margin: '0 auto', maxWidth: block.maxWidth || 1080,
      padding: '0 clamp(20px,4vw,56px)',
      display: 'grid', gridTemplateColumns: cols,
      // Was clamp(8px,1.5vw,20px) — matched nothing else on the page. Every
      // other tight gap in the block system (GridGalleryBlock's own cells,
      // and the figure+figure collapse between stacked gallery blocks) is
      // 3px; this was the one gap left inconsistent, found 2026-09-12.
      gap: 3,
      alignItems: 'start',
    }}>
      {showA && (
        <AdminImageOverlay blockId={block._blockId} fieldPath={pathA} currentSrc={srcA}
          wrapperStyle={wrapperStyle}
          onUpdated={newSrc => { if (typeof block.imageA === 'object') block.imageA.src = newSrc; else block.imageA = newSrc; }}>
          <img src={imgUrl(srcA, useNatural ? { width: 1100, resize: 'contain' } : { width: 1000, resize: 'contain' })} alt={altA} loading="lazy" style={{ ...imgStyle, objectPosition: posOf(block.imageA), cursor: 'pointer' }}
            onLoad={noteOrient(setOrA)} onError={() => setFailedA(true)} onClick={() => openLightbox((block.__lightboxStart ?? 0) + posA)} />
        </AdminImageOverlay>
      )}
      {showB && (
        <AdminImageOverlay blockId={block._blockId} fieldPath={pathB} currentSrc={srcB}
          wrapperStyle={wrapperStyle}
          onUpdated={newSrc => { if (typeof block.imageB === 'object') block.imageB.src = newSrc; else block.imageB = newSrc; }}>
          <img src={imgUrl(srcB, useNatural ? { width: 1100, resize: 'contain' } : { width: 1000, resize: 'contain' })} alt={altB} loading="lazy" style={{ ...imgStyle, objectPosition: posOf(block.imageB), cursor: 'pointer' }}
            onLoad={noteOrient(setOrB)} onError={() => setFailedB(true)} onClick={() => openLightbox((block.__lightboxStart ?? 0) + posB)} />
        </AdminImageOverlay>
      )}
      {showC && (
        <AdminImageOverlay blockId={block._blockId} fieldPath={pathC} currentSrc={srcC}
          wrapperStyle={wrapperStyle}
          onUpdated={newSrc => { if (typeof block.imageC === 'object') block.imageC.src = newSrc; else block.imageC = newSrc; }}>
          <img src={imgUrl(srcC, useNatural ? { width: 1100, resize: 'contain' } : { width: 1000, resize: 'contain' })} alt={altC} loading="lazy" style={{ ...imgStyle, objectPosition: posOf(block.imageC), cursor: 'pointer' }}
            onLoad={noteOrient(setOrC)} onError={() => setFailedC(true)} onClick={() => openLightbox((block.__lightboxStart ?? 0) + posC)} />
        </AdminImageOverlay>
      )}
    </div>
    {(showA || showB || showC) && [block.imageA, block.imageB, block.imageC].some(im => im && typeof im === 'object' && im.caption) && (
      <div style={{
        maxWidth: block.maxWidth || 1080, margin: '10px auto 0',
        padding: '0 clamp(20px,4vw,56px)',
        display: 'grid', gridTemplateColumns: cols, gap: 3,
      }}>
        {[[block.imageA, showA], [block.imageB, showB], [block.imageC, showC]].map(([im, show], i) => {
          if (!show) return null;
          const cap = im && typeof im === 'object' ? im.caption : '';
          return cap ? (
            <p key={i} style={{
              fontFamily: FU, fontSize: 10, letterSpacing: '0.14em',
              color: (C && (C.subtle || C.muted)) || 'rgba(245,240,232,0.55)',
              margin: 0, textAlign: 'center',
            }}>{cap}</p>
          ) : <div key={i} />;
        })}
      </div>
    )}
    </figure>
  );
}

// ── Block: Diptych ────────────────────────────────────────────────────────────

function DiptychBlock({ block, C }) {
  const left  = block.left  || block.images?.[0] || {};
  const right = block.right || block.images?.[1] || {};
  const srcL  = left.url  || left.src  || '';
  const srcR  = right.url || right.src || '';
  const dipId = useRef(`dip-${Math.random().toString(36).slice(2)}`).current;

  if (!srcL && !srcR) return null;

  // Compute field paths for AdminImageOverlay, handle both left/right and images[] shapes
  const useImages = !block.left && !block.right && block.images;
  function dipPath(side, meta, idx) {
    if (useImages) return meta.src !== undefined ? `images.${idx}.src` : `images.${idx}.url`;
    return side === 'left'
      ? (meta.src !== undefined ? 'left.src' : 'left.url')
      : (meta.src !== undefined ? 'right.src' : 'right.url');
  }

  const items = [
    { src: srcL, meta: left,  side: 'left',  idx: 0 },
    { src: srcR, meta: right, side: 'right', idx: 1 },
  ].filter(({ src }) => src);

  const openLightbox = useArticleLightbox();

  return (
    <figure style={{ margin: '4em 0', padding: 0 }}>
      {/* Scoped responsive CSS, stack to single column on mobile */}
      <style>{`
        #${dipId} { display: grid; grid-template-columns: ${srcL && srcR ? '1fr 1fr' : '1fr'}; gap: 3px; }
        @media (max-width: 600px) { #${dipId} { grid-template-columns: 1fr; } }
        #${dipId} > div { height: clamp(240px, 38vw, 540px); }
        @media (max-width: 600px) { #${dipId} > div { height: 72vw; } }
      `}</style>

      <div
        id={dipId}
        style={{ width: '100vw', marginLeft: '50%', transform: 'translateX(-50%)' }}
      >
        {items.map(({ src, meta, side, idx }, pos) => (
          <div key={idx} style={{ overflow: 'hidden', background: C.card, position: 'relative' }}>
            <AdminImageOverlay
              blockId={block._blockId}
              fieldPath={dipPath(side, meta, idx)}
              currentSrc={src}
              wrapperStyle={{ width: '100%', height: '100%' }}
              onUpdated={newSrc => {
                if (useImages) { if (block.images[idx]) block.images[idx].src = newSrc; }
                else if (side === 'left') { block.left = { ...(block.left || {}), src: newSrc }; }
                else { block.right = { ...(block.right || {}), src: newSrc }; }
              }}
            >
              <img
                src={imgUrl(src, { width: 900, resize: 'contain' })}
                alt={meta.alt || meta.caption || ''}
                loading="lazy"
                onClick={() => openLightbox((block.__lightboxStart ?? 0) + pos)}
                style={{
                  width: '100%', height: '100%',
                  objectFit: 'cover',
                  objectPosition: meta.focal_point || 'center center',
                  display: 'block',
                  cursor: 'pointer',
                }}
              />
            </AdminImageOverlay>
          </div>
        ))}
      </div>

      {/* Captions */}
      {block.caption
        ? <Caption text={block.caption} C={C} />
        : (left.caption || right.caption) && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: srcL && srcR ? '1fr 1fr' : '1fr',
              gap: 3, padding: '10px clamp(24px, 5vw, 60px) 0',
            }}>
              {[left.caption, right.caption].map((cap, i) => cap ? (
                <p key={i} style={{
                  fontFamily: FU, fontSize: 10, letterSpacing: '0.14em',
                  color: C.subtle, margin: 0, textAlign: 'center',
                }}>{cap}</p>
              ) : <div key={i} />)}
            </div>
          )
      }
    </figure>
  );
}

// ── Block: FilmStrip ──────────────────────────────────────────────────────────
// Native horizontal scroll with snap. No JS required for scrolling.

function FilmStripBlock({ block, C }) {
  const images  = block.images || [];
  const stripId = useRef(`fs-${Math.random().toString(36).slice(2)}`).current;
  const openLightbox = useArticleLightbox();

  if (!images.length) return null;

  // Local position within the FILTERED (truthy-src) sequence, matching
  // getBlockImageUrls exactly — images.map(i => ...) isn't filtered, so a
  // missing src earlier in the array would otherwise shift indices.
  let __fsPos = 0;
  const filmStripPositions = images.map(img => {
    const src = img.url || img.src || (typeof img === 'string' ? img : '');
    return src ? __fsPos++ : -1;
  });

  return (
    <figure style={{ margin: '4em 0', padding: 0, overflow: 'hidden' }}>
      {/* Inject scrollbar-hiding CSS once */}
      <style>{`
        #${stripId} { scrollbar-width: none; -ms-overflow-style: none; }
        #${stripId}::-webkit-scrollbar { display: none; }
      `}</style>

      <div
        id={stripId}
        style={{
          display: 'flex',
          gap: 3,
          overflowX: 'scroll',
          scrollSnapType: 'x mandatory',
          WebkitOverflowScrolling: 'touch',
          cursor: 'grab',
        }}
      >
        {images.map((img, i) => {
          const src = img.url || img.src || (typeof img === 'string' ? img : '');
          if (!src) return null;
          const fsPath = (typeof img === 'object' && img.src !== undefined)
            ? `images.${i}.src` : `images.${i}.url`;
          return (
            <div key={i} onClick={() => openLightbox((block.__lightboxStart ?? 0) + filmStripPositions[i])} style={{
              flexShrink: 0,
              scrollSnapAlign: 'start',
              height: 'clamp(260px, 55vh, 680px)',
              width: 'clamp(300px, 70vw, 860px)',
              overflow: 'hidden',
              background: C.card,
              position: 'relative',
              cursor: 'pointer',
            }}>
              <AdminImageOverlay
                blockId={block._blockId}
                fieldPath={fsPath}
                currentSrc={src}
                wrapperStyle={{ width: '100%', height: '100%' }}
                onUpdated={newSrc => { if (typeof block.images[i] === 'object') block.images[i].src = newSrc; else block.images[i] = newSrc; }}
              >
                <img
                  src={imgUrl(src, { width: 1200, resize: 'contain' })}
                  alt={img.alt || img.caption || `Image ${i + 1}`}
                  loading="lazy"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: img.focal_point || 'center center',
                    display: 'block',
                    pointerEvents: 'none',
                  }}
                />
              </AdminImageOverlay>
              {img.caption && (
                <div style={{
                  position: 'absolute', bottom: 0, left: 0, right: 0,
                  padding: '20px 16px 12px',
                  background: `linear-gradient(to top, ${C.bg}cc, transparent)`,
                  fontFamily: FU,
                  fontSize: 9,
                  letterSpacing: '0.14em',
                  color: C.muted,
                }}>
                  {img.caption}
                </div>
              )}
            </div>
          );
        })}

        {/* Trailing spacer so last image can snap flush */}
        <div style={{ flexShrink: 0, width: 1 }} />
      </div>

      {/* Swipe hint, fades after first interaction */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 8,
        marginTop: 12,
      }}>
        <div style={{ height: 1, width: 20, background: C.border2 }} />
        <span style={{
          fontFamily: FU, fontSize: 8, letterSpacing: '0.24em',
          textTransform: 'uppercase', color: C.subtle,
        }}>
          Scroll to explore
        </span>
        <div style={{ height: 1, width: 20, background: C.border2 }} />
      </div>

      <Caption text={block.caption} C={C} />
    </figure>
  );
}

// ── Block: CenteredGallery ────────────────────────────────────────────────────
// A centred, column-constrained image gallery, never full-bleed.
// Sits within the max-width 1080px text column, centred on the page.
//
// block.layout:
//   '2-col'    , 2 equal landscape images side by side      (4:3 ratio)
//   '3-col'    , 3 equal square/portrait images             (1:1 ratio)
//   'feature-2', 1 large (65%) left + 2 stacked small right (landscape feature)
//   '2-tall'   , 2 portrait images side by side             (3:4 ratio)
//   'masonry'  , CSS columns, images display at natural height (3 cols)
//
// block.images: [{ src, alt, caption }]
// block.title: optional heading above the grid
// block.caption: optional caption below the grid
// Click any image → lightbox

function CenteredGalleryBlock({ block, C }) {
  const layout = block.layout || '3-col';
  const images = (block.images || []).filter(img => {
    const s = img.src || img.url || (typeof img === 'string' ? img : '');
    return Boolean(s);
  });
  if (!images.length) return null;

  const [hovIdx, setHovIdx] = useState(-1);
  const openLightbox = useArticleLightbox();

  function openAt(i) { openLightbox((block.__lightboxStart ?? 0) + i); }

  const gap   = 'clamp(6px,0.8vw,12px)';
  const radius = 2;

  // Shared cell render
  function Cell({ img, i, style: cellStyle = {} }) {
    const src = img.src || img.url || (typeof img === 'string' ? img : '');
    const alt = img.alt || img.caption || '';
    const fp  = typeof img === 'object' && img.src !== undefined
      ? `images.${i}.src` : `images.${i}.url`;
    return (
      <div
        style={{
          position: 'relative', overflow: 'hidden',
          background: C.card, cursor: 'pointer',
          borderRadius: radius,
          ...cellStyle,
        }}
        onClick={() => openAt(i)}
        onMouseEnter={() => setHovIdx(i)}
        onMouseLeave={() => setHovIdx(-1)}
      >
        <AdminImageOverlay
          blockId={block._blockId}
          fieldPath={fp}
          currentSrc={src}
          wrapperStyle={{ width: '100%', height: '100%' }}
          onUpdated={newSrc => {
            if (typeof block.images[i] === 'object') block.images[i].src = newSrc;
            else block.images[i] = newSrc;
          }}
        >
          <img
            src={imgUrl(src, { width: 900, resize: 'contain' })}
            alt={alt}
            loading="lazy"
            style={{
              width: '100%', height: '100%',
              objectFit: 'cover', display: 'block',
              transform: hovIdx === i ? 'scale(1.04)' : 'scale(1)',
              transition: 'transform 0.55s ease',
            }}
          />
        </AdminImageOverlay>
        {img.caption && (
          <p style={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            margin: 0, padding: '6px 10px',
            background: 'linear-gradient(transparent, rgba(0,0,0,0.52))',
            fontFamily: FU, fontSize: 9, color: '#f5f0e8',
            letterSpacing: '0.10em', lineHeight: 1.4,
            opacity: hovIdx === i ? 1 : 0,
            transition: 'opacity 0.25s ease',
          }}>
            {img.caption}
          </p>
        )}
      </div>
    );
  }

  // ── Layout renderers ──
  let grid = null;

  if (layout === '2-col') {
    const pair = images.slice(0, 2);
    grid = (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap }}>
        {pair.map((img, i) => (
          <Cell key={i} img={img} i={i} style={{ aspectRatio: '4/3' }} />
        ))}
      </div>
    );
  }

  else if (layout === '3-col') {
    const trio = images.slice(0, 3);
    grid = (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap }}>
        {trio.map((img, i) => (
          <Cell key={i} img={img} i={i} style={{ aspectRatio: '1/1' }} />
        ))}
      </div>
    );
  }

  else if (layout === 'feature-2') {
    const [main, ...rest] = images.slice(0, 3);
    grid = (
      <div style={{ display: 'grid', gridTemplateColumns: '65fr 35fr', gap, alignItems: 'stretch' }}>
        {/* Large feature left */}
        <Cell img={main} i={0} style={{ aspectRatio: '4/3' }} />
        {/* 2 stacked small right */}
        <div style={{ display: 'grid', gridTemplateRows: '1fr 1fr', gap }}>
          {rest.slice(0, 2).map((img, j) => (
            <Cell key={j + 1} img={img} i={j + 1} style={{ width: '100%', height: '100%', minHeight: 0 }} />
          ))}
        </div>
      </div>
    );
  }

  else if (layout === '2-tall') {
    const pair = images.slice(0, 2);
    grid = (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap }}>
        {pair.map((img, i) => (
          <Cell key={i} img={img} i={i} style={{ aspectRatio: '3/4' }} />
        ))}
      </div>
    );
  }

  else if (layout === 'masonry') {
    // CSS columns, images display at their natural aspect ratio
    const gallId = `cgm-${Math.random().toString(36).slice(2,7)}`;
    grid = (
      <>
        <style>{`
          #${gallId} { columns: 3; column-gap: ${gap}; }
          @media (max-width:600px) { #${gallId} { columns: 2; } }
          #${gallId} > div { break-inside: avoid; margin-bottom: ${gap}; }
        `}</style>
        <div id={gallId}>
          {images.map((img, i) => {
            const src = img.src || img.url || '';
            if (!src) return null;
            const fp = typeof img === 'object' && img.src !== undefined
              ? `images.${i}.src` : `images.${i}.url`;
            return (
              <div key={i}>
                <div
                  style={{ position: 'relative', overflow: 'hidden', cursor: 'pointer', borderRadius: radius, background: C.card }}
                  onClick={() => openAt(i)}
                  onMouseEnter={() => setHovIdx(i)}
                  onMouseLeave={() => setHovIdx(-1)}
                >
                  <AdminImageOverlay
                    blockId={block._blockId} fieldPath={fp} currentSrc={src}
                    wrapperStyle={{ width: '100%' }}
                    onUpdated={newSrc => {
                      if (typeof block.images[i] === 'object') block.images[i].src = newSrc;
                      else block.images[i] = newSrc;
                    }}
                  >
                    <img
                      src={imgUrl(src, { width: 700, resize: 'contain' })}
                      alt={img.alt || ''}
                      loading="lazy"
                      style={{
                        width: '100%', height: 'auto', display: 'block',
                        transform: hovIdx === i ? 'scale(1.03)' : 'scale(1)',
                        transition: 'transform 0.5s ease',
                      }}
                    />
                  </AdminImageOverlay>
                </div>
              </div>
            );
          })}
        </div>
      </>
    );
  }

  if (!grid) return null;

  return (
    <figure style={{
      margin: '3.5em auto',
      padding: '0 clamp(20px,4vw,56px)',
      maxWidth: 1080,
    }}>
      {/* Optional section title */}
      {block.title && (
        <div style={{
          fontFamily: FU, fontSize: 8, fontWeight: 700,
          letterSpacing: '0.30em', textTransform: 'uppercase',
          color: GOLD, textAlign: 'center',
          marginBottom: 20,
        }}>
          {block.title}
        </div>
      )}

      {grid}

      {/* Caption */}
      {block.caption && (
        <figcaption style={{
          fontFamily: FU, fontSize: 10, letterSpacing: '0.14em',
          color: C.subtle, marginTop: 12, textAlign: 'center',
        }}>
          {block.caption}
        </figcaption>
      )}

    </figure>
  );
}

// ── Block: GridGallery ────────────────────────────────────────────────────────
// layout: '3x2' (6 images, 3 cols × 2 rows) | '3x3' (9 images, 3 cols × 3 rows)
// style:  'tight' | 'square' | 'tall' | 'padded'
// Click any image → the shared article-wide lightbox (useArticleLightbox).

const GRID_STYLES = {
  tight:  { gap: 3,  radius: 0,  cellH: 'clamp(160px, 22vw, 320px)', aspect: null },
  square: { gap: 3,  radius: 0,  cellH: null,                         aspect: '1/1' },
  tall:   { gap: 3,  radius: 0,  cellH: 'clamp(240px, 34vw, 500px)', aspect: null },
  landscape: { gap: 3, radius: 0, cellH: null,                        aspect: '3/2' },  // wide 3:2 cells, landscape shots fill exactly
  portrait:  { gap: 3, radius: 0, cellH: null,                        aspect: '2/3' },  // tall 2:3 cells, portrait shots fill exactly
  padded: { gap: 14, radius: 8,  cellH: 'clamp(160px, 20vw, 300px)', aspect: null },
};

function GridGalleryBlock({ block, C }) {
  const layout  = block.layout || '3x2';
  const style   = GRID_STYLES[block.style] || GRID_STYLES.tight;
  const maxImgs = layout === '3x3' ? 9 : 6;
  const images  = (block.images || []).slice(0, maxImgs);
  if (!images.length) return null;

  const [hovIdx, setHovIdx] = useState(-1);
  const openLightbox = useArticleLightbox();

  const gridId = useRef(`gg-${Math.random().toString(36).slice(2)}`).current;

  // Local position within the FILTERED (truthy-src) sequence — matches
  // getBlockImageUrls exactly, so a missing src doesn't shift indices.
  let __ggPos = 0;
  const ggPositions = images.map(img => {
    const src = img.url || img.src || (typeof img === 'string' ? img : '');
    return src ? __ggPos++ : -1;
  });

  function openAt(i) { openLightbox((block.__lightboxStart ?? 0) + ggPositions[i]); }

  // Cell height CSS, 'square' uses aspect-ratio, others use fixed clamp
  const cellHeightStyle = style.aspect
    ? { aspectRatio: style.aspect }
    : { height: style.cellH };

  return (
    <figure style={{ ...tightMargin(block, '4em'), padding: 0 }}>
      <style>{`
        #${gridId} {
          display: grid;
          grid-template-columns: repeat(${(parseInt(String(block.layout||'3x2'), 10) || 3)}, 1fr);
          gap: ${style.gap}px;
        }
        @media (max-width: 600px) {
          #${gridId} { grid-template-columns: repeat(${Math.min(2, (parseInt(String(block.layout||'3x2'), 10) || 3))}, 1fr); }
        }
      `}</style>

      <div
        id={gridId}
        style={block.contained
          ? { maxWidth: block.maxWidth || 1080, margin: '0 auto', padding: '0 clamp(20px,4vw,56px)' }
          : { width: '100vw', marginLeft: '50%', transform: 'translateX(-50%)' }}
      >
        {images.map((img, i) => {
          const src = img.url || img.src || (typeof img === 'string' ? img : '');
          if (!src) return null;
          const isHov = hovIdx === i;
          const ggPath = typeof img === 'string'
            ? `images.${i}`
            : (img.src !== undefined ? `images.${i}.src` : `images.${i}.url`);
          return (
            <div
              key={i}
              role="button"
              tabIndex={0}
              aria-label={`Open image ${i + 1} in gallery`}
              onClick={() => openAt(i)}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') openAt(i); }}
              onMouseEnter={() => setHovIdx(i)}
              onMouseLeave={() => setHovIdx(-1)}
              style={{
                position:     'relative',
                overflow:     'hidden',
                background:   C.card,
                cursor:       'pointer',
                borderRadius: style.radius,
                ...cellHeightStyle,
              }}
            >
              <AdminImageOverlay
                blockId={block._blockId}
                fieldPath={ggPath}
                currentSrc={src}
                wrapperStyle={{ width: '100%', height: '100%' }}
                onUpdated={newSrc => {
                  if (typeof block.images[i] === 'string') block.images[i] = newSrc;
                  else if (block.images[i]) block.images[i].src = newSrc;
                }}
              >
                <img
                  src={imgUrl(src, { width: 900, resize: 'contain' })}
                  alt={img.alt || img.caption || `Photo ${i + 1}`}
                  loading="lazy"
                  style={{
                    width:          '100%',
                    height:         '100%',
                    objectFit:      'cover',
                    objectPosition: img.focal_point || 'center center',
                    display:        'block',
                    transition:     'transform 0.6s ease',
                    transform:      isHov ? 'scale(1.04)' : 'scale(1)',
                  }}
                />
              </AdminImageOverlay>
              {/* Hover overlay, expand icon */}
              <div style={{
                position:       'absolute',
                inset:          0,
                background:     isHov ? 'rgba(0,0,0,0.28)' : 'rgba(0,0,0,0)',
                transition:     'background 0.35s ease',
                display:        'flex',
                alignItems:     'center',
                justifyContent: 'center',
              }}>
                <div style={{
                  width:          40,
                  height:         40,
                  borderRadius:   '50%',
                  border:         '1px solid rgba(255,255,255,0.75)',
                  background:     'rgba(0,0,0,0.35)',
                  display:        'flex',
                  alignItems:     'center',
                  justifyContent: 'center',
                  opacity:        isHov ? 1 : 0,
                  transform:      isHov ? 'scale(1)' : 'scale(0.7)',
                  transition:     'opacity 0.3s ease, transform 0.3s ease',
                  backdropFilter: 'blur(4px)',
                  color:          '#fff',
                  fontSize:       16,
                }}>
                  ⤢
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <Caption text={block.caption} C={C} />
    </figure>
  );
}

// ── Block: AmbientVideo ───────────────────────────────────────────────────────
// Supports YouTube URL, Vimeo URL, or direct video file (MP4/WebM).
// YouTube/Vimeo → muted background iframe (no UI chrome).
// Direct file   → <video> with IntersectionObserver play/pause + sound toggle.

function detectVideoSource(url) {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/);
  if (yt) return { type: 'youtube', id: yt[1] };
  const vim = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vim) return { type: 'vimeo', id: vim[1] };
  return { type: 'mp4', id: null };
}

function AmbientVideoBlock({ block, C }) {
  const src    = block.url || block.src || block.video_url;
  const source = detectVideoSource(src);
  const poster = imgUrl(block.poster || block.thumbnail, { width: 1200 }) || '';

  const videoRef  = useRef(null);
  const [muted,   setMuted]  = useState(true);

  // MP4-only: IntersectionObserver, play when visible, pause when off
  useEffect(() => {
    if (!videoRef.current || !src || source?.type !== 'mp4') return;
    const video = videoRef.current;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) video.play().catch(() => {});
        else                       video.pause();
      },
      { threshold: 0.25 }
    );
    obs.observe(video);
    return () => obs.disconnect();
  }, [src, source?.type]);

  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = muted;
  }, [muted]);

  if (!src || !source) return null;

  // ── YouTube embed URL (background / ambient mode) ─────────────────────────
  const ytEmbed   = source.type === 'youtube'
    ? `https://www.youtube.com/embed/${source.id}?autoplay=1&mute=1&loop=1&playlist=${source.id}&controls=0&modestbranding=1&playsinline=1&rel=0&showinfo=0`
    : null;

  // ── Vimeo background mode, no controls, autoplay, muted ─────────────────
  const vimeoEmbed = source.type === 'vimeo'
    ? `https://player.vimeo.com/video/${source.id}?autoplay=1&muted=1&loop=1&background=1&transparent=0`
    : null;

  const isEmbed  = source.type === 'youtube' || source.type === 'vimeo';
  const embedUrl = ytEmbed || vimeoEmbed;

  return (
    <figure style={{ margin: '4em 0', padding: 0 }}>
      <div style={{
        position:  'relative',
        width:     '100vw',
        marginLeft:'50%',
        transform: 'translateX(-50%)',
        height:    'clamp(280px, 50vw, 660px)',
        overflow:  'hidden',
        background:'#000',
      }}>

        {/* ── Iframe embed (YouTube / Vimeo) ── */}
        {isEmbed && (
          <iframe
            key={embedUrl}
            src={embedUrl}
            title={block.caption || 'Ambient video'}
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            style={{
              position:  'absolute',
              top:       '50%',
              left:      '50%',
              /* 16/9 → scale up so it fills the container at any aspect ratio */
              width:     'max(100%, calc(100vh * 16 / 9))',
              height:    'max(100%, calc(100vw * 9 / 16))',
              transform: 'translate(-50%, -50%)',
              border:    'none',
              pointerEvents: 'none', // keep the cinematic feel, no clicking into iframe
            }}
          />
        )}

        {/* ── Direct video file (MP4 / WebM upload) ── */}
        {!isEmbed && (
          <video
            ref={videoRef}
            src={src}
            poster={poster || undefined}
            autoPlay
            muted
            loop
            playsInline
            preload="none"
            style={{
              width:          '100%',
              height:         '100%',
              objectFit:      'cover',
              objectPosition: block.focal_point || 'center center',
              display:        'block',
            }}
          />
        )}

        {/* Ambient overlay, very subtle dark wash at bottom */}
        <div style={{
          position:       'absolute',
          inset:          0,
          background:     `linear-gradient(to top, ${C.bg}55 0%, transparent 45%)`,
          pointerEvents:  'none',
        }} />

        {/* Sound toggle, MP4 only (iframes are always muted) */}
        {!isEmbed && (
          <button
            onClick={() => setMuted(m => !m)}
            title={muted ? 'Unmute' : 'Mute'}
            style={{
              position:        'absolute',
              top:             16,
              right:           16,
              background:      `${C.bg}bb`,
              border:          `1px solid ${C.border2}`,
              color:           C.muted,
              fontFamily:      FU,
              fontSize:        9,
              letterSpacing:   '0.18em',
              textTransform:   'uppercase',
              padding:         '5px 10px',
              cursor:          'pointer',
              backdropFilter:  'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              display:         'flex',
              alignItems:      'center',
              gap:             5,
              transition:      'color 0.2s, border-color 0.2s',
            }}
          >
            <span style={{ fontSize: 12 }}>{muted ? '🔇' : '🔊'}</span>
            {muted ? 'Sound' : 'Mute'}
          </button>
        )}

        {/* Optional text overlay */}
        {block.overlay_text && (
          <div style={{
            position:    'absolute',
            bottom:      'clamp(24px, 4vw, 48px)',
            left:        'clamp(24px, 5vw, 60px)',
            fontFamily:  FD,
            fontWeight:  400,
            fontSize:    'clamp(22px, 3vw, 40px)',
            lineHeight:  1.15,
            color:       '#fff',
            maxWidth:    600,
            textShadow:  '0 2px 20px rgba(0,0,0,0.6)',
          }}>
            {block.overlay_text}
          </div>
        )}
      </div>

      <Caption text={block.caption} C={C} />
    </figure>
  );
}

// ── Block: EditorNote ─────────────────────────────────────────────────────────
// "From the Editor" centred italic opening statement with author attribution.
// Best placed as the first block, an authorial preface before the prose.

function EditorNoteBlock({ block, C }) {
  const text = block.text || block.content || '';
  const attr = block.attribution || '';
  const role = block.role || '';
  if (!text) return null;

  return (
    <section style={{
      maxWidth: 820,
      margin: '0 auto 4em',
      padding: '3.5em clamp(24px, 5vw, 60px)',
      borderTop:    `1px solid ${C.border}`,
      borderBottom: `1px solid ${C.border}`,
      textAlign: 'center',
    }}>
      <div style={{
        fontFamily: FU, fontSize: 8, letterSpacing: '0.36em',
        textTransform: 'uppercase', color: GOLD, marginBottom: 28,
      }}>
        From the Editor
      </div>
      <blockquote style={{ margin: 0, padding: 0 }}>
        <p style={{
          fontFamily: FD, fontStyle: 'italic', fontWeight: 400,
          fontSize: 'clamp(20px, 2.4vw, 32px)',
          lineHeight: 1.5, color: C.text, margin: '0 0 28px',
        }}>
          "{text}"
        </p>
        {(attr || role) && (
          <footer style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14 }}>
            <div style={{ height: 1, width: 36, background: C.border2 }} />
            <div>
              {attr && (
                <div style={{
                  fontFamily: FU, fontSize: 9, letterSpacing: '0.22em',
                  textTransform: 'uppercase', color: C.muted,
                }}>
                  {attr}
                </div>
              )}
              {role && (
                <div style={{
                  fontFamily: FU, fontSize: 8, letterSpacing: '0.16em',
                  color: C.subtle, marginTop: 3,
                }}>
                  {role}
                </div>
              )}
            </div>
            <div style={{ height: 1, width: 36, background: C.border2 }} />
          </footer>
        )}
      </blockquote>
    </section>
  );
}

// ── Block: Venue Contact ──────────────────────────────────────────────────────
// Venue Edit posts: a "Contact the Venue" section that reuses the showcase's
// VenueEnquireCard (the enquiry form, lead linked to the listing), plus three
// links, the listing, the showcase, and a FOLLOWED external link to the venue's
// own website. block.content: { venue_name, listing_id, listing_url,
// showcase_url, website, background }
function VenueContactBlock({ block, C }) {
  const venueName = block.venue_name || '';
  // listing_url and showcase_url are STORED on the block, so they are a snapshot of
  // where the venue lived when the article was written. That is how this block came
  // to link Atholl Palace, Cromlix and Beverly Wilshire (all draft), the Villa Nascosta
  // showcase (draft), and stale shapes like /united-states/... and /italy/lazio/planners/...
  // Every one was a live internal 404.
  //
  // The gate is the arbiter, not the stored value. An internal link survives only if
  // the destination is publicly routable right now; otherwise the CTA is dropped rather
  // than shown pointing at a 404 or, worse, at a cold prospect's private page. The
  // external website link is unaffected: it is not ours to route.
  const links = [
    block.listing_url  && isPubliclyRoutable(block.listing_url)  && { label: 'View the Listing',  href: block.listing_url,  external: false },
    block.showcase_url && isPubliclyRoutable(block.showcase_url) && { label: 'View the Showcase', href: block.showcase_url, external: false },
    block.website      && { label: 'Visit the Website', href: block.website,      external: true  },
  ].filter(Boolean);
  if (!block.listing_id && links.length === 0) return null;

  return (
    <section style={{ margin: '2.5em 0 4em' }}>
      <VenueEnquireCard data={{
        background:  block.background || '',
        headline:    venueName ? `Enquire with ${venueName}` : 'Contact the Venue',
        subline:     'Send an enquiry and our wedding team will connect you with the venue.',
        venueName,
        listingId:   block.listing_id || null,
        listingType: 'venue',
        leadSource:  'editorial_article',
      }} />

      {links.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 14, marginTop: 30, padding: '0 24px' }}>
          {links.map(l => (
            <a
              key={l.label}
              href={l.href}
              {...(l.external ? { target: '_blank', rel: 'noopener' } : {})}
              style={{
                fontFamily: FU, fontSize: 10, fontWeight: 700, letterSpacing: '0.18em',
                textTransform: 'uppercase', color: '#F7F2E8', textDecoration: 'none',
                padding: '14px 28px', background: '#14110d', border: '1px solid #14110d', borderRadius: 2,
                transition: 'background 0.2s, color 0.2s, border-color 0.2s', whiteSpace: 'nowrap',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = GOLD; e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.color = '#1b160e'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#14110d'; e.currentTarget.style.borderColor = '#14110d'; e.currentTarget.style.color = '#F7F2E8'; }}
            >
              {l.label}{l.external ? ' ↗' : ' →'}
            </a>
          ))}
        </div>
      )}
    </section>
  );
}

// ── Block: Credits ────────────────────────────────────────────────────────────
// Venue, photographer, gown, florist, the editorial credits table.
// block.items = [{ label: 'Venue', value: 'Villa del Balbianello' }, …]

function CreditsBlock({ block, C }) {
  const items = (block.items || []).filter(it => it.label && it.value);
  if (!items.length) return null;

  return (
    <div style={{
      maxWidth: 1080,
      margin: '3em auto 0',
      padding: '0 clamp(20px, 4vw, 56px)',
    }}>
      <div style={{
        borderTop: `1px solid ${GOLD}33`,
        paddingTop: 28,
      }}>
        <div style={{
          fontFamily: FU, fontSize: 8, letterSpacing: '0.34em',
          textTransform: 'uppercase', color: GOLD, marginBottom: 20,
        }}>
          Credits
        </div>
        <dl style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: '16px 32px', margin: 0,
        }}>
          {items.map((item, i) => (
            <div key={i}>
              <dt style={{
                fontFamily: FU, fontSize: 8, letterSpacing: '0.18em',
                textTransform: 'uppercase', color: C.subtle, marginBottom: 4,
              }}>
                {item.label}
              </dt>
              <dd style={{
                fontFamily: FS, fontSize: 'clamp(13px, 1.2vw, 15px)',
                color: C.body, margin: 0, lineHeight: 1.5,
              }}>
                {item.value}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}

// ── Block: VendorCredits ───────────────────────────────────────────────────────
// Atelier / vendor details + contact info. block.heading, block.vendors:
// [{ role, name, url? }]. Linked name when a url is present (opens new tab).

function VendorCreditsBlock({ block, C }) {
  const vendors = (block.vendors || []).filter(v => v && v.name);
  if (!vendors.length) return null;
  const open = (url) => {
    if (!url) return;
    const abs = url.startsWith('http') ? url : `https://${url}`;
    window.open(abs, '_blank', 'noopener,noreferrer');
  };
  return (
    <div style={{ maxWidth: 1080, margin: '3.5em auto 0', padding: '0 clamp(20px,4vw,56px)' }}>
      <div style={{ borderTop: `1px solid ${GOLD}33`, paddingTop: 32 }}>
        <div style={{ fontFamily: FU, fontSize: 8, letterSpacing: '0.34em', textTransform: 'uppercase', color: GOLD, marginBottom: 24, textAlign: 'center' }}>
          {block.heading || 'The Atelier'}
        </div>
        <dl style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: '20px 32px', margin: '0 auto', maxWidth: 760, justifyItems: 'center', textAlign: 'center' }}>
          {vendors.map((v, i) => (
            <div key={i}>
              <dt style={{ fontFamily: FU, fontSize: 8, letterSpacing: '0.18em', textTransform: 'uppercase', color: GOLD, marginBottom: 5 }}>{v.role}</dt>
              <dd style={{ margin: 0 }}>
                {v.url ? (
                  <button onClick={() => open(v.url)} style={{ fontFamily: FS, fontSize: 'clamp(14px,1.3vw,16px)', color: C.body, background: 'none', border: 'none', borderBottom: `1px solid ${GOLD}66`, cursor: 'pointer', padding: '0 0 1px' }}>{v.name}</button>
                ) : (
                  <span style={{ fontFamily: FS, fontSize: 'clamp(14px,1.3vw,16px)', color: C.body }}>{v.name}</span>
                )}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}

// ── Block: ShopCollectionHeader ────────────────────────────────────────────────
// Branded section header with gold rule, separates editorial from commerce.
// block.label (eyebrow), block.headline (title), block.subline (subtitle)

function ShopCollectionHeaderBlock({ block, C }) {
  return (
    <div style={{ margin: '80px auto 0', maxWidth: 1080, padding: '0 clamp(20px, 4vw, 56px)' }}>
      <div style={{ height: 1, background: `linear-gradient(to right, transparent, ${GOLD}55, transparent)`, marginBottom: 36 }} />
      {block.label && (
        <div style={{
          fontFamily: FU, fontSize: 8, fontWeight: 700,
          letterSpacing: '0.36em', textTransform: 'uppercase',
          color: GOLD, marginBottom: 14,
        }}>
          {block.label}
        </div>
      )}
      {block.headline && (
        <h2 style={{
          fontFamily: FD, fontWeight: 400,
          fontSize: 'clamp(30px, 4vw, 56px)',
          color: C.text, margin: '0 0 10px', lineHeight: 1.1,
          letterSpacing: '-0.01em',
        }}>
          {block.headline}
        </h2>
      )}
      {block.subline && (
        <p style={{
          fontFamily: FU, fontSize: 'clamp(11px, 1.1vw, 14px)',
          fontWeight: 300, letterSpacing: '0.1em',
          color: C.muted, margin: '0 0 48px',
        }}>
          {block.subline}
        </p>
      )}
    </div>
  );
}

// ── Block: EditorialFeature ────────────────────────────────────────────────────
// Full-width 50/50 image + text split. Editorial pause, like Whistles / Vogue.
// block.image, block.imagePosition ('left' | 'right'), block.label,
// block.headline, block.body, block.cta, block.ctaUrl

function EditorialFeatureBlock({ block, C, isMobile }) {
  const imgLeft = !block.imagePosition || block.imagePosition === 'left';
  return (
    <div style={{
      display: isMobile ? 'flex' : 'grid',
      flexDirection: 'column',
      gridTemplateColumns: '1fr 1fr',
      margin: '80px 0',
      minHeight: isMobile ? 'auto' : 560,
    }}>
      <div style={{
        order: imgLeft ? 0 : 1,
        overflow: 'hidden', position: 'relative',
      }}>
        {block.image && (
          <img
            src={imgUrl(block.image, { width: 1200, resize: 'contain' })}
            alt={block.headline || ''}
            loading="lazy"
            style={{
              width: '100%', height: '100%', objectFit: 'cover',
              display: 'block', minHeight: isMobile ? 300 : 560,
            }}
          />
        )}
      </div>
      <div style={{
        order: imgLeft ? 1 : 0,
        background: C.surface || (isLightBg(C.bg) ? '#f2ede7' : '#111008'),
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
        padding: 'clamp(36px, 6vw, 80px)',
      }}>
        {block.label && (
          <div style={{
            fontFamily: FU, fontSize: 8, fontWeight: 700,
            letterSpacing: '0.36em', textTransform: 'uppercase',
            color: GOLD, marginBottom: 16,
          }}>
            {block.label}
          </div>
        )}
        {(block.headline || block.heading) && (
          <h2 style={{
            fontFamily: FD, fontWeight: 400,
            fontSize: 'clamp(24px, 2.8vw, 44px)',
            color: C.text, margin: '0 0 20px', lineHeight: 1.2,
          }}>
            {block.headline || block.heading}
          </h2>
        )}
        {block.body && (
          <div
            className="lwd-wysiwyg"
            dangerouslySetInnerHTML={safeHtml(block.body)}
            style={{
              fontFamily: FU, fontSize: 'clamp(13px, 1.2vw, 15px)',
              fontWeight: 300, lineHeight: 1.8,
              color: C.muted, margin: '0 0 28px',
            }}
          />
        )}
        {block.cta && block.ctaUrl && (
          <a
            href={block.ctaUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontFamily: FU, fontSize: 9, fontWeight: 700,
              letterSpacing: '0.2em', textTransform: 'uppercase',
              color: GOLD, textDecoration: 'none',
              borderBottom: `1px solid ${GOLD}`,
              paddingBottom: 3, display: 'inline-block', alignSelf: 'flex-start',
            }}
          >
            {block.cta}
          </a>
        )}
      </div>
    </div>
  );
}

// ── Block: ProductStory ────────────────────────────────────────────────────────
// Narrative product card, large portrait image + editorial panels.
// Why We Love It / Destination Fit / Styling Notes + Shop Now CTA.

function ProductStoryBlock({ block, C, isMobile }) {
  return (
    <a
      href={block.url || '#'}
      target="_blank"
      rel="nofollow noopener noreferrer"
      style={{
        display: isMobile ? 'block' : 'grid',
        gridTemplateColumns: '360px 1fr',
        gap: 0, margin: '80px auto',
        maxWidth: 1080, padding: '0 clamp(20px, 4vw, 56px)',
        textDecoration: 'none', cursor: 'pointer',
      }}
    >
      {block.image && (
        <div style={{
          aspectRatio: '3/4', overflow: 'hidden',
          marginBottom: isMobile ? 28 : 0, flexShrink: 0,
        }}>
          <img
            src={imgUrl(block.image, { width: 720, resize: 'contain' })}
            alt={block.name || ''}
            loading="lazy"
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        </div>
      )}
      <div style={{
        paddingLeft: isMobile ? 0 : 56,
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
      }}>
        {block.brand && (
          <div style={{
            fontFamily: FU, fontSize: 8, fontWeight: 700,
            letterSpacing: '0.36em', textTransform: 'uppercase',
            color: GOLD, marginBottom: 10,
          }}>
            {block.brand}
          </div>
        )}
        {block.name && (
          <h3 style={{
            fontFamily: FD, fontWeight: 400,
            fontSize: 'clamp(22px, 2.4vw, 34px)',
            color: C.text, margin: '0 0 8px', lineHeight: 1.2,
          }}>
            {block.name}
          </h3>
        )}
        {block.price && (
          <div style={{
            fontFamily: FU, fontSize: 15, fontWeight: 600,
            color: C.muted, marginBottom: 32, letterSpacing: '0.04em',
          }}>
            {block.price}
          </div>
        )}
        {[
          { label: 'Why We Love It', text: block.whyWeLoveIt },
          { label: 'Destination Fit', text: block.destinationFit },
          { label: 'Styling Notes',   text: block.stylingNotes },
        ].filter(p => p.text).map((panel, pi) => (
          <div key={pi} style={{ marginBottom: 24 }}>
            <div style={{
              fontFamily: FU, fontSize: 8, fontWeight: 700,
              letterSpacing: '0.22em', textTransform: 'uppercase',
              color: GOLD, marginBottom: 6,
            }}>
              {panel.label}
            </div>
            <p style={{
              fontFamily: FU, fontSize: 'clamp(13px, 1.2vw, 15px)',
              fontWeight: 300, lineHeight: 1.8,
              color: C.muted, margin: 0,
            }}>
              {panel.text}
            </p>
          </div>
        ))}
      </div>
    </a>
  );
}

// ── Block: CollectionStrip ─────────────────────────────────────────────────────
// Horizontal swipeable product carousel with prev/next arrows.
// block.products = [{ image, brand, name, price, url, editorialNote, badge }]

function CollectionStripBlock({ block, C, isMobile }) {
  const stripRef = useRef(null);
  const scroll = (dir) => {
    if (!stripRef.current) return;
    stripRef.current.scrollBy({ left: dir * 360, behavior: 'smooth' });
  };

  const products = block.products || [];
  if (!products.length) return null;
  const isLight = isLightBg(C.bg);
  const showArrows = !isMobile && products.length > 3;

  return (
    <div style={{ margin: '0 0 80px', position: 'relative' }}>
      {showArrows && (
        <button
          onClick={() => scroll(-1)}
          aria-label="Scroll left"
          style={{
            position: 'absolute', left: 8, top: '38%',
            transform: 'translateY(-50%)', zIndex: 2,
            background: C.bg, border: `1px solid ${GOLD}50`,
            color: GOLD, width: 44, height: 44, borderRadius: '50%',
            cursor: 'pointer', fontSize: 22, lineHeight: 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
          }}
        >
          ‹
        </button>
      )}
      {showArrows && (
        <button
          onClick={() => scroll(1)}
          aria-label="Scroll right"
          style={{
            position: 'absolute', right: 8, top: '38%',
            transform: 'translateY(-50%)', zIndex: 2,
            background: C.bg, border: `1px solid ${GOLD}50`,
            color: GOLD, width: 44, height: 44, borderRadius: '50%',
            cursor: 'pointer', fontSize: 22, lineHeight: 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
          }}
        >
          ›
        </button>
      )}
      <div
        ref={stripRef}
        style={{
          display: 'flex',
          gap: 'clamp(16px, 2vw, 28px)',
          overflowX: 'auto',
          scrollSnapType: 'x mandatory',
          padding: '0 clamp(20px, 4vw, 56px) 24px',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        {products.map((p, pi) => (
          <div key={pi} style={{ flex: '0 0 clamp(240px, 26vw, 320px)', scrollSnapAlign: 'start' }}>
            <ProductCard
              product={{
                image:         p.image,
                brand:         p.brand,
                title:         p.name,
                price:         p.price,
                salePrice:     p.salePrice,
                description:   p.desc,
                editorialNote: p.editorialNote,
                affiliateUrl:  p.url,
                badge:         p.badge,
                retailer:      p.retailer,
                cta:           p.cta || 'Shop Now',
              }}
              variant="portrait"
              isLight={isLight}
              noBorder={true}
              noButton={true}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Light-theme detection, robust luminance check on the active editorial bg.
// The LWD Edit theme flips between DARK (bg #070604) and LIGHT (bg #f7f4ef); an older
// hardcoded `C.bg === '#faf8f4'` test never matched either, so every "isLight"
// branch (section themes, product grids, style tips) was stuck dark, which left
// editorial_section backgrounds forced dark in lights-on mode (dark text on dark).
function isLightBg(bg) {
  if (!bg || typeof bg !== 'string' || bg[0] !== '#' || bg.length < 7) return false;
  const r = parseInt(bg.slice(1, 3), 16), g = parseInt(bg.slice(3, 5), 16), b = parseInt(bg.slice(5, 7), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) >= 140;
}

// ── Editorial typography, reads from admin siteSettings stored in localStorage
// Called inside renderArticleBlock (module-level fn) so must be module-level too.
function getEditorialTypo() {
  try {
    const saved = JSON.parse(localStorage.getItem('lwd_theme_overrides') || '{}');
    const et = saved?.site?.editorialTypography || {};
    const _ff = (name) => `'${name}', ${
      /nunito|inter|outfit|montserrat|lato|poppins|raleway|open sans|roboto|dm sans|manrope|urbanist|jost|sora|figtree|bricolage/i.test(name)
        ? 'sans-serif' : 'Georgia, serif'
    }`;
    return {
      h1Font: _ff(et.h1Font || 'Gilda Display'), h1Weight: et.h1Weight || 400,
      h2Font: _ff(et.h2Font || 'Gilda Display'), h2Weight: et.h2Weight || 400,
      h3Font: _ff(et.h3Font || 'Gilda Display'), h3Weight: et.h3Weight || 400,
      bodyFont: _ff(et.bodyFont || 'Nunito'),    lineH: et.bodyLineH || 1.5,
      // Only an explicit admin override; otherwise null so the renderer falls
      // back to the theme-aware C.body (light in dark mode, dark in light mode).
      bodyColour: et.bodyColour || null,
    };
  } catch {
    return {
      h1Font: FD, h1Weight: 400,
      h2Font: FD,                                h2Weight: 400,
      h3Font: FD, h3Weight: 400,
      bodyFont: FS, lineH: 1.5, bodyColour: null,
    };
  }
}

// ── Article-wide lightbox ────────────────────────────────────────────────────
// ONE shared instance of the SAME immersive photo lightbox used on showcase
// and listing pages (src/components/profile/Lightbox.jsx — full-screen, photo
// credit panel, thumbnail strip, slideshow, "View All" grid, Pinterest/social
// share), not a bespoke Editorial-only modal (2026-09-12, Tai: "this is not
// the correct gallery that we use on the showcase" / "we use an immersive
// gallery"). Clicking ANY image in ANY block opens this SAME instance, seeded
// with every image on the page, positioned at the image that was clicked —
// not just that block's own images ("it must load all images ... on that
// page/post"). Each image block reads `openLightbox` from this context
// instead of owning its own local modal instance.
const ArticleLightboxContext = createContext(null);
function useArticleLightbox() {
  return useContext(ArticleLightboxContext) || (() => {});
}

// Single source of truth for "what images does this block contribute, in
// what order" — used both to build the article-wide flat image list AND (by
// each block component) to compute the correct global index for a click.
// Mirrors the exact src-extraction/filtering each block component already
// uses for its own rendering, so a block's local image position always lines
// up with its slice of the global list. Returns {src, alt} pairs — the
// showcase Lightbox shows the alt text as the photo's "Description".
function getBlockImageUrls(block) {
  const type = block.type;
  const mk = (src, alt) => (src ? [{ src, alt: alt || '' }] : []);
  if (type === 'contained_image' || type === 'full_bleed_image' || type === 'full_width_image'
      || type === 'image' || type === 'parallax_image') {
    const single = block.url || block.src || block.image_url;
    return mk(single, block.alt || block.caption);
  }
  if (type === 'dual_image') {
    const get = (im) => im?.src || (typeof im === 'string' ? im : '');
    const alt = (im, fallback) => (im && typeof im === 'object' && im.alt) || fallback || '';
    return [
      { src: get(block.imageA), alt: alt(block.imageA, block.altA) },
      { src: get(block.imageB), alt: alt(block.imageB, block.altB) },
      { src: get(block.imageC), alt: alt(block.imageC, block.altC) },
    ].filter(x => x.src);
  }
  if (type === 'diptych' || type === 'two_image_spread') {
    const left  = block.left  || block.images?.[0] || {};
    const right = block.right || block.images?.[1] || {};
    const getSrc = (im) => im?.url || im?.src || '';
    return [
      { src: getSrc(left),  alt: left.alt  || left.caption  || '' },
      { src: getSrc(right), alt: right.alt || right.caption || '' },
    ].filter(x => x.src);
  }
  if (type === 'film_strip' || type === 'three_image_strip' || type === 'slider') {
    return (block.images || [])
      .map(img => ({
        src: img.url || img.src || (typeof img === 'string' ? img : ''),
        alt: (img && typeof img === 'object' && (img.alt || img.caption)) || '',
      }))
      .filter(x => x.src);
  }
  if (type === 'grid_gallery' || type === 'gallery') {
    const maxImgs = block.layout === '3x3' ? 9 : 6;
    return (block.images || []).slice(0, maxImgs)
      .map(img => ({
        src: img.url || img.src || (typeof img === 'string' ? img : ''),
        alt: (img && typeof img === 'object' && (img.alt || img.caption)) || '',
      }))
      .filter(x => x.src);
  }
  if (type === 'centered_gallery') {
    return (block.images || [])
      .map(img => ({
        src: img.src || img.url || (typeof img === 'string' ? img : ''),
        alt: (img && typeof img === 'object' && (img.alt || img.caption)) || '',
      }))
      .filter(x => x.src);
  }
  return [];
}

// ── ArticleBody, dispatches blocks to the right renderer ────────────────────

function ArticleBody({ body, C, post }) {
  // Mobile detection, drives EditorialSection layout collapse on narrow
  // viewports. 900px is the threshold below which the split layout would
  // feel cramped.
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 900 : false,
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setIsMobile(window.innerWidth < 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  if (!body || !body.length) return null;

  // Chapter ornaments are OFF by default. They suit narrative pieces (real
  // weddings, long-form features) but feel heavy on news / opinion / lookbook
  // articles. Driven by:
  //   1) explicit ai_metadata.chapter_ornaments boolean (writer override), OR
  //   2) template_type === 'cinematic' (default-on for cinematic narratives), OR
  //   3) category_slug === 'real-weddings' (default-on for the real weddings
  //      template, which is structured as numbered chapters).
  const ornamentsEnabled = post?.ai_metadata?.chapter_ornaments === true
    || post?.template_type === 'cinematic'
    || post?.category_slug === 'real-weddings';

  let sectionCount = 0;
  const chapterMap = new Map();
  if (ornamentsEnabled) {
    body.forEach((b, i) => {
      if (b.type === 'editorial_section') {
        sectionCount += 1;
        chapterMap.set(b.id ?? i, sectionCount);
      }
    });
  }

  // Pre-bound recursive renderer so EditorialSectionBlock can reuse the
  // same block library inside its right column. Keeps a single source of
  // truth for how each block type renders, no duplication.
  const renderSingleBlock = (block, key, idx) => renderArticleBlock(
    block, key, C, renderSingleBlock, isMobile, chapterMap.get(block.id ?? idx) || 0,
  );

  // A strip/group of 4+ images (composeGroup, see KB-57) renders as several
  // consecutive dual/grid-gallery blocks, each its own <figure>. Two images
  // WITHIN one figure sit 3px apart; two figures stacked back-to-back should
  // read as one continuous grid, not as two separately-spaced blocks.
  //
  // FIRST fix (2026-09-12) did this with a CSS rule relying on `:has()`
  // (`figure:has(+ figure) { margin-bottom: 3px }`) to shrink the PRECEDING
  // figure's margin, paired with `figure + figure { margin-top: 3px }` for
  // the FOLLOWING one — both sides needed because adjacent block margins
  // collapse to the LARGER value, not the sum. `:has()` isn't supported in
  // every browser in the field; where it silently fails, only the top-margin
  // half applies and the collapsed gap reverts to the large default (found
  // 2026-09-12, reported as "still not fixed" on a real device after the
  // dev-server fix measured correctly in an evergreen headless browser).
  //
  // REAL fix: decide adjacency here in JS, not in CSS, and stamp the exact
  // margin each figure needs directly onto the block object before it
  // renders (read by GridGalleryBlock/DualImageBlock/ContainedImageBlock/
  // FullBleedImageBlock as `block.__tightTop`/`__tightBottom`). No reliance
  // on any CSS relational selector or on margin-collapse behaviour at all —
  // works identically in every browser.
  const IMG_BLOCK_TYPES = new Set([
    'dual_image', 'grid_gallery', 'gallery', 'contained_image',
    'full_bleed_image', 'full_width_image', 'image',
  ]);
  body.forEach((block, i) => {
    if (!IMG_BLOCK_TYPES.has(block.type)) return;
    block.__tightTop = IMG_BLOCK_TYPES.has(body[i - 1]?.type);
    block.__tightBottom = IMG_BLOCK_TYPES.has(body[i + 1]?.type);
  });

  // Article-wide lightbox: flatten every image block's images (in document
  // order) into one list, stamping each block with the global index of its
  // OWN first image (__lightboxStart). A block's click handler then just
  // adds its own local image position to that offset — see getBlockImageUrls.
  // The real photography credit (from the article's own `credits` block, if
  // any) is attached to every photo, same as the showcase Lightbox shows a
  // listing's real photographer — not invented, just the article's own byline.
  const photoCredit = useMemo(() => {
    const creditsBlock = body.find(b => b.type === 'credits');
    const item = (creditsBlock?.items || []).find(it => /photo/i.test(it.label || ''));
    return item?.value || null;
  }, [body]);
  const gallery = useMemo(() => {
    const items = [];
    body.forEach((block) => {
      block.__lightboxStart = items.length;
      getBlockImageUrls(block).forEach(({ src, alt }) => {
        items.push({
          id: String(items.length),
          src, alt,
          ...(photoCredit ? { credit_name: photoCredit } : {}),
        });
      });
    });
    return items;
  }, [body, photoCredit]);
  const [lightIdx, setLightIdx] = useState(null);
  const openLightbox = useCallback((i) => setLightIdx(i), []);

  return (
    <ArticleLightboxContext.Provider value={openLightbox}>
      <div className="lwd-block-list">
        {body.map((block, i) => renderSingleBlock(block, block.id ?? i, i))}
      </div>
      {lightIdx !== null && (
        <ProfileLightbox
          gallery={gallery}
          idx={lightIdx}
          setLightIdx={setLightIdx}
          onClose={() => setLightIdx(null)}
          onPrev={() => setLightIdx((i) => (i - 1 + gallery.length) % gallery.length)}
          onNext={() => setLightIdx((i) => (i + 1) % gallery.length)}
        />
      )}
    </ArticleLightboxContext.Provider>
  );
}

// Renders ONE block. Self-contained so it can be passed as a callback to
// EditorialSectionBlock's renderRightBlock. The fourth arg (renderInner)
// is the same function for nested-block rendering inside editorial_section
// right columns.
function renderArticleBlock(block, key, C, renderInner, isMobile = false, chapterNumber = 0) {
        const { h1Font, h2Font, h2Weight, h3Font, h3Weight, bodyFont, lineH, bodyColour } = getEditorialTypo();
        const articleFD       = h1Font;
        const articleFS       = bodyFont;
        const articleLineH    = lineH;
        const articleBodyCol  = bodyColour || C.body;
        const articleH2Font   = h2Font;
        const articleH2Weight = h2Weight;
        const articleH3Font   = h3Font;
        const articleH3Weight = h3Weight;
        const type = block.type;

        // ── Editorial split-section ─────────────────────────────────────────
        // Cinematic storytelling system. Sticky split-column with right-side
        // blocks recursively rendered through this same dispatcher.
        // Use this for: real weddings, long features, multi-chapter narratives.
        if (type === 'editorial_section')
          return (
            <EditorialSectionBlock
              key={key}
              block={block}
              renderRightBlock={(b, i) => renderInner(b, b.id ?? `${key}-r${i}`, i)}
              isMobile={isMobile}
              theme={isLightBg(C.bg) ? 'light' : 'dark'}
              chapterNumber={chapterNumber}
            />
          );

        // ── VT (Vogue / Tatler) refined section ─────────────────────────────
        // Refined editorial system. Title-below-image, sans-serif body, no
        // overlays, generous whitespace. Use this for: news, fashion, opinion,
        // single-shot features, lookbooks. Title chamber breathes; section
        // padding is intentionally exaggerated (80-128px) for the magazine
        // feel.
        if (type === 'vt_section')
          return (
            <VTSectionBlock
              key={key}
              block={block}
              renderRightBlock={(b, i) => renderInner(b, b.id ?? `${key}-r${i}`, i)}
              isMobile={isMobile}
              theme={isLightBg(C.bg) ? 'light' : 'dark'}
            />
          );

        // ── Immersive full-width blocks, rendered outside the text column ──
        if (type === 'editor_note')
          return <EditorNoteBlock key={key} block={block} C={C} />;
        if (type === 'credits')
          return <CreditsBlock key={key} block={block} C={C} />;
        if (type === 'vendor_credits')
          return <VendorCreditsBlock key={key} block={block} C={C} />;
        if (type === 'venue_contact')
          return <VenueContactBlock key={key} block={block} C={C} />;
        if (type === 'parallax_image') {
          const parSrc  = block.url || block.src || block.image_url || '';
          const parPath = block.src ? 'src' : block.url ? 'url' : 'image_url';
          return (
            <AdminImageOverlay key={key} blockId={block._blockId} fieldPath={parPath} currentSrc={parSrc}
              onUpdated={newSrc => { block.src = newSrc; delete block.url; delete block.image_url; }}>
              <ParallaxImageBlock block={block} C={C} />
            </AdminImageOverlay>
          );
        }
        // contained_image, single image at text-column width (NOT full-bleed)
        if (type === 'contained_image')
          return (
            <AdminImageOverlay key={key} blockId={block._blockId} fieldPath="src" currentSrc={block.src}
              onUpdated={newSrc => { block.src = newSrc; }}>
              <ContainedImageBlock block={block} C={C} />
            </AdminImageOverlay>
          );
        if (type === 'full_bleed_image' || type === 'full_width_image' || type === 'image')
          return (
            <AdminImageOverlay key={key} blockId={block._blockId} fieldPath="src" currentSrc={block.src}
              onUpdated={newSrc => { block.src = newSrc; }}>
              <FullBleedImageBlock block={block} C={C} />
            </AdminImageOverlay>
          );
        // two_image_spread uses images[0]/images[1], DiptychBlock handles this natively
        if (type === 'diptych' || type === 'two_image_spread')
          return <DiptychBlock key={key} block={block} C={C} />;
        // three_image_strip and slider both use images[] array, FilmStripBlock is the right fit
        if (type === 'film_strip' || type === 'three_image_strip' || type === 'slider')
          return <FilmStripBlock key={key} block={block} C={C} />;
        if (type === 'grid_gallery' || type === 'gallery')
          return <GridGalleryBlock key={key} block={block} C={C} />;
        if (type === 'centered_gallery')
          return <CenteredGalleryBlock key={key} block={block} C={C} />;
        // embed (Vimeo/YouTube URL) and ambient_video share the same component + detection
        if (type === 'ambient_video' || type === 'embed')
          return <AmbientVideoBlock key={key} block={block} C={C} />;

        // ── product_grid, editorial commerce block ──────────────────────────
        if (type === 'product_grid') {
          const products = block.products || [];
          if (!products.length) return null;
          const cols = block.columns || Math.min(products.length, 3);
          const isLight = isLightBg(C.bg);
          return (
            <div key={key} style={{ margin: '56px 0' }}>
              {(block.headline || block.subline) && (
                <div style={{ marginBottom: 28 }}>
                  <div style={{ fontFamily: "'Nunito','Inter',sans-serif", fontSize: 8, color: '#c9a96e', letterSpacing: '0.3em', textTransform: 'uppercase', marginBottom: 8, opacity: 0.8 }}>
                    {block.label || 'The LWD Edit'}
                  </div>
                  {block.headline && (
                    <h3 style={{ fontFamily: "'Gilda Display','Playfair Display',Georgia,serif", fontWeight: 400, fontSize: 'clamp(20px, 2vw, 28px)', color: C.text, margin: '0 0 6px', lineHeight: 1.2 }}>
                      {block.headline}
                    </h3>
                  )}
                  {block.subline && (
                    <p style={{ fontFamily: "'Nunito','Inter',sans-serif", fontSize: 12, color: C.muted || 'rgba(245,240,232,0.55)', margin: 0, lineHeight: 1.6 }}>
                      {block.subline}
                    </p>
                  )}
                </div>
              )}
              <div style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${cols}, 1fr)`,
                gap: 'clamp(16px, 2vw, 28px)',
              }}>
                {products.map((p, ii) => (
                  <ProductCard
                    key={ii}
                    product={{
                      image:         p.image,
                      brand:         p.brand,
                      title:         p.name,
                      price:         p.price,
                      salePrice:     p.salePrice,
                      description:   p.desc,
                      editorialNote: p.editorialNote,
                      affiliateUrl:  p.url,
                      badge:         p.badge,
                      retailer:      p.retailer,
                      cta:           p.cta || 'Shop Now',
                    }}
                    variant="portrait"
                    isLight={isLight}
                  />
                ))}
              </div>
            </div>
          );
        }

        // ── shop_products, Editorial Commerce (real product data) ──────────
        // Fetches block.product_ids from the live `products` table in one query,
        // preserves editor order, renders affiliate cards (sponsored nofollow).
        if (type === 'shop_products')
          return <ShopProductsBlock key={key} block={block} C={C} />;

        // ── intro ────────────────────────────────────────────────────────────
        if (type === 'intro')
          return (
            <p key={key}
              // safeHtml, not a raw injection. This was the ONLY editorial body
              // renderer bypassing it, and it leaked in both directions: stored HTML
              // reached the page unsanitised, and it skipped the routability gate, so
              // every held-destination link written into an article's opening
              // paragraph survived. It was the single source of the 33 links that
              // blocked the release: /italy/venice, /england/lake-district,
              // /italy/tuscany/val-d-orcia, /england/london/belgravia and the rest,
              // all sitting in intro prose rather than body prose.
              dangerouslySetInnerHTML={safeHtml(block.text || '')}
              style={{
                fontFamily: "'Gilda Display','Playfair Display',Georgia,serif",
                fontSize: 'clamp(20px,2.5vw,26px)', fontWeight: 400, fontStyle: 'italic',
                color: C.text, lineHeight: 1.65, margin: '0 auto 32px',
                maxWidth: 1080, padding: '0 clamp(20px,4vw,56px)',
                borderLeft: '3px solid #c9a96e', paddingLeft: 24,
              }}
            />
          );

        // ── quote_highlight ── Premium pull quote: a stop-the-scroll moment ────
        // Larger italic display type, generous vertical space, twin short gold
        // rules framing it (no boxy borders). One component, every article gains.
        if (type === 'quote_highlight' && block.text)
          return (
            <div key={key} style={{ margin: 'clamp(64px,9vw,120px) auto', maxWidth: 1080, padding: '0 clamp(20px,4vw,56px)' }}>
              <div style={{ textAlign: 'center', padding: 'clamp(12px,2vw,24px) 0' }}>
                <div style={{ width: 48, height: 1, background: '#c9a96e', opacity: 0.75, margin: '0 auto clamp(24px,3vw,34px)' }} />
                <p style={{ fontFamily: "'Gilda Display','Playfair Display',Georgia,serif", fontSize: 'clamp(26px,3.8vw,46px)', fontWeight: 400, fontStyle: 'italic', color: C.text, lineHeight: 1.42, margin: '0 auto', maxWidth: 760, letterSpacing: '0.005em' }}>
                  {block.text}
                </p>
                {block.attribution && (
                  <cite style={{ fontFamily: "'Nunito','Inter',sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#c9a96e', fontStyle: 'normal', display: 'block', marginTop: 20 }}>
                    {block.attribution}
                  </cite>
                )}
                <div style={{ width: 48, height: 1, background: '#c9a96e', opacity: 0.75, margin: 'clamp(24px,3vw,34px) auto 0' }} />
              </div>
            </div>
          );

        // ── lwd_insight ── Editorial device, NOT a marketing box. A concise
        // observation drawn from LWD research/experience, adding context not
        // promotion. Gold label only (no rule, no distinct type treatment) —
        // body copy reads exactly like the rest of the article; the label is
        // what marks it as LWD's own voice, not a different visual register.
        if (type === 'lwd_insight' && block.text)
          return (
            <aside key={key} style={{ margin: 'clamp(52px,7vw,92px) auto', maxWidth: 1080, padding: '0 clamp(20px,4vw,56px)' }}>
              <div style={{ maxWidth: 760, margin: '0 auto' }}>
                <div style={{ fontFamily: "'Nunito','Inter',sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: '#c9a96e', marginBottom: 14 }}>
                  {block.label || 'LWD Insight'}
                </div>
                <p style={{ fontFamily: articleFS, fontSize: 'clamp(16px, 1.5vw, 18.5px)', lineHeight: articleLineH, color: articleBodyCol, margin: 0 }}>
                  {block.text}
                </p>
              </div>
            </aside>
          );

        // ── at_a_glance ── Structured canonical facts, not another prose summary.
        // Only fields with a real value render — never pad with blanks. Values must
        // already be reconciled with canon (capacity/facets/verified) by the caller;
        // this block only displays, it never infers or guesses a figure.
        if (type === 'at_a_glance') {
          const agItems = (block.items || []).filter(it => it && it.value != null && String(it.value).trim() !== '');
          if (!agItems.length) return null;
          return (
            <aside key={key} style={{ margin: 'clamp(52px,7vw,92px) auto', maxWidth: 1080, padding: '0 clamp(20px,4vw,56px)' }}>
              <div style={{ maxWidth: 760, margin: '0 auto', border: `1px solid ${C.border}`, padding: 'clamp(30px,4.5vw,48px)' }}>
                <div style={{ fontFamily: FU, fontSize: 11, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: GOLD, marginBottom: 26, textAlign: 'center' }}>
                  {block.heading || 'At a Glance'}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px 32px' }}>
                  {agItems.map((it, i) => (
                    <div key={i} style={{ borderBottom: `1px solid ${C.border}`, paddingBottom: 10 }}>
                      <div style={{ fontFamily: FU, fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.subtle || C.muted }}>{it.label}</div>
                      <div style={{ fontFamily: FD, fontSize: 16, color: C.text, marginTop: 4 }}>{it.value}</div>
                    </div>
                  ))}
                </div>
              </div>
            </aside>
          );
        }

        // ── know_before ── "What Couples Should Know". Positives AND practical
        // limitations, decision-relevant facts only — never marketing positives alone.
        if (type === 'know_before') {
          const kbItems = (block.items || []).filter(Boolean);
          if (!kbItems.length) return null;
          return (
            <aside key={key} style={{ margin: 'clamp(52px,7vw,92px) auto', maxWidth: 1080, padding: '0 clamp(20px,4vw,56px)' }}>
              <div style={{ maxWidth: 760, margin: '0 auto', background: C.card, border: `1px solid ${C.border}`, padding: 'clamp(30px,4.5vw,48px)' }}>
                <div style={{ fontFamily: FU, fontSize: 11, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: GOLD, marginBottom: 22 }}>
                  {block.heading || 'What Couples Should Know'}
                </div>
                <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                  {kbItems.map((it, i) => (
                    <li key={i} style={{ display: 'flex', gap: 13, alignItems: 'flex-start', marginBottom: i === kbItems.length - 1 ? 0 : 15 }}>
                      <span style={{ color: GOLD, fontSize: 15, lineHeight: 1.7, flexShrink: 0 }}>&#9670;</span>
                      <span style={{ fontFamily: FD, fontSize: 16, lineHeight: 1.65, color: C.text }}>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          );
        }

        // ── best_for ── Small and decisive, evidence-backed tags, not SEO keywords.
        if (type === 'best_for') {
          const bfItems = (block.items || []).filter(Boolean);
          if (!bfItems.length) return null;
          return (
            <aside key={key} style={{ margin: 'clamp(44px,6vw,76px) auto', maxWidth: 1080, padding: '0 clamp(20px,4vw,56px)' }}>
              <div style={{ maxWidth: 760, margin: '0 auto', textAlign: 'center' }}>
                <div style={{ fontFamily: FU, fontSize: 11, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: GOLD, marginBottom: 20 }}>
                  {block.heading || 'Best For'}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center' }}>
                  {bfItems.map((it, i) => (
                    <span key={i} style={{ fontFamily: FU, fontSize: 12, letterSpacing: '0.04em', color: C.text, border: `1px solid ${C.border}`, borderRadius: 20, padding: '9px 20px' }}>{it}</span>
                  ))}
                </div>
              </div>
            </aside>
          );
        }

        // ── section_divider ──────────────────────────────────────────────────
        if (type === 'section_divider') {
          const divStyle = block.style || 'ornament';
          if (divStyle === 'space') return <div key={key} style={{ height: 80 }} />;
          if (divStyle === 'line') return (
            <div key={key} style={{ margin: '48px auto', maxWidth: 1080, padding: '0 clamp(20px,4vw,56px)', display: 'flex', justifyContent: 'center' }}>
              <div style={{ width: 120, height: 1, background: '#c9a96e' }} />
            </div>
          );
          return (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '48px auto', maxWidth: 1080, padding: '0 clamp(20px,4vw,56px)' }}>
              <div style={{ flex: 1, height: 1, background: 'rgba(201,169,110,0.18)' }} />
              <span style={{ color: '#c9a96e', fontSize: 10, letterSpacing: '0.3em' }}>✦</span>
              <div style={{ flex: 1, height: 1, background: 'rgba(201,169,110,0.18)' }} />
            </div>
          );
        }

        // ── dual_image ───────────────────────────────────────────────────────
        if (type === 'dual_image' && (block.imageA || block.imageB))
          return <DualImageBlock key={key} block={block} C={C} />;

        // ── product_tile ─────────────────────────────────────────────────────
        if (type === 'product_tile') {
          const ptProducts = block.products || [];
          if (!ptProducts.length) return null;
          const ptCols = block.columns || Math.min(ptProducts.length, 3);
          const ptLight = isLightBg(C.bg);
          return (
            <div key={key} style={{ margin: '40px auto', maxWidth: 1080, padding: '0 clamp(20px,4vw,56px)' }}>
              {block.sectionTitle && (
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontFamily: "'Nunito','Inter',sans-serif", fontSize: 8, color: '#c9a96e', letterSpacing: '0.28em', textTransform: 'uppercase', marginBottom: 6, opacity: 0.8 }}>The LWD Edit</div>
                  <div style={{ fontFamily: "'Gilda Display','Playfair Display',Georgia,serif", fontSize: 'clamp(18px,1.8vw,24px)', fontWeight: 400, color: C.text }}>{block.sectionTitle}</div>
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${ptCols},1fr)`, gap: 'clamp(16px,2vw,24px)' }}>
                {ptProducts.map((p, ii) => (
                  <ProductCard key={ii} product={{ image: p.image, brand: p.brand, title: p.name, price: p.price, salePrice: p.salePrice, description: p.desc, affiliateUrl: p.url, badge: p.badge, editorialNote: p.editorialNote, retailer: p.retailer, cta: p.cta || 'Shop Now' }} variant="portrait" isLight={ptLight} />
                ))}
              </div>
            </div>
          );
        }

        // ── style_tip ────────────────────────────────────────────────────────
        if (type === 'style_tip')
          return (
            <div key={key} style={{ margin: '48px auto', maxWidth: 1080, padding: '0 clamp(20px,4vw,56px)' }}>
              <StyleAdvice
                heading={block.heading}
                body={block.body}
                tip={block.tip}
                author={typeof block.author === 'object' ? block.author?.name : block.author}
                isLight={isLightBg(C.bg)}
              />
            </div>
          );

        // ── faq ──────────────────────────────────────────────────────────────
        if (type === 'faq') {
          const faqs = block.items || (block.question ? [{ question: block.question, answer: block.answer }] : []);
          if (!faqs.length) return null;
          return (
            <div key={key} style={{ margin: '32px auto', maxWidth: 1080, padding: '0 clamp(20px,4vw,56px)' }}>
              {block.heading && <div style={{ fontFamily: "'Gilda Display','Playfair Display',Georgia,serif", fontSize: 'clamp(20px,2vw,26px)', fontWeight: 400, color: C.text, marginBottom: 20 }}>{block.heading}</div>}
              <div style={{ display: 'flex', flexDirection: 'column', border: '1px solid rgba(201,169,110,0.18)', borderRadius: 4, overflow: 'hidden' }}>
                {faqs.map((faq, fi) => (
                  <details key={fi} style={{ borderBottom: fi < faqs.length - 1 ? '1px solid rgba(201,169,110,0.18)' : 'none' }}>
                    <summary style={{ padding: '16px 20px', cursor: 'pointer', fontFamily: "'Nunito','Inter',sans-serif", fontSize: 14, fontWeight: 600, color: C.text, listStyle: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      {faq.question}
                      <span style={{ color: '#c9a96e', flexShrink: 0, marginLeft: 12 }}>+</span>
                    </summary>
                    <div style={{ padding: '0 20px 16px', fontFamily: "'Nunito','Inter',sans-serif", fontSize: 14, color: C.muted || 'rgba(245,240,232,0.7)', lineHeight: 1.7 }}>
                      {faq.answer}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          );
        }

        // ── shop_collection_header, branded section header ──────────────────
        if (type === 'shop_collection_header')
          return <ShopCollectionHeaderBlock key={key} block={block} C={C} />;

        // ── editorial_feature, full-width image + text split ────────────────
        if (type === 'editorial_feature')
          return <EditorialFeatureBlock key={key} block={block} C={C} isMobile={isMobile} />;

        // ── product_story, narrative product card ───────────────────────────
        if (type === 'product_story')
          return <ProductStoryBlock key={key} block={block} C={C} isMobile={isMobile} />;

        // ── collection_strip, horizontal scroll carousel ────────────────────
        if (type === 'collection_strip')
          return <CollectionStripBlock key={key} block={block} C={C} isMobile={isMobile} />;

        // ── Text blocks, rendered inside the readable column ──
        return (
          <div key={key} style={{
            maxWidth: 1080,
            margin: '0 auto',
            padding: '0 clamp(20px, 4vw, 56px)',
          }}>
            {/* body_wysiwyg, Tiptap-produced HTML containing paragraphs,
                images, videos, galleries, embeds, and the Phase 1 block
                types. Rendered via dangerouslySetInnerHTML; the reader
                CSS below (.lwd-pullquote, .lwd-gallery, .lwd-cover, etc.)
                styles each block to match the editor preview. */}
            {type === 'body_wysiwyg' && (
              <div
                className="lwd-wysiwyg"
                style={{
                  fontFamily: articleFS,
                  fontSize: 'clamp(16px, 1.5vw, 18.5px)',
                  lineHeight: articleLineH,
                  color: articleBodyCol,
                }}
                dangerouslySetInnerHTML={safeHtml(block.text || block.content || '')}

              />
            )}

            {(type === 'paragraph' || type === 'body_wysiwyg') && (
              <div
                className="lwd-wysiwyg"
                style={{
                  fontFamily: articleFS,
                  fontSize: 'clamp(16px, 1.5vw, 18.5px)',
                  lineHeight: articleLineH,
                  color: articleBodyCol,
                  margin: '0 0 1.65em',
                }}
                dangerouslySetInnerHTML={safeHtml(block.text || block.content || '')}

              />
            )}

            {(type === 'heading' || type === 'h2') && (() => {
              // Modern treatment: sans + weight≥600 → uppercase chapter-label; serif → large heading
              const isSansLabel = articleH2Weight >= 600 && /nunito|inter|outfit|montserrat|lato|poppins|raleway|open sans|roboto|dm sans|manrope|urbanist|jost|sora|figtree|bricolage/i.test(articleH2Font);
              return (
                <div style={{ margin: isSansLabel ? '3.6em 0 1.2em' : '2.6em 0 0.8em' }}>
                  {isSansLabel && (
                    <div style={{
                      width: 32, height: 1, background: GOLD, marginBottom: 14, opacity: 0.7,
                    }} />
                  )}
                  <div
                    className="lwd-wysiwyg"
                    style={{
                      fontFamily: articleH2Font,
                      fontWeight: articleH2Weight,
                      fontSize: isSansLabel ? 'clamp(9px, 0.9vw, 11px)' : 'clamp(24px, 2.8vw, 38px)',
                      lineHeight: isSansLabel ? 1 : 1.15,
                      color: isSansLabel ? GOLD : C.text,
                      letterSpacing: isSansLabel ? '0.22em' : '-0.01em',
                      textTransform: isSansLabel ? 'uppercase' : 'none',
                    }}
                    dangerouslySetInnerHTML={safeHtml(block.text || block.content || '')}

                  />
                </div>
              );
            })()}

            {(type === 'subheading' || type === 'h3') && (
              <div
                className="lwd-wysiwyg"
                style={{
                  fontFamily: articleH3Font,
                  fontWeight: articleH3Weight,
                  fontStyle: 'italic',
                  fontSize: 'clamp(19px, 2vw, 26px)',
                  lineHeight: 1.25,
                  color: C.text,
                  margin: '2.2em 0 0.6em',
                }}
                dangerouslySetInnerHTML={safeHtml(block.text || block.content || '')}

              ></div>
            )}

            {(type === 'quote' || type === 'pull_quote' || type === 'blockquote') && (
              <blockquote style={{
                margin: '2.6em 0',
                padding: '4px 0 4px 28px',
                borderLeft: `2px solid ${GOLD}`,
              }}>
                <p style={{
                  fontFamily: articleFD,
                  fontWeight: 400,
                  fontStyle: 'italic',
                  fontSize: 'clamp(20px, 2.2vw, 30px)',
                  lineHeight: 1.4,
                  color: C.text,
                  margin: '0 0 10px',
                }}>
                  "{block.text || block.content}"
                </p>
                {block.attribution && (
                  <cite style={{
                    fontFamily: FU,
                    fontSize: 9,
                    letterSpacing: '0.2em',
                    textTransform: 'uppercase',
                    color: C.muted,
                    fontStyle: 'normal',
                  }}>
                   , {block.attribution}
                  </cite>
                )}
              </blockquote>
            )}

            {type === 'image' && (() => {
              const src = block.url || block.src || block.image_url;
              if (!src) return null;
              return (
                <figure style={{ margin: '2.6em 0' }}>
                  <img
                    src={imgUrl(src, { width: 1080, resize: 'contain' })}
                    alt={block.alt || block.caption || ''}
                    loading="lazy"
                    style={{
                      // height:auto shows the whole image proportionally; resize:'contain'
                      // guarantees the transform is proportional too (no squashed delivery).
                      // objectFit is a no-op at auto height and removed to avoid confusion.
                      width: '100%', height: 'auto', display: 'block',
                    }}
                  />
                  {block.caption && (
                    <figcaption style={{
                      fontFamily: FU, fontSize: 10, letterSpacing: '0.14em',
                      color: C.subtle, marginTop: 8, textAlign: 'center',
                    }}>
                      {block.caption}
                    </figcaption>
                  )}
                </figure>
              );
            })()}

            {type === 'divider' && (
              <hr style={{
                border: 'none',
                height: 1,
                background: `linear-gradient(to right, transparent, ${GOLD}44, transparent)`,
                margin: '3.4em 0',
              }} />
            )}
          </div>
        );
}

// ── PrevNextNav ───────────────────────────────────────────────────────────────
// Same-category continuation, immediate neighbours only (approved plan,
// 2026-09-12: Option 1 — same category, editorial_priority then
// published_at, never site-wide chronological, no manual curation, omit a
// missing side rather than cross into another category). Reuses the round
// author-avatar visual language (ArticleAuthor's 52px circular image) rather
// than inventing a new card shape. Deliberately separate from "You May Also
// Like" below it — this is "keep reading this story," not discovery.
function PrevNextNav({ prev, next, onNavigateArticle, C }) {
  if (!prev && !next) return null;

  const Item = ({ post, dir }) => {
    if (!post) return <div />;
    const isPrev = dir === 'prev';
    return (
      <button
        onClick={() => onNavigateArticle(post.slug)}
        style={{
          display: 'flex', alignItems: 'center', gap: 14,
          flexDirection: isPrev ? 'row' : 'row-reverse',
          textAlign: isPrev ? 'left' : 'right',
          background: 'none', border: 'none', cursor: 'pointer', padding: 0,
          width: '100%', minWidth: 0,
        }}
      >
        {post.cover_image && (
          <img
            src={imgUrl(post.cover_image, { width: 200 })}
            alt={post.cover_image_alt || post.title}
            style={{
              width: 52, height: 52, borderRadius: '50%',
              objectFit: 'cover', flexShrink: 0,
              border: `1px solid ${C.border2}`,
            }}
          />
        )}
        <div style={{ minWidth: 0 }}>
          <div style={{
            fontFamily: FU, fontSize: 8, letterSpacing: '0.28em',
            textTransform: 'uppercase', color: C.subtle, marginBottom: 4,
          }}>
            {isPrev ? '← Previous' : 'Next →'}
          </div>
          <div style={{
            fontFamily: FD, fontSize: 'clamp(14px,1.3vw,17px)', color: C.text,
            lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis',
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          }}>
            {post.title}
          </div>
        </div>
      </button>
    );
  };

  return (
    <div style={{
      maxWidth: 1080, margin: '0 auto',
      padding: 'clamp(28px, 3vw, 40px) clamp(20px, 4vw, 56px)',
      borderTop: `1px solid ${C.border}`,
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: 24,
    }}>
      <Item post={prev} dir="prev" />
      <Item post={next} dir="next" />
    </div>
  );
}

// ── AlsoInThisIssue ───────────────────────────────────────────────────────────

function AlsoInThisIssue({ posts, onNavigateArticle, C }) {
  if (!posts?.length) return null;
  return (
    <section style={{
      borderTop: `1px solid ${C.border}`,
      padding: 'clamp(48px, 6vw, 80px) clamp(24px, 6vw, 80px)',
      background: C.surface,
    }}>
      <div style={{ marginBottom: 36 }}>
        <div style={{
          fontFamily: FU, fontSize: 8, letterSpacing: '0.36em',
          textTransform: 'uppercase', color: GOLD, marginBottom: 8,
        }}>
          The LWD Edit
        </div>
        <h2 style={{
          fontFamily: FD, fontWeight: 400,
          fontSize: 'clamp(20px, 2vw, 28px)',
          color: C.text, margin: 0,
        }}>
          You May Also Like
        </h2>
      </div>

      <style>{`
        .lwd-also-rail { scrollbar-width: none; -ms-overflow-style: none; }
        .lwd-also-rail::-webkit-scrollbar { display: none; }
      `}</style>
      <div className="lwd-also-rail" style={{
        display: 'flex',
        gap: 'clamp(20px, 2vw, 32px)',
        overflowX: 'auto',
        scrollSnapType: 'x mandatory',
        WebkitOverflowScrolling: 'touch',
        paddingBottom: 4,
      }}>
        {posts.map(p => (
          <article
            key={p.id || p.slug}
            onClick={() => onNavigateArticle(p.slug)}
            style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 0,
              flex: '0 0 clamp(200px, 18vw, 248px)', scrollSnapAlign: 'start' }}
          >
            {/* Portrait image, 3:4 */}
            <div style={{ aspectRatio: '3/4', overflow: 'hidden', background: C.card, marginBottom: 14 }}>
              {p.cover_image && (
                <img
                  src={imgUrl(p.cover_image, { width: 800, resize: 'contain' })}
                  alt={p.title}
                  loading="lazy"
                  style={{
                    width: '100%', height: '100%', objectFit: 'cover', display: 'block',
                    transition: 'transform 0.6s ease',
                  }}
                  onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.04)'}
                  onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                />
              )}
            </div>
            {p.category_label && (
              <span style={{
                fontFamily: FU, fontSize: 7, letterSpacing: '0.28em',
                textTransform: 'uppercase', color: GOLD, marginBottom: 8, display: 'block',
              }}>
                {p.category_label}
              </span>
            )}
            <h3 style={{
              fontFamily: FD, fontWeight: 400,
              fontSize: 'clamp(15px, 1.4vw, 19px)',
              lineHeight: 1.25, color: C.text, margin: '0 0 10px',
            }}>
              {p.title}
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              {(p.author_data?.name) && (
                <span style={{ fontFamily: FU, fontSize: 8, color: C.muted, letterSpacing: '0.1em' }}>
                  {p.author_data.name}
                </span>
              )}
              {(p.author_data?.name) && p.reading_time && (
                <span style={{ color: C.subtle, fontSize: 8 }}>·</span>
              )}
              {p.reading_time && (
                <span style={{ fontFamily: FU, fontSize: 8, color: C.subtle, letterSpacing: '0.1em' }}>
                  {p.reading_time} min read
                </span>
              )}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

// ── RelatedVenues ── LWD Linking System (Tier 2): real <a href> internal links
// from the Edit out to the venue/vendor listings it features or shares a
// destination with. Crawlable (passes link equity to commercial pages) and
// SPA-intercepted for client nav.
function RelatedVenues({ venues, C }) {
  if (!venues || venues.length === 0) return null;
  return (
    <section style={{ maxWidth: 1180, margin: '0 auto', padding: '8px 24px 56px', borderTop: `1px solid ${C.border}` }}>
      <div style={{ fontFamily: FU, fontSize: 8, letterSpacing: '0.36em', textTransform: 'uppercase', color: GOLD, margin: '40px 0 8px' }}>
        From the directory
      </div>
      <h2 style={{ fontFamily: FD, fontWeight: 400, fontSize: 'clamp(20px, 2vw, 28px)', color: C.text, margin: '0 0 22px' }}>
        Related venues
      </h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 18 }}>
        {venues.map((v) => {
          const img = listingImage(v);
          return (
            <a key={v.id} href={listingUrl(v)} style={{ textDecoration: 'none', display: 'block' }}>
              <div style={{ aspectRatio: '4 / 3', borderRadius: 6, overflow: 'hidden', background: C.border, marginBottom: 10 }}>
                {img && <img src={imgUrl(img, { width: 500 })} alt={v.name} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />}
              </div>
              <div style={{ fontFamily: FD, fontWeight: 400, fontSize: 16, color: C.text, lineHeight: 1.25, marginBottom: 3 }}>{v.name}</div>
              <div style={{ fontFamily: FU, fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.muted }}>
                {[v.city, v.region].filter(Boolean).join(' · ')}
              </div>
            </a>
          );
        })}
      </div>
    </section>
  );
}

// ── useRecentlyViewed ─────────────────────────────────────────────────────────

const RV_KEY = 'lwd_edit_viewed';
const RV_MAX = 8;

function useRecentlyViewed(post) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!post?.slug) return;
    try {
      const stored = JSON.parse(localStorage.getItem(RV_KEY) || '[]');
      const filtered = stored.filter(p => p.slug !== post.slug);
      const entry = {
        slug:           post.slug,
        title:          post.title,
        cover_image:    post.cover_image || (Array.isArray(post.cover_images) ? post.cover_images[0] : '') || '',
        category_label: post.category_label,
        category_slug:  post.category_slug,
        author_data:    post.author_data,
        reading_time:   post.reading_time,
      };
      const updated = [entry, ...filtered].slice(0, RV_MAX);
      localStorage.setItem(RV_KEY, JSON.stringify(updated));
      // Show items excluding the one just added (the current article)
      setItems(filtered.slice(0, 4));
    } catch {}
  }, [post?.slug]);

  return items;
}

// ── RecentlyViewed ────────────────────────────────────────────────────────────

function RecentlyViewed({ items, onNavigateArticle, C }) {
  if (!items?.length) return null;
  return (
    <section style={{
      borderTop: `1px solid ${C.border}`,
      padding: 'clamp(40px, 5vw, 64px) clamp(24px, 6vw, 80px)',
      background: C.bg,
    }}>
      <div style={{ marginBottom: 28 }}>
        <div style={{
          fontFamily: FU, fontSize: 8, letterSpacing: '0.36em',
          textTransform: 'uppercase', color: GOLD, marginBottom: 8,
        }}>
          The LWD Edit
        </div>
        <h2 style={{
          fontFamily: FD, fontWeight: 400,
          fontSize: 'clamp(18px, 1.8vw, 24px)',
          color: C.text, margin: 0,
        }}>
          Recently Viewed
        </h2>
      </div>

      {/* Horizontal scroll on mobile, auto grid on desktop */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, 200px)',
        justifyContent: 'start',
        gap: 'clamp(16px, 2vw, 28px)',
        overflowX: 'auto',
        scrollSnapType: 'x mandatory',
        WebkitOverflowScrolling: 'touch',
        paddingBottom: 4,
      }}>
        {items.map(p => (
          <article
            key={p.slug}
            onClick={() => onNavigateArticle(p.slug)}
            style={{
              cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 0,
              scrollSnapAlign: 'start', minWidth: 160,
            }}
          >
            <div style={{ aspectRatio: '3/4', overflow: 'hidden', background: C.card, marginBottom: 12 }}>
              {p.cover_image && (
                <img
                  src={imgUrl(p.cover_image, { width: 800, resize: 'contain' })}
                  alt={p.title}
                  loading="lazy"
                  style={{
                    width: '100%', height: '100%', objectFit: 'cover', display: 'block',
                    transition: 'transform 0.6s ease',
                  }}
                  onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.04)'}
                  onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                />
              )}
            </div>
            {p.category_label && (
              <span style={{
                fontFamily: FU, fontSize: 7, letterSpacing: '0.24em',
                textTransform: 'uppercase', color: GOLD, marginBottom: 6, display: 'block',
              }}>
                {p.category_label}
              </span>
            )}
            <h3 style={{
              fontFamily: FD, fontWeight: 400,
              fontSize: 'clamp(13px, 1.1vw, 16px)',
              lineHeight: 1.3, color: C.text, margin: '0 0 8px',
            }}>
              {p.title}
            </h3>
            {p.author_data?.name && (
              <span style={{ fontFamily: FU, fontSize: 7, color: C.subtle, letterSpacing: '0.1em' }}>
                {p.author_data.name}
              </span>
            )}
          </article>
        ))}
      </div>
    </section>
  );
}

// ── ArticleAuthor ─────────────────────────────────────────────────────────────

function ArticleAuthor({ post, C, masthead }) {
  // Directory features carry the publication masthead byline, not a personal bio.
  if (masthead) {
    return (
      <div style={{ maxWidth: 1080, margin: '0 auto', padding: 'clamp(32px, 4vw, 56px) clamp(20px, 4vw, 56px)', borderTop: `1px solid ${C.border}` }}>
        <div style={{ fontFamily: FU, fontSize: 8, letterSpacing: '0.28em', textTransform: 'uppercase', color: C.subtle, marginBottom: 8 }}>Written by</div>
        <div style={{ fontFamily: FU, fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: C.text, marginBottom: 8 }}>The LWD Edit</div>
        <p style={{ fontFamily: FS, fontSize: 'clamp(13px, 1.2vw, 15px)', lineHeight: 1.75, color: C.body, margin: 0, maxWidth: 620 }}>The editorial team at Luxury Wedding Directory, profiling the world's finest wedding venues and creatives.</p>
      </div>
    );
  }
  const author = post.author_data;
  if (!author?.name) return null;

  return (
    <div style={{
      maxWidth: 1080,
      margin: '0 auto',
      padding: 'clamp(32px, 4vw, 56px) clamp(20px, 4vw, 56px)',
      borderTop: `1px solid ${C.border}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 18 }}>
        {author.avatar && (
          <img
            src={imgUrl(author.avatar, { width: 200 })}
            alt={author.name}
            style={{
              width: 52, height: 52, borderRadius: '50%',
              objectFit: 'cover', flexShrink: 0,
              border: `1px solid ${C.border2}`,
            }}
          />
        )}
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{
              fontFamily: FU, fontSize: 8, letterSpacing: '0.28em',
              textTransform: 'uppercase', color: C.subtle,
            }}>
              Written by
            </span>
          </div>
          <div style={{
            fontFamily: FU, fontSize: 11, letterSpacing: '0.16em',
            textTransform: 'uppercase', color: C.text, marginBottom: 3,
          }}>
            {author.name}
          </div>
          {author.role && (
            <div style={{
              fontFamily: FU, fontSize: 9, letterSpacing: '0.14em',
              color: C.muted, marginBottom: 10,
            }}>
              {author.role}
            </div>
          )}
          {author.bio && (
            <p style={{
              fontFamily: FS, fontSize: 'clamp(13px, 1.2vw, 15px)',
              lineHeight: 1.75, color: C.body, margin: 0,
            }}>
              {author.bio}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ── SocialShare ───────────────────────────────────────────────────────────────

function SocialShare({ post, C }) {
  const [copied, setCopied] = useState(false);

  const url   = typeof window !== 'undefined' ? window.location.href : '';
  const title = post.title || '';
  const img   = post.cover_image || '';

  function copyLink() {
    navigator.clipboard?.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    });
  }
  function openPinterest() {
    const u = `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(url)}&media=${encodeURIComponent(img)}&description=${encodeURIComponent(title + ', LWD · The LWD Edit')}`;
    window.open(u, '_blank', 'noopener');
  }
  function openWhatsApp() {
    window.open(`https://wa.me/?text=${encodeURIComponent(title + '\n' + url)}`, '_blank', 'noopener');
  }

  const btnStyle = {
    fontFamily: FU, fontSize: 8, letterSpacing: '0.2em',
    textTransform: 'uppercase',
    color: C.muted,
    background: 'none',
    border: `1px solid ${C.border}`,
    padding: '6px 14px', cursor: 'pointer', borderRadius: 2,
    transition: 'border-color 0.2s, color 0.2s',
  };

  return (
    <div style={{
      maxWidth: 1080,
      margin: '0 auto',
      padding: '0 clamp(20px, 4vw, 56px) clamp(32px, 4vw, 56px)',
      display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
    }}>
      <span style={{
        fontFamily: FU, fontSize: 8, letterSpacing: '0.3em',
        textTransform: 'uppercase', color: C.subtle, marginRight: 4,
      }}>
        Share
      </span>
      <button
        onClick={copyLink}
        style={{ ...btnStyle, color: copied ? GOLD : C.muted, borderColor: copied ? `${GOLD}55` : C.border }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = `${GOLD}55`; e.currentTarget.style.color = GOLD; }}
        onMouseLeave={e => { if (!copied) { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.muted; } }}
      >
        {copied ? '✓ Copied' : '⌁ Copy link'}
      </button>
      <button
        onClick={openPinterest}
        style={btnStyle}
        onMouseEnter={e => { e.currentTarget.style.borderColor = `${GOLD}55`; e.currentTarget.style.color = GOLD; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.muted; }}
      >
        Pinterest
      </button>
      <button
        onClick={openWhatsApp}
        style={btnStyle}
        onMouseEnter={e => { e.currentTarget.style.borderColor = `${GOLD}55`; e.currentTarget.style.color = GOLD; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.muted; }}
      >
        WhatsApp
      </button>
    </div>
  );
}

// ── SubscribeNudge ────────────────────────────────────────────────────────────
// End-of-article subscribe capture, dark, minimal, gold accent.

function SubscribeNudge({ C }) {
  const [email,   setEmail]   = useState('');
  const [done,    setDone]    = useState(false);
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    await subscribeNewsletter({ email: email.trim(), source: 'editorial_article' });
    setDone(true);
    setLoading(false);
  }

  return (
    <section style={{
      background: DARK.bg,
      padding: 'clamp(56px, 8vw, 100px) clamp(24px, 6vw, 80px)',
      textAlign: 'center',
      borderTop: `1px solid ${GOLD}18`,
    }}>
      {done ? (
        <div>
          <div style={{
            fontFamily: FU, fontSize: 8, letterSpacing: '0.36em',
            textTransform: 'uppercase', color: GOLD, marginBottom: 16,
          }}>
            ✦ Received
          </div>
          <p style={{
            fontFamily: FD, fontStyle: 'italic', fontWeight: 400,
            fontSize: 'clamp(18px, 2vw, 26px)', color: DARK.muted, margin: 0,
          }}>
            We'll be in touch.
          </p>
        </div>
      ) : (
        <>
          <div style={{
            fontFamily: FU, fontSize: 8, letterSpacing: '0.4em',
            textTransform: 'uppercase', color: GOLD, marginBottom: 20,
          }}>
            The Editorial
          </div>
          <h2 style={{
            fontFamily: FD, fontWeight: 400,
            fontSize: 'clamp(24px, 3vw, 42px)',
            lineHeight: 1.2, color: DARK.text, margin: '0 0 12px',
          }}>
            Stay close to the stories that matter.
          </h2>
          <p style={{
            fontFamily: FS, fontSize: 'clamp(14px, 1.3vw, 17px)',
            color: DARK.muted, margin: '0 auto 36px', maxWidth: 440, lineHeight: 1.7,
          }}>
            No noise. The editorial, when it's ready.
          </p>
          <form
            onSubmit={submit}
            style={{
              display: 'flex', gap: 0, maxWidth: 440, margin: '0 auto',
              border: `1px solid ${GOLD}40`,
            }}
          >
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="Your email address"
              required
              style={{
                flex: 1, background: 'transparent',
                border: 'none', outline: 'none',
                fontFamily: FU, fontSize: 10, letterSpacing: '0.1em',
                color: DARK.text, padding: '14px 18px',
              }}
            />
            <button
              type="submit"
              disabled={loading}
              style={{
                fontFamily: FU, fontSize: 8, letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: DARK.bg, background: GOLD,
                border: 'none', padding: '14px 22px',
                cursor: loading ? 'wait' : 'pointer',
                flexShrink: 0, whiteSpace: 'nowrap',
              }}
            >
              {loading ? '…' : 'Subscribe →'}
            </button>
          </form>
        </>
      )}
    </section>
  );
}

// ── VTHero, refined Vogue/Tatler-style article opener ──────────────────────
// Renders as the article hero when the FIRST content block is a vt_section.
// This keeps the system honest, when a writer chooses VT for their first
// section, the entire opening reads as VT (clean image + text chamber below);
// they don't get a cinematic gradient overlay smashed against their refined
// section. One language per article opening.
//
// Layout:
//   [ clean image, full-bleed, ~70vh, no overlay ]
//   [   ────  thin gold rule above eyebrow  ────  ]
//   [ EYEBROW · CATEGORY (caps, gold, wide-track)  ]
//   [   Italic Serif Title (clamp 32-60px)         ]
//   [   Sans-serif standfirst (16-18px)            ]
//   [   ─── thin gold rule (36px) ───              ]
//   [   By Author · 12 Mar 2026 · 6 min            ]
//
// Mobile: image collapses to ~58vh, type chamber padding shrinks but
// proportions stay restrained. No on-image text ever.
function VTHero({ post, theme, accent, label, cat, title, stan, author }) {
  const isLight = theme === 'light';
  const titleColor = isLight ? '#181410' : '#f5f0e8';
  const standColor = isLight ? 'rgba(24,20,16,0.68)' : 'rgba(245,240,232,0.74)';
  const subtleColor = isLight ? 'rgba(24,20,16,0.52)' : 'rgba(245,240,232,0.48)';
  const bg          = isLight ? '#FAF8F4' : '#0a0908';
  const cover       = post.cover_image
    || (Array.isArray(post.cover_images) && post.cover_images[0])
    || '';
  // hero_focal_point defaults to the bare 'center' column default on every row
  // (no y-offset), so treat that as "not curated" and keep the existing
  // letterbox-friendly default; only honour values someone actually set.
  const focalPoint  = (post.hero_focal_point && post.hero_focal_point !== 'center')
    ? post.hero_focal_point
    : 'center 32%';

  return (
    <header
      className="lwd-vt-hero"
      style={{
        background: bg,
        color: titleColor,
      }}
    >
      <style>{`
        @keyframes lwd-vt-rise {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .lwd-vt-hero .vt-rise { opacity: 0; animation: lwd-vt-rise 900ms cubic-bezier(0.2, 0.7, 0.2, 1) forwards; }
        .lwd-vt-hero .vt-rise.r1 { animation-delay: 100ms; }
        .lwd-vt-hero .vt-rise.r2 { animation-delay: 220ms; }
        .lwd-vt-hero .vt-rise.r3 { animation-delay: 360ms; }
        .lwd-vt-hero .vt-rise.r4 { animation-delay: 500ms; }
        @media (prefers-reduced-motion: reduce) {
          .lwd-vt-hero .vt-rise { animation: none; opacity: 1; }
        }
        @media (max-width: 640px) {
          .lwd-vt-hero h1 { font-size: clamp(22px, 6.5vw, 34px) !important; }
          /* Less text on mobile so the hero breathes, hide the standfirst;
             it still opens the article body. */
          .lwd-vt-hero .vt-stan { display: none !important; }
        }
      `}</style>

      {/* ── Image, clean, no overlay, true full-bleed via negative margins ── */}
      {cover && (
        <div
          className="vt-rise r1"
          style={{
            width: '100vw',
            marginLeft:  'calc(50% - 50vw)',
            marginRight: 'calc(50% - 50vw)',
            height: 'clamp(420px, 70vh, 760px)',
            background: '#0a0908',
            overflow: 'hidden',
          }}
        >
          <img
            src={imgUrl(cover, { width: 1600, resize: 'contain' })}
            alt={title || ''}
            fetchpriority="high"
            loading="eager"
            style={{
              width: '100%', height: '100%',
              objectFit: 'cover',
              objectPosition: focalPoint,
              display: 'block',
            }}
          />
        </div>
      )}

      {/* ── Title chamber, narrow column, generous whitespace ── */}
      <div style={{
        maxWidth: 760,
        margin: '0 auto',
        padding: 'clamp(56px, 7vw, 112px) clamp(20px, 4vw, 32px) clamp(48px, 6vw, 80px)',
        textAlign: 'center',
      }}>
        {/* Eyebrow / category, small caps, gold, wide tracking */}
        {(label || cat) && (
          <div className="vt-rise r2" style={{
            fontFamily: FU,
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: '0.32em',
            textTransform: 'uppercase',
            color: GOLD,
            marginBottom: 22,
          }}>
            {[label, cat].filter(Boolean).join(' · ')}
          </div>
        )}

        {/* Title, italic serif, restrained scale (NOT 100+px) */}
        <h1 className="vt-rise r3" style={{
          fontFamily: FD,
          fontWeight: 400,
          fontStyle: 'italic',
          fontSize: 'clamp(34px, 4.6vw, 60px)',
          lineHeight: 1.08,
          letterSpacing: '-0.018em',
          color: titleColor,
          margin: '0 0 22px',
        }}>
          {title}
        </h1>

        {/* Standfirst */}
        {stan && (
          <p className="vt-rise r3 vt-stan" style={{
            fontFamily: FU,
            fontWeight: 300,
            fontSize: 'clamp(15px, 1.4vw, 19px)',
            lineHeight: 1.65,
            color: standColor,
            margin: '0 auto 28px',
            maxWidth: 580,
          }}>
            {stan}
          </p>
        )}

        {/* Single ornament, 36px gold rule */}
        <div aria-hidden className="vt-rise r4" style={{
          width: 36, height: 1,
          background: GOLD, opacity: 0.7,
          margin: '0 auto 22px',
        }} />

        {/* Byline + save button */}
        <div className="vt-rise r4" style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
        }}>
          <div style={{
            fontFamily: FU, fontSize: 10, fontWeight: 500,
            letterSpacing: '0.22em', textTransform: 'uppercase',
            color: subtleColor,
          }}>
            {[author && `By ${author}`, fmtDate(post.published_at || post.created_at), post.reading_time && `${post.reading_time} min`]
              .filter(Boolean).join(' · ')}
          </div>
          <ArticleSaveButton slug={post.slug} size={15} C={{ muted: subtleColor }} inline />
        </div>
      </div>
    </header>
  );
}

// ── SplitHero, 50/50 left text · right image ────────────────────────────────
// SheerLuxe / HELLO! standard feature layout.
// Left: dark/light panel, category eyebrow, title, standfirst, byline.
// Right: full-height cover image, no overlay.
// Mobile: image stacks above text.
function SplitHero({ post, theme, accent, label, cat, title, stan, author }) {
  const isLight   = theme === 'light';
  const bg        = isLight ? '#FAF8F4' : '#0a0908';
  const titleCol  = isLight ? '#181410' : '#f5f0e8';
  const bodyCol   = isLight ? 'rgba(24,20,16,0.68)' : 'rgba(245,240,232,0.72)';
  const subtleCol = isLight ? 'rgba(24,20,16,0.48)' : 'rgba(245,240,232,0.44)';
  const cover     = post.cover_image
    || (Array.isArray(post.cover_images) && post.cover_images[0])
    || '';
  const [loaded, setLoaded] = useState(false);
  const { h1Font: H1F, h1Weight: H1W } = getEditorialTypo();

  return (
    <header style={{ background: bg, paddingTop: 'clamp(40px,5vw,80px)' }}>
      <style>{`
        @keyframes lwd-split-rise {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .lwd-split-hero .s-rise { opacity: 0; animation: lwd-split-rise 800ms cubic-bezier(0.2,0.7,0.2,1) forwards; }
        .lwd-split-hero .s-rise.s1 { animation-delay: 80ms; }
        .lwd-split-hero .s-rise.s2 { animation-delay: 210ms; }
        .lwd-split-hero .s-rise.s3 { animation-delay: 360ms; }
        .lwd-split-hero .s-rise.s4 { animation-delay: 500ms; }
        @media (max-width: 768px) {
          .lwd-split-inner { flex-direction: column !important; }
          .lwd-split-text  { padding: 40px 24px 48px !important; }
          .lwd-split-image { height: 66vw !important; width: 100% !important; flex: none !important; }
        }
        @media (prefers-reduced-motion: reduce) {
          .lwd-split-hero .s-rise { animation: none; opacity: 1; }
        }
        @media (max-width: 640px) {
          .lwd-split-hero h1 { font-size: clamp(22px, 6.5vw, 34px) !important; }
          /* Less text on mobile so the hero breathes, hide the standfirst;
             it still opens the article body. */
          .lwd-split-hero .s-stan { display: none !important; }
        }
      `}</style>
      <div className="lwd-split-hero">
        <div className="lwd-split-inner" style={{ display: 'flex', minHeight: 'clamp(460px,62vh,720px)' }}>

          {/* Text panel */}
          <div className="lwd-split-text" style={{
            flex: '0 0 50%',
            display: 'flex', flexDirection: 'column', justifyContent: 'center',
            padding: 'clamp(48px,7vw,96px) clamp(32px,5vw,72px)',
            background: bg,
          }}>
            {(label || cat) && (
              <div className="s-rise s1" style={{
                fontFamily: FU, fontSize: 9, fontWeight: 700,
                letterSpacing: '0.30em', textTransform: 'uppercase',
                color: GOLD, marginBottom: 20,
              }}>
                {[label, cat].filter(Boolean).join(' · ')}
              </div>
            )}
            <h1 className="s-rise s2" style={{
              fontFamily: H1F, fontWeight: H1W, fontStyle: 'italic',
              fontSize: 'clamp(34px,3.8vw,62px)',
              lineHeight: 1.04, letterSpacing: '-0.018em',
              color: titleCol, margin: '0 0 20px',
            }}>
              {title}
            </h1>
            {stan && (
              <p className="s-rise s3 s-stan" style={{
                fontFamily: FU, fontWeight: 300,
                fontSize: 'clamp(14px,1.3vw,18px)',
                lineHeight: 1.7, color: bodyCol,
                margin: '0 0 28px', maxWidth: 480,
              }}>
                {stan}
              </p>
            )}
            <div aria-hidden className="s-rise s4" style={{
              width: 32, height: 1, background: GOLD, opacity: 0.65, marginBottom: 18,
            }} />
            {/* Byline + save, SheerLuxe style: meta left, bookmark right */}
            <div className="s-rise s4" style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
            }}>
              <div style={{
                fontFamily: FU, fontSize: 10, letterSpacing: '0.20em',
                textTransform: 'uppercase', color: subtleCol,
              }}>
                {[author && `By ${author}`, fmtDate(post.published_at || post.created_at), post.reading_time && `${post.reading_time} min`]
                  .filter(Boolean).join(' · ')}
              </div>
              <ArticleSaveButton slug={post.slug} size={16} C={{ muted: subtleCol }} inline />
            </div>
          </div>

          {/* Image panel */}
          {cover && (
            <div className="lwd-split-image" style={{
              flex: '0 0 50%', overflow: 'hidden', background: '#0a0908',
            }}>
              <img
                src={imgUrl(cover, { width: 1200, resize: 'contain' })}
                alt={title}
                fetchpriority="high"
                loading="eager"
                onLoad={() => setLoaded(true)}
                style={{
                  width: '100%', height: '100%', objectFit: 'cover', display: 'block',
                  opacity: loaded ? 1 : 0, transition: 'opacity 0.6s ease',
                }}
              />
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

// ── MinimalHero, text only, no hero image ────────────────────────────────────
// Vogue Business / Financial Times editorial style.
// Centred category, large italic title, standfirst, byline rule.
// Used for opinion, comment, trend analysis, data-forward pieces.
function MinimalHero({ post, theme, accent, label, cat, title, stan, author }) {
  const isLight   = theme === 'light';
  const bg        = isLight ? '#FAF8F4' : '#0a0908';
  const titleCol  = isLight ? '#181410' : '#f5f0e8';
  const bodyCol   = isLight ? 'rgba(24,20,16,0.66)' : 'rgba(245,240,232,0.70)';
  const subtleCol = isLight ? 'rgba(24,20,16,0.48)' : 'rgba(245,240,232,0.44)';
  const borderCol = isLight ? 'rgba(24,20,16,0.10)' : 'rgba(245,240,232,0.10)';
  const { h1Font: H1F, h1Weight: H1W } = getEditorialTypo();

  return (
    <header style={{ background: bg, paddingTop: 'clamp(60px,8vw,120px)' }}>
      <style>{`
        @keyframes lwd-min-rise {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .lwd-min-hero .m-rise { opacity: 0; animation: lwd-min-rise 750ms cubic-bezier(0.2,0.7,0.2,1) forwards; }
        .lwd-min-hero .m-rise.m1 { animation-delay: 60ms; }
        .lwd-min-hero .m-rise.m2 { animation-delay: 180ms; }
        .lwd-min-hero .m-rise.m3 { animation-delay: 310ms; }
        .lwd-min-hero .m-rise.m4 { animation-delay: 450ms; }
        @media (prefers-reduced-motion: reduce) {
          .lwd-min-hero .m-rise { animation: none; opacity: 1; }
        }
        @media (max-width: 640px) {
          .lwd-min-hero h1 { font-size: clamp(28px, 8vw, 40px) !important; }
          /* Less text on mobile so the hero breathes, hide the standfirst;
             it still opens the article body. */
          .lwd-min-hero .m-stan { display: none !important; }
        }
      `}</style>
      <div className="lwd-min-hero" style={{
        maxWidth: 820, margin: '0 auto',
        padding: '0 clamp(20px,5vw,56px)',
        textAlign: 'center',
      }}>
        {(label || cat) && (
          <div className="m-rise m1" style={{
            fontFamily: FU, fontSize: 9, fontWeight: 700,
            letterSpacing: '0.32em', textTransform: 'uppercase',
            color: GOLD, marginBottom: 24,
          }}>
            {[label, cat].filter(Boolean).join(' · ')}
          </div>
        )}
        <h1 className="m-rise m2" style={{
          fontFamily: H1F, fontWeight: H1W, fontStyle: 'italic',
          fontSize: 'clamp(40px,5.5vw,80px)',
          lineHeight: 1.04, letterSpacing: '-0.022em',
          color: titleCol, margin: '0 0 24px',
        }}>
          {title}
        </h1>
        {stan && (
          <p className="m-rise m3 m-stan" style={{
            fontFamily: FU, fontWeight: 300,
            fontSize: 'clamp(15px,1.5vw,19px)',
            lineHeight: 1.7, color: bodyCol,
            margin: '0 auto 28px', maxWidth: 580,
          }}>
            {stan}
          </p>
        )}
        <div className="m-rise m4" style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
          paddingBottom: 'clamp(40px,5vw,64px)',
        }}>
          <div style={{ height: 1, width: 28, background: borderCol, flexShrink: 0 }} />
          <span style={{
            fontFamily: FU, fontSize: 10, letterSpacing: '0.20em',
            textTransform: 'uppercase', color: subtleCol,
          }}>
            {[author && `By ${author}`, fmtDate(post.published_at || post.created_at), post.reading_time && `${post.reading_time} min`]
              .filter(Boolean).join(' · ')}
          </span>
          <ArticleSaveButton slug={post.slug} size={15} C={{ muted: subtleCol }} inline />
          <div style={{ height: 1, width: 28, background: borderCol, flexShrink: 0 }} />
        </div>
        <div style={{ height: 1, background: borderCol }} />
      </div>
    </header>
  );
}

// ── BannerHero, compact editorial banner ─────────────────────────────────────
// Shorter than cinematic (~55vh). Full-bleed image, gradient, title bottom-left.
// Good for secondary features, category openers, roundup articles.
function BannerHero({ post, theme, accent, label, cat, title, stan, author }) {
  const cover = post.cover_image
    || (Array.isArray(post.cover_images) && post.cover_images[0])
    || '';
  const [loaded, setLoaded] = useState(false);
  const { h1Font: H1F, h1Weight: H1W } = getEditorialTypo();

  return (
    <header style={{ paddingTop: 'clamp(40px,5vw,72px)' }}>
      <div style={{
        position: 'relative', width: '100%',
        height: 'clamp(300px,55vh,580px)',
        overflow: 'hidden', background: '#0a0908',
      }}>
        {cover && (
          <img
            src={imgUrl(cover, { width: 1600, resize: 'contain' })}
            alt={title}
            fetchpriority="high"
            loading="eager"
            onLoad={() => setLoaded(true)}
            style={{
              position: 'absolute', inset: 0,
              width: '100%', height: '100%', objectFit: 'cover',
              objectPosition: 'center 35%',
              opacity: loaded ? 0.82 : 0,
              transition: 'opacity 0.7s ease',
            }}
          />
        )}
        <div style={{
          position: 'absolute', inset: 0,
          background: `
            linear-gradient(to top, #0a0908 0%, rgba(10,9,8,0.68) 32%, rgba(10,9,8,0.12) 62%, transparent 100%),
            linear-gradient(to right, rgba(10,9,8,0.55) 0%, transparent 52%)
          `,
          pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          padding: 'clamp(28px,5vw,56px) clamp(24px,7vw,100px)',
          maxWidth: 1000,
        }}>
          {(label || cat) && (
            <div style={{
              fontFamily: FU, fontSize: 9, letterSpacing: '0.26em',
              textTransform: 'uppercase', color: GOLD, marginBottom: 14,
            }}>
              {[label, cat].filter(Boolean).join(' · ')}
            </div>
          )}
          <h1 style={{
            fontFamily: H1F, fontWeight: H1W, fontStyle: 'italic',
            fontSize: 'clamp(30px,4vw,58px)',
            lineHeight: 1.04, letterSpacing: '-0.018em',
            color: '#f5f0e8', margin: '0 0 14px',
            textShadow: '0 2px 20px rgba(0,0,0,0.4)',
          }}>
            {title}
          </h1>
          <div style={{
            fontFamily: FU, fontSize: 9, letterSpacing: '0.18em',
            textTransform: 'uppercase', color: 'rgba(245,240,232,0.5)',
          }}>
            {[author && `By ${author}`, fmtDate(post.published_at || post.created_at), post.reading_time && `${post.reading_time} min`]
              .filter(Boolean).join(' · ')}
          </div>
        </div>
      </div>
    </header>
  );
}

// ── GallerySplitHero, multi-image grid + title chamber ──────────────────────
// Primary image (64%) + thumbnail strip (36%) above a centred title block.
// Thumbnails are clickable to swap the primary. Used for real wedding shoots,
// venue roundups, any article with 3+ cover images.
// Mobile: image at top full-width, thumbnail strip scrolls horizontally.
function GallerySplitHero({ post, theme, accent, label, cat, title, stan, author }) {
  const isLight   = theme === 'light';
  const bg        = isLight ? '#FAF8F4' : '#0a0908';
  const titleCol  = isLight ? '#181410' : '#f5f0e8';
  const bodyCol   = isLight ? 'rgba(24,20,16,0.68)' : 'rgba(245,240,232,0.72)';
  const subtleCol = isLight ? 'rgba(24,20,16,0.48)' : 'rgba(245,240,232,0.44)';
  const borderCol = isLight ? 'rgba(24,20,16,0.08)' : 'rgba(245,240,232,0.08)';
  const allImgs = (() => {
    const arr = Array.isArray(post.cover_images) ? post.cover_images.filter(Boolean) : [];
    if (arr.length > 0) return arr;
    return post.cover_image ? [post.cover_image] : [];
  })();
  const [activeIdx, setActiveIdx] = useState(0);
  const primary = allImgs[activeIdx] || allImgs[0] || '';
  const { h1Font: H1F, h1Weight: H1W } = getEditorialTypo();

  return (
    <header className="lwd-gs-hero" style={{ background: bg, paddingTop: 'clamp(40px,5vw,72px)' }}>
      <style>{`
        @keyframes lwd-gs-fade { from{opacity:0} to{opacity:1} }
        .lwd-gs-primary { animation: lwd-gs-fade 0.32s ease forwards; }
        @media (max-width: 640px) {
          .lwd-gs-hero h1 { font-size: clamp(22px, 6.5vw, 34px) !important; }
          /* Less text on mobile so the hero breathes, hide the standfirst;
             it still opens the article body. */
          .lwd-gs-hero .gs-stan { display: none !important; }
        }
        @media (max-width: 768px) {
          .lwd-gs-grid { flex-direction: column !important; height: auto !important; }
          .lwd-gs-primary-wrap { flex: none !important; height: 66vw !important; width: 100% !important; }
          .lwd-gs-thumbs { flex: none !important; flex-direction: row !important; height: 18vw !important; width: 100% !important; overflow-x: auto !important; overflow-y: hidden !important; }
          .lwd-gs-thumbs > div { flex: 0 0 18vw !important; height: 18vw !important; }
        }
      `}</style>

      {primary && (
        <div className="lwd-gs-grid" style={{
          display: 'flex', gap: 3,
          height: 'clamp(340px,58vh,660px)',
          width: '100vw',
          marginLeft: 'calc(50% - 50vw)',
        }}>
          <div className="lwd-gs-primary-wrap" style={{
            flex: allImgs.length > 1 ? '0 0 64%' : '0 0 100%',
            overflow: 'hidden', background: '#0a0908',
          }}>
            <img
              key={primary}
              src={imgUrl(primary, { width: 1400, resize: 'contain' })}
              alt={title}
              fetchpriority="high"
              loading="eager"
              className="lwd-gs-primary"
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          </div>
          {allImgs.length > 1 && (
            <div className="lwd-gs-thumbs" style={{
              flex: '0 0 calc(36% - 3px)',
              display: 'flex', flexDirection: 'column', gap: 3, overflow: 'hidden',
            }}>
              {allImgs.filter((_, i) => i !== activeIdx).slice(0, 3).map((src) => {
                const realIdx = allImgs.indexOf(src);
                return (
                  <div
                    key={src}
                    onClick={() => setActiveIdx(realIdx)}
                    style={{
                      flex: 1, overflow: 'hidden', cursor: 'pointer',
                      background: '#0a0908', position: 'relative',
                    }}
                  >
                    <img
                      src={imgUrl(src, { width: 600, resize: 'contain' })}
                      alt=""
                      loading="lazy"
                      style={{
                        width: '100%', height: '100%', objectFit: 'cover', display: 'block',
                        transition: 'opacity 0.2s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.opacity = '0.78'}
                      onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                    />
                    {realIdx === activeIdx && (
                      <div style={{
                        position: 'absolute', inset: 0,
                        border: `2px solid ${GOLD}`,
                        pointerEvents: 'none',
                      }} />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Title chamber */}
      <div style={{
        maxWidth: 820, margin: '0 auto',
        padding: 'clamp(40px,5vw,72px) clamp(20px,4vw,32px) clamp(48px,6vw,80px)',
        textAlign: 'center',
        borderBottom: `1px solid ${borderCol}`,
      }}>
        {(label || cat) && (
          <div style={{
            fontFamily: FU, fontSize: 9, fontWeight: 700,
            letterSpacing: '0.32em', textTransform: 'uppercase',
            color: GOLD, marginBottom: 22,
          }}>
            {[label, cat].filter(Boolean).join(' · ')}
          </div>
        )}
        <h1 style={{
          fontFamily: H1F, fontWeight: H1W, fontStyle: 'italic',
          fontSize: 'clamp(34px,4.6vw,60px)',
          lineHeight: 1.06, letterSpacing: '-0.018em',
          color: titleCol, margin: '0 0 20px',
        }}>
          {title}
        </h1>
        {stan && (
          <p className="gs-stan" style={{
            fontFamily: FU, fontWeight: 300,
            fontSize: 'clamp(14px,1.4vw,18px)',
            lineHeight: 1.68, color: bodyCol,
            margin: '0 auto 26px', maxWidth: 560,
          }}>
            {stan}
          </p>
        )}
        <div aria-hidden style={{
          width: 32, height: 1, background: GOLD, opacity: 0.65, margin: '0 auto 20px',
        }} />
        <div style={{
          fontFamily: FU, fontSize: 10, letterSpacing: '0.20em',
          textTransform: 'uppercase', color: subtleCol,
        }}>
          {[author && `By ${author}`, fmtDate(post.published_at || post.created_at), post.reading_time && `${post.reading_time} min`]
            .filter(Boolean).join(' · ')}
        </div>
      </div>
    </header>
  );
}

// ── CinematicTemplate ─────────────────────────────────────────────────────────

function CinematicTemplate({ post, related, relatedVenues = [], prevArticle = null, nextArticle = null, sealed, onNavigateArticle, onNavigateHome, onNavigateCategory }) {
  // Shared lights state, synced with /the-edit via localStorage + custom event,
  // so the masthead toggle stays consistent across the entire Edit section.
  const [isDark, , toggleLights] = useEditorialLights();
  const recentlyViewed = useRecentlyViewed(post);
  const [heroIdx,    setHeroIdx]    = useState(0);
  const [heroReady,  setHeroReady]  = useState(false);
  const [heroSoundOn, setHeroSoundOn] = useState(false);
  // Scope ref for GalleryLightbox, bounds its DOM scan to the article
  // body so it doesn't grab unrelated .lwd-gallery elements elsewhere.
  const articleBodyRef = useRef(null);
  // Hero scroll-cue auto-fade. Flips data-scrolled on the .lwd-hero element
  // the first time the user scrolls past 80px, which triggers a CSS opacity
  // transition on the scroll cue. One-time, removed after firing.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onScroll = () => {
      if (window.scrollY > 80) {
        document.querySelector('.lwd-hero')?.setAttribute('data-scrolled', '1');
        window.removeEventListener('scroll', onScroll);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Build the hero image array, cover_images takes priority, falls back to cover_image
  const heroImgs = (() => {
    const arr = Array.isArray(post.cover_images) ? post.cover_images.filter(Boolean) : [];
    if (arr.length > 0) return arr;
    return post.cover_image ? [post.cover_image] : [];
  })();

  // Hero video, YouTube or Vimeo embed URL (starts muted, togglable)
  const heroVideoSource = detectVideoSource(post.hero_video_url || '');
  const heroVideoEmbed  = (() => {
    if (!heroVideoSource) return null;
    const m = heroSoundOn ? '0' : '1';
    if (heroVideoSource.type === 'youtube')
      return `https://www.youtube.com/embed/${heroVideoSource.id}?autoplay=1&mute=${m}&loop=1&playlist=${heroVideoSource.id}&controls=0&modestbranding=1&iv_load_policy=3&cc_load_policy=0&cc_lang_pref=none&playsinline=1&rel=0&showinfo=0`;
    if (heroVideoSource.type === 'vimeo')
      return `https://player.vimeo.com/video/${heroVideoSource.id}?autoplay=1&muted=${m}&loop=1&background=${heroSoundOn ? '0' : '1'}&transparent=0&texttrack=false`;
    return null;
  })();

  // Force captions OFF on the editorial hero film via the real YouTube IFrame API
  // (a raw enablejsapi iframe ignores unloadModule without the API handshake, so
  // auto-captions leak through). Reliably removes caption/cc modules, re-killing on loop.
  const heroFilmRef = useRef(null);
  useEffect(() => {
    if (heroVideoSource?.type !== 'youtube' || !heroVideoSource?.id) return undefined;
    let cancelled = false, capTimer = null, player = null;
    const killCaps = (p) => { try { p.unloadModule('captions'); p.unloadModule('cc'); p.setOption('captions', 'track', {}); p.setOption('cc', 'track', {}); } catch {} };
    const ensureApi = () => new Promise((resolve) => {
      if (window.YT && window.YT.Player) return resolve(window.YT);
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => { try { prev && prev(); } catch {} resolve(window.YT); };
      if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
        const s = document.createElement('script'); s.src = 'https://www.youtube.com/iframe_api'; document.head.appendChild(s);
      }
    });
    ensureApi().then((YT) => {
      if (cancelled || !heroFilmRef.current || !YT?.Player) return;
      try {
        player = new YT.Player(heroFilmRef.current, {
          events: {
            onReady: (e) => { killCaps(e.target); if (!capTimer) capTimer = setInterval(() => killCaps(e.target), 1500); },
            onStateChange: (e) => { if (e.data === 1) killCaps(e.target); },
          },
        });
      } catch {}
    });
    return () => { cancelled = true; if (capTimer) clearInterval(capTimer); try { player?.destroy?.(); } catch {} };
  }, [heroVideoSource?.type, heroVideoSource?.id, heroSoundOn]);

  // Auto-advance when multiple hero images
  useEffect(() => {
    if (heroImgs.length <= 1) return;
    const t = setInterval(() => setHeroIdx(i => (i + 1) % heroImgs.length), 5500);
    return () => clearInterval(t);
  }, [heroImgs.length]);

  // Suppress Aura launcher while editorial is active, fire/cleanup custom events
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('lwd:editorial-mode', { detail: { active: true } }));
    return () => {
      window.dispatchEvent(new CustomEvent('lwd:editorial-mode', { detail: { active: false } }));
    };
  }, []);

  const C          = isDark ? DARK : LIGHT;
  const title      = post.title || '';
  const stan       = post.standfirst || post.excerpt || '';
  // Directory features (Our Venues / Our Creatives) carry the masthead byline,
  // "The LWD Edit", not a personal author. Authored pieces (Perspectives etc.)
  // keep their real byline.
  const isDirectoryFeature = ['our-venues', 'our-creatives'].some(id =>
    post.category_slug === id
    || slugifyLabel(post.category_label) === id
    || (post.tags || []).map(resolveTagCategory).includes(id)
  );
  const author     = isDirectoryFeature ? 'The LWD Edit' : (post.author_data?.name || null);
  const label      = post.featured_label || null;
  const accent     = getCategoryAccent(post);
  const cat        = getCategoryLabel(post);

  // H1 font for the hero title, read from admin siteSettings
  const { h1Font: articleH1Font, h1Weight: articleH1Weight } = getEditorialTypo();
  const blocks = Array.isArray(post.content) ? post.content : [];

  // Pull the venue_contact (enquiry form) out of the body so it renders as the
  // final CTA, AFTER the author bio / filed-under / share block, rather than
  // inline at the end of the prose.
  const venueContactBlock = blocks.find(b => b && b.type === 'venue_contact') || null;
  const bodyBlocks = venueContactBlock ? blocks.filter(b => b !== venueContactBlock) : blocks;

  // Hero style, driven by post.hero_style (set in the article editor Hero panel).
  // Values: 'cinematic' | 'editorial' | 'split' | 'minimal' | 'banner' | 'gallery-split'
  // Backward compat: if unset, fall back to first-block detection for vt_section articles.
  const heroStyle = post.hero_style || post.heroStyle || '';
  const firstBlock = blocks.find(b => b && b.type !== 'editor_note' && b.type !== 'credits');
  const useVTHero           = heroStyle === 'editorial' || (!heroStyle && firstBlock?.type === 'vt_section');
  const useSplitHero        = heroStyle === 'split';
  const useMinimalHero      = heroStyle === 'minimal';
  const useBannerHero       = heroStyle === 'banner';
  const useGallerySplitHero = heroStyle === 'gallery-split';
  const useCoverHero        = heroStyle === 'editorial-cover' || heroStyle === 'cover';
  // cinematic = everything else (heroStyle === 'cinematic' or unset non-VT articles)

  // Cover-hero masthead lockup line. NOT "The LWD Edit" (that's already in the site
  // masthead/nav), a positioning signature instead. Explicit override via
  // post.hero_kicker; otherwise derived from the post's own tags so it stays
  // accurate (a Grade II* exclusive-use house is not a 'five-star' hotel).
  const coverKicker = (() => {
    if (post.hero_kicker) return post.hero_kicker;
    const tags = [...(post.style_tags || []), ...(post.tags || [])].map(t => String(t).toLowerCase());
    if (tags.some(t => t.includes('five-star') || t === 'five star')) return 'Five-Star Luxury';
    if (tags.some(t => t.includes('exclusive use'))) return 'Exclusive Use';
    return 'The Luxury Edit';
  })().toUpperCase();

  // Merge all tag types for the tags section
  const allTags = [
    ...(post.tags || []),
    ...(post.location_tags || []).filter(t => !(post.tags || []).includes(t)),
    ...(post.style_tags || []).filter(t => !(post.tags || []).includes(t)),
  ];

  // ── Sticky cover-hero reveal: as the article body slides up over the pinned
  // hero, fade the background film's volume to silence (scroll-linked audio fade).
  // YouTube/Vimeo volume is driven via postMessage (iframe has enablejsapi=1).
  const coverHeroRef = useRef(null);
  useEffect(() => {
    if (!useCoverHero || !heroVideoEmbed) return;
    let raf = 0;
    const apply = () => {
      raf = 0;
      const h = window.innerHeight || 800;
      const p = Math.min(1, Math.max(0, window.scrollY / (h * 0.9)));
      const ifr = coverHeroRef.current?.querySelector('iframe');
      if (!ifr || !ifr.contentWindow) return;
      const vol = Math.round(100 * (1 - p));
      if (heroVideoSource?.type === 'youtube') {
        ifr.contentWindow.postMessage(JSON.stringify({ event: 'command', func: 'setVolume', args: [vol] }), '*');
      } else if (heroVideoSource?.type === 'vimeo') {
        ifr.contentWindow.postMessage(JSON.stringify({ method: 'setVolume', value: vol / 100 }), '*');
      }
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(apply); };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); if (raf) cancelAnimationFrame(raf); };
  }, [useCoverHero, heroVideoEmbed, heroVideoSource]);

  // ── Hide the background film once the pinned hero is fully covered ──────────
  // A playing cross-origin video iframe is promoted to its own GPU compositor
  // layer, which Chrome/Safari paint ABOVE later siblings regardless of z-index.
  // So once the article body slides up over the sticky hero, the film bleeds
  // over the page (a solid dark panel) as the reader scrolls. visibility:hidden
  // does NOT drop the composited surface, only display:none does. The iframe is
  // position:absolute, so toggling it causes no scroll jump.
  //
  // The hero is position:sticky pinned at top:0, so it never leaves the viewport
  // (observing it directly would always report it visible). Instead observe the
  // #lwd-hero-sentinel that sits in normal flow at the hero/body boundary (the
  // same sentinel StickyArticleMeta uses): when it leaves the top of the viewport
  // the body has fully covered the hero. Held in state (not imperative DOM) so
  // the iframe's `key` remount on the sound toggle keeps the right value.
  const [heroCovered, setHeroCovered] = useState(false);
  useEffect(() => {
    if (!useCoverHero || !heroVideoEmbed) return;
    if (typeof IntersectionObserver === 'undefined') return;
    const sentinel = document.querySelector('#lwd-hero-sentinel');
    if (!sentinel) return;
    // A sentinel is "not intersecting" both ABOVE the viewport (hero covered)
    // and BELOW it (still at the top, hero fully visible). Disambiguate by the
    // sign of its top: covered only once it has scrolled above the viewport top.
    const obs = new IntersectionObserver(
      ([entry]) => setHeroCovered(entry.boundingClientRect.top <= 0),
      { threshold: 0 }
    );
    obs.observe(sentinel);
    return () => obs.disconnect();
  }, [useCoverHero, heroVideoEmbed]);

  return (
    <article style={{
      background: C.bg,
      color: C.text,
      transition: 'background 0.4s ease, color 0.4s ease',
    }}>

      {/* ── Sticky left meta panel, fixed in left gutter, wide screens only ── */}
      <StickyArticleMeta
        post={post}
        cat={cat}
        label={label}
        author={author}
        slug={post.slug}
        C={C}
      />

      {/* ── HERO ROUTER ──
          Driven by post.hero_style (set in the article editor).
          editorial   → VTHero   , clean full-bleed image + text chamber below
          split       → SplitHero, 50/50 left text · right image
          minimal     → MinimalHero, text only, no image
          banner      → BannerHero, compact ~55vh banner with overlay
          gallery-split → GallerySplitHero, multi-image grid + title chamber
          cinematic (default) → 100svh dark hero with zoom animation */}
      {useVTHero && (
        <VTHero
          post={post} theme={isDark ? 'dark' : 'light'}
          accent={accent} label={label} cat={cat}
          title={title} stan={stan} author={author}
        />
      )}
      {useSplitHero && (
        <SplitHero
          post={post} theme={isDark ? 'dark' : 'light'}
          accent={accent} label={label} cat={cat}
          title={title} stan={stan} author={author}
        />
      )}
      {useMinimalHero && (
        <MinimalHero
          post={post} theme={isDark ? 'dark' : 'light'}
          accent={accent} label={label} cat={cat}
          title={title} stan={stan} author={author}
        />
      )}
      {useBannerHero && (
        <BannerHero
          post={post} theme={isDark ? 'dark' : 'light'}
          accent={accent} label={label} cat={cat}
          title={title} stan={stan} author={author}
        />
      )}
      {useGallerySplitHero && (
        <GallerySplitHero
          post={post} theme={isDark ? 'dark' : 'light'}
          accent={accent} label={label} cat={cat}
          title={title} stan={stan} author={author}
        />
      )}

      {/* ── Editorial Cover hero, the LWD magazine-cover treatment ──
          Full-bleed image (or film), inset hairline frame, THE LWD EDIT masthead
          lockup, and a restrained centred title over a whisper-soft veil.
          Mirrors _gen_magazine.mjs so the Edit hero reads like the cover. */}
      {useCoverHero && (
      <header
        ref={coverHeroRef}
        className="lwd-hero lwd-cover-hero"
        style={{
          position: 'sticky', top: 0, zIndex: 0, width: '100%',
          height: '100svh', minHeight: 600, overflow: 'hidden',
          background: '#0f120e',
        }}
      >
        <style>{`
          @keyframes lwd-cv-zoom { from { transform: scale(1.0) translateZ(0);} to { transform: scale(1.06) translateZ(0);} }
          @keyframes lwd-cv-rise { from { opacity:0; transform: translateY(18px);} to { opacity:1; transform: translateY(0);} }
          .lwd-cover-hero .cv-img { animation: lwd-cv-zoom 16s ease-in-out infinite alternate; transform-origin: center center; }
          .lwd-cover-hero .cv-rise { opacity:0; animation: lwd-cv-rise 1000ms cubic-bezier(0.2,0.7,0.2,1) forwards; }
          .lwd-cover-hero .cv-rise.c1 { animation-delay: 120ms; }
          .lwd-cover-hero .cv-rise.c2 { animation-delay: 300ms; }
          .lwd-cover-hero .cv-rise.c3 { animation-delay: 480ms; }
          .lwd-cover-hero .cv-rise.c4 { animation-delay: 660ms; }
          .lwd-cover-hero .lwd-scroll-cue { transition: opacity 600ms ease; }
          .lwd-cover-hero[data-scrolled="1"] .lwd-scroll-cue { opacity: 0; }
          @media (max-width: 640px) {
            .lwd-cover-hero h1 { font-size: clamp(24px, 7vw, 40px) !important; line-height: 1.12 !important; }
            /* Less text on mobile so the hero film breathes, hide the standfirst
               and lift the title lockup; the standfirst still opens the article body. */
            .lwd-cover-hero .cv-stan { display: none !important; }
            .lwd-cover-hero .cv-title-lockup { bottom: clamp(56px, 9vh, 96px) !important; }
          }
          /* When a film is playing behind the cover, let the whole overlay
             (veil + masthead + title) breathe: hold, melt away to reveal the
             film, then return. Image heroes stay static (no .cv-breathe). */
          @keyframes lwd-cv-breathe {
            0%, 46%   { opacity: 1; }
            61%, 76%  { opacity: 0; }
            91%, 100% { opacity: 1; }
          }
          .lwd-cover-hero .cv-breathe {
            animation: lwd-cv-breathe 15s ease-in-out 4s infinite;
          }
          /* Once the reader scrolls into the article, stop melting and keep the
             overlay steady (the body is taking over anyway). */
          .lwd-cover-hero[data-scrolled="1"] .cv-breathe { animation: none; opacity: 1; }
          @media (prefers-reduced-motion: reduce) {
            .lwd-cover-hero .cv-img { animation: none; }
            .lwd-cover-hero .cv-rise { animation: none; opacity: 1; }
            .lwd-cover-hero .cv-breathe { animation: none; opacity: 1; }
          }
          /* Sound toggle, site standard: top-right, revealed on hover. On
             touch devices (no hover) it stays visible so it's always reachable. */
          .lwd-hero-sound { opacity: 0; transition: opacity .35s ease, background .2s, border-color .2s; }
          .lwd-cover-hero:hover .lwd-hero-sound,
          .lwd-cover-hero:focus-within .lwd-hero-sound { opacity: 1; }
          @media (hover: none) { .lwd-hero-sound { opacity: 1 !important; } }
        `}</style>

        {/* Background film (optional) */}
        {heroVideoEmbed && (
          <>
            <iframe
              key={heroSoundOn ? 'sound' : 'muted'}
              ref={heroFilmRef}
              src={`${heroVideoEmbed}&enablejsapi=1`}
              title="Hero video" frameBorder="0"
              allow="autoplay; fullscreen" allowFullScreen
              style={{
                position: 'absolute', top: '50%', left: '50%',
                width:  'max(100%, calc(100vh * 16 / 9))',
                height: 'max(100%, calc(100vw * 9 / 16))',
                transform: 'translate(-50%, -50%)', border: 'none',
                pointerEvents: 'none', opacity: 0.92,
                // Sits above the cover image, which is always rendered underneath as the
                // fallback for a film that never paints.
                zIndex: 1,
                // Drop the composited surface once the hero is fully covered so
                // the film can't bleed over the article body on scroll.
                display: heroCovered ? 'none' : 'block',
              }}
            />
            <button
              className="lwd-hero-sound"
              onClick={() => setHeroSoundOn(s => !s)}
              aria-label={heroSoundOn ? 'Mute video' : 'Unmute video'}
              style={{
                position: 'absolute', top: 20, right: 24, zIndex: 10,
                background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                border: '1px solid rgba(201,169,110,0.3)', borderRadius: 20,
                padding: '7px 14px', display: 'flex', alignItems: 'center', gap: 7,
                cursor: 'pointer',
              }}
            >
              <span style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 12 }}>
                {[1, 0.5, 0.8, 0.3, 0.7].map((h, i) => (
                  <span key={i} style={{
                    width: 2, height: heroSoundOn ? `${h * 100}%` : '30%',
                    background: '#c9a96e', borderRadius: 1, display: 'block',
                    transition: heroSoundOn ? `height 0.${3 + i}s ease ${i * 0.08}s` : 'height 0.3s ease',
                  }} />
                ))}
              </span>
              <span style={{
                fontFamily: FU, fontSize: 9, fontWeight: 700, letterSpacing: '0.16em',
                textTransform: 'uppercase',
                color: heroSoundOn ? '#c9a96e' : 'rgba(245,240,232,0.5)',
                transition: 'color 0.2s',
              }}>
                {heroSoundOn ? 'Sound On' : 'Sound Off'}
              </span>
            </button>
          </>
        )}

        {/* Background image. ALWAYS rendered, including behind a hero film.
            It used to be `!heroVideoEmbed && heroImgs[0]`, so an article with a hero video had
            NO poster layer at all: if the film did not paint, for any reason, the hero was a
            black rectangle. Browsers refuse muted autoplay often enough that this was the
            normal case rather than the edge case, and it shipped live on real articles.
            The film sits on top at z-index 1 and covers this when it does play, and this is
            what remains when it does not. */}
        {heroImgs[0] && (
          <img
            className="cv-img"
            src={imgUrl(heroImgs[heroIdx] || heroImgs[0], { width: 1800, resize: 'contain' })}
            alt={title || ''}
            fetchpriority="high" loading="eager"
            onLoad={() => setHeroReady(true)}
            style={{
              position: 'absolute', inset: 0, width: '100%', height: '100%',
              objectFit: 'cover', objectPosition: 'center 34%',
              opacity: heroReady ? 1 : 0, transition: 'opacity 1.4s ease',
              zIndex: 0,
            }}
          />
        )}

        {/* Whisper veil, legibility only, photo stays pristine */}
        <div aria-hidden className={heroVideoEmbed ? 'cv-breathe' : undefined} style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          background: 'linear-gradient(180deg, rgba(14,16,12,0.56) 0%, rgba(14,16,12,0.12) 24%, rgba(14,16,12,0.04) 48%, rgba(14,16,12,0.46) 78%, rgba(14,16,12,0.84) 100%)',
        }} />

        {/* THE LWD EDIT masthead lockup */}
        <div className={`cv-rise c1${heroVideoEmbed ? ' cv-breathe' : ''}`} style={{
          position: 'absolute', top: 'clamp(54px, 7vw, 96px)', left: 0, right: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16,
        }}>
          <span style={{ width: 'clamp(50px,9vw,110px)', height: 1, background: 'linear-gradient(to left, rgba(201,169,110,0.85), transparent)' }} />
          <span style={{ width: 6, height: 6, background: GOLD, transform: 'rotate(45deg)' }} />
          <span style={{ fontFamily: FD, fontSize: 'clamp(13px,1.4vw,17px)', letterSpacing: '0.3em', textIndent: '0.3em', color: '#f6f1e8', whiteSpace: 'nowrap' }}>{coverKicker}</span>
          <span style={{ width: 6, height: 6, background: GOLD, transform: 'rotate(45deg)' }} />
          <span style={{ width: 'clamp(50px,9vw,110px)', height: 1, background: 'linear-gradient(to right, rgba(201,169,110,0.85), transparent)' }} />
        </div>

        {/* Centred title lockup, restrained */}
        <div className={`cv-title-lockup${heroVideoEmbed ? ' cv-breathe' : ''}`} style={{
          position: 'absolute', left: 0, right: 0, bottom: 'clamp(78px, 11vh, 150px)',
          textAlign: 'center', padding: '0 clamp(28px, 7vw, 90px)',
        }}>
          {(label || cat) && (
            <div className="cv-rise c2" style={{
              fontFamily: FU, fontSize: 11, fontWeight: 600, letterSpacing: '0.3em',
              textTransform: 'uppercase', color: GOLD, marginBottom: 22,
            }}>
              {[label, cat].filter(Boolean).join(' · ')}
            </div>
          )}
          <h1 className="cv-rise c3" style={{
            fontFamily: articleH1Font, fontWeight: articleH1Weight,
            fontSize: 'clamp(40px, 5.2vw, 80px)', lineHeight: 1.04,
            color: '#fbf7ef', margin: 0, letterSpacing: '-0.012em',
            textShadow: '0 2px 30px rgba(0,0,0,0.35)',
          }}>
            {title}
          </h1>
          {stan && (
            <h2 className="cv-rise c3 cv-stan" style={{
              fontFamily: FD, fontStyle: 'italic', fontWeight: 400, fontSize: 'clamp(18px, 1.9vw, 26px)',
              lineHeight: 1.4, color: 'rgba(246,241,232,0.86)',
              margin: '24px auto 0', maxWidth: 620,
            }}>
              {stan}
            </h2>
          )}
          <div aria-hidden className="cv-rise c4" style={{ width: 48, height: 1, background: 'rgba(245,240,232,0.55)', margin: '26px auto 22px' }} />
          <div className="cv-rise c4" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: FU, fontSize: 10, fontWeight: 500, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'rgba(245,240,232,0.62)' }}>
              {[author && `By ${author}`, fmtDate(post.published_at || post.created_at), post.reading_time && `${post.reading_time} min`].filter(Boolean).join(' · ')}
            </span>
            <ArticleSaveButton slug={post.slug} size={15} C={{ muted: 'rgba(245,240,232,0.62)' }} inline />
          </div>
        </div>

        {/* Scroll cue */}
        <div className={`lwd-scroll-cue cv-rise c4${heroVideoEmbed ? ' cv-breathe' : ''}`} style={{
          position: 'absolute', bottom: 26, left: '50%', transform: 'translateX(-50%)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
        }}>
          <span style={{ fontFamily: FU, fontSize: 8, letterSpacing: '0.28em', textTransform: 'uppercase', color: 'rgba(245,240,232,0.5)' }}>Scroll</span>
          <div style={{ width: 1, height: 34, background: 'linear-gradient(to bottom, rgba(245,240,232,0.5), transparent)' }} />
        </div>
      </header>
      )}

      {/* ── Cinematic 100svh hero, always dark regardless of theme toggle ── */}
      {!useVTHero && !useSplitHero && !useMinimalHero && !useBannerHero && !useGallerySplitHero && !useCoverHero && (
      <header
        className="lwd-hero"
        style={{
          position: 'relative',
          width: '100%',
          height: '100svh',
          minHeight: 600,
          overflow: 'hidden',
          background: DARK.bg,
        }}
      >
        {/* ── Cinematic hero animations ──
            Ambient zoom: hero image scales 1.0 → 1.06 over 14s, alternating,
            so the still photograph gets a "moving plate" feel without being
            actual video. Title rise: each text element fades up with a 200ms
            stagger so the headline arrives like a cinema title card. Scroll
            cue: fades out the first time the user scrolls. */}
        <style>{`
          @keyframes lwd-hero-zoom {
            from { transform: scale(1.0)  translateZ(0); }
            to   { transform: scale(1.06) translateZ(0); }
          }
          @keyframes lwd-hero-rise {
            from { opacity: 0; transform: translateY(28px); filter: blur(4px); }
            to   { opacity: 1; transform: translateY(0);     filter: blur(0); }
          }
          .lwd-hero img.lwd-hero-img {
            animation: lwd-hero-zoom 14s ease-in-out infinite alternate;
            transform-origin: center center;
          }
          .lwd-hero .lwd-hero-rise {
            opacity: 0;
            animation: lwd-hero-rise 1100ms cubic-bezier(0.2, 0.7, 0.2, 1) forwards;
          }
          .lwd-hero .lwd-hero-rise.r1 { animation-delay: 80ms; }
          .lwd-hero .lwd-hero-rise.r2 { animation-delay: 280ms; }
          .lwd-hero .lwd-hero-rise.r3 { animation-delay: 480ms; }
          .lwd-hero .lwd-hero-rise.r4 { animation-delay: 680ms; }
          .lwd-hero .lwd-scroll-cue { transition: opacity 600ms ease; }
          .lwd-hero[data-scrolled="1"] .lwd-scroll-cue { opacity: 0; }
          @media (prefers-reduced-motion: reduce) {
            .lwd-hero img.lwd-hero-img { animation: none; }
            .lwd-hero .lwd-hero-rise { animation: none; opacity: 1; }
          }
          /* Sound toggle, site standard: top-right, revealed on hover. */
          .lwd-hero-sound { opacity: 0; transition: opacity .35s ease, background .2s, border-color .2s; }
          .lwd-hero:hover .lwd-hero-sound,
          .lwd-hero:focus-within .lwd-hero-sound { opacity: 1; }
          @media (hover: none) { .lwd-hero-sound { opacity: 1 !important; } }
        `}</style>
        {/* Hero video, YouTube/Vimeo cinematic background (renders below images) */}
        {heroVideoEmbed && (
          <>
            <iframe
              key={heroSoundOn ? 'sound' : 'muted'}
              src={heroVideoEmbed}
              title="Hero video"
              frameBorder="0"
              allow="autoplay; fullscreen"
              allowFullScreen
              style={{
                position: 'absolute',
                top: '50%', left: '50%',
                width:  'max(100%, calc(100vh * 16 / 9))',
                height: 'max(100%, calc(100vw * 9 / 16))',
                transform: 'translate(-50%, -50%)',
                border: 'none',
                pointerEvents: 'none',
                opacity: 0.78,
              }}
            />
            {/* Sound toggle, site standard: top-right pill, revealed on hover */}
            <button
              className="lwd-hero-sound"
              onClick={() => setHeroSoundOn(s => !s)}
              aria-label={heroSoundOn ? 'Mute video' : 'Unmute video'}
              style={{
                position: 'absolute', top: 20, right: 24, zIndex: 10,
                background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                border: '1px solid rgba(201,169,110,0.3)', borderRadius: 20,
                padding: '7px 14px', display: 'flex', alignItems: 'center', gap: 7,
                cursor: 'pointer',
              }}
            >
              <span style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 12 }}>
                {[1, 0.5, 0.8, 0.3, 0.7].map((h, i) => (
                  <span key={i} style={{
                    width: 2, height: heroSoundOn ? `${h * 100}%` : '30%',
                    background: '#c9a96e', borderRadius: 1, display: 'block',
                    transition: heroSoundOn ? `height 0.${3 + i}s ease ${i * 0.08}s` : 'height 0.3s ease',
                  }} />
                ))}
              </span>
              <span style={{
                fontFamily: FU, fontSize: 9, fontWeight: 700, letterSpacing: '0.16em',
                textTransform: 'uppercase',
                color: heroSoundOn ? '#c9a96e' : 'rgba(245,240,232,0.5)',
                transition: 'color 0.2s',
              }}>
                {heroSoundOn ? 'Sound On' : 'Sound Off'}
              </span>
            </button>
          </>
        )}

        {/* Hero images, crossfade when multiple. The active image gets the
            ambient zoom animation. Hidden when video is present. */}
        {!heroVideoEmbed && heroImgs.map((src, i) => (
          <img
            key={src}
            src={imgUrl(src, { width: 1600, resize: 'contain' })}
            alt={i === 0 ? title : ''}
            fetchpriority={i === 0 ? 'high' : 'auto'}
            loading={i === 0 ? 'eager' : 'lazy'}
            onLoad={() => { if (i === 0) setHeroReady(true); }}
            className={i === heroIdx ? 'lwd-hero-img' : ''}
            style={{
              position: 'absolute', inset: 0,
              width: '100%', height: '100%',
              objectFit: 'cover',
              objectPosition: 'center 30%',
              opacity: i === heroIdx ? (heroReady || i > 0 ? 0.82 : 0) : 0,
              transition: 'opacity 1.8s ease',
            }}
          />
        ))}

        {/* Cinematic gradient, kept original two-direction wash but lifted
            the bottom scrim a touch since title is bigger now. */}
        <div style={{
          position: 'absolute', inset: 0,
          background: `
            linear-gradient(to top, ${DARK.bg} 0%, ${DARK.bg}e8 22%, ${DARK.bg}55 52%, transparent 78%),
            linear-gradient(to right, ${DARK.bg}88 0%, transparent 55%)
          `,
          pointerEvents: 'none',
        }} />

        {/* Hero is now nav-free, the lights toggle has moved up into the
            masthead (next to ← Directory) so the photograph and headline
            have the entire frame. */}

        {/* Hero text, bottom left, staggered rise on load */}
        <div style={{
          position: 'absolute',
          bottom: 0, left: 0, right: 0,
          padding: 'clamp(40px, 7vw, 100px) clamp(24px, 8vw, 120px)',
          display: 'flex', flexDirection: 'column', gap: 22,
          maxWidth: 1120,
        }}>
          {/* Label + category */}
          <div className="lwd-hero-rise r1" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {label && (
              <span style={{
                fontFamily: FU, fontSize: 9, letterSpacing: '0.28em',
                textTransform: 'uppercase', color: accent,
                background: `${accent}18`, border: `1px solid ${accent}40`,
                padding: '4px 10px', borderRadius: 2,
              }}>
                {label}
              </span>
            )}
            {cat && (
              <span style={{
                fontFamily: FU, fontSize: 9, letterSpacing: '0.24em',
                textTransform: 'uppercase', color: DARK.muted,
              }}>
                {cat}
              </span>
            )}
          </div>

          {/* Title, italic editorial scale. Big enough to feel like a
              magazine spread, small enough to read clean. Sits in the
              5-8.5vw band rather than the 9vw silliness. */}
          <h1 className="lwd-hero-rise r2" style={{
            fontFamily: articleH1Font, fontWeight: articleH1Weight,
            fontStyle: 'italic',
            fontSize: 'clamp(44px, 6.4vw, 104px)',
            lineHeight: 1.02,
            color: DARK.text,
            margin: 0,
            letterSpacing: '-0.02em',
            textShadow: '0 2px 28px rgba(0,0,0,0.35)',
          }}>
            {title}
          </h1>

          {/* Standfirst, the H2 subtitle beneath the single H1 title */}
          {stan && (
            <h2 className="lwd-hero-rise r3" style={{
              fontFamily: FS, fontSize: 'clamp(15px, 1.5vw, 20px)', fontWeight: 400,
              lineHeight: 1.65, color: DARK.body, margin: 0, maxWidth: 640,
            }}>
              {stan}
            </h2>
          )}

          {/* Byline */}
          <div className="lwd-hero-rise r4" style={{ display: 'flex', alignItems: 'center', gap: 14, paddingTop: 4, flexWrap: 'wrap' }}>
            {author && (
              <span style={{
                fontFamily: FU, fontSize: 10, letterSpacing: '0.18em',
                textTransform: 'uppercase', color: DARK.muted,
              }}>
                By {author}
              </span>
            )}
            <span style={{ color: DARK.subtle, fontSize: 12 }}>·</span>
            <span style={{
              fontFamily: FU, fontSize: 10, letterSpacing: '0.16em', color: DARK.subtle,
            }}>
              {fmtDate(post.published_at || post.created_at)}
            </span>
            {post.reading_time && (
              <>
                <span style={{ color: DARK.subtle, fontSize: 12 }}>·</span>
                <span style={{
                  fontFamily: FU, fontSize: 10, letterSpacing: '0.14em', color: DARK.subtle,
                }}>
                  {post.reading_time} min
                </span>
              </>
            )}
            <ArticleSaveButton slug={post.slug} size={16} C={{ muted: DARK.muted }} inline />
          </div>
        </div>

        {/* Scroll cue, fades out after the first scroll */}
        <div className="lwd-scroll-cue lwd-hero-rise r4" style={{
          position: 'absolute', bottom: 28, right: 'clamp(24px, 6vw, 80px)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
        }}>
          <span style={{
            fontFamily: FU, fontSize: 8, letterSpacing: '0.28em',
            textTransform: 'uppercase', color: DARK.subtle, writingMode: 'vertical-rl',
          }}>
            Scroll
          </span>
          <div style={{
            width: 1, height: 40,
            background: `linear-gradient(to bottom, ${DARK.subtle}, transparent)`,
          }} />
        </div>

        {/* Hero slideshow dots, visible only when multiple images */}
        {heroImgs.length > 1 && (
          <div style={{
            position: 'absolute', bottom: 28, left: '50%', transform: 'translateX(-50%)',
            display: 'flex', gap: 7, alignItems: 'center', zIndex: 10,
          }}>
            {heroImgs.map((_, i) => (
              <button
                key={i}
                onClick={() => setHeroIdx(i)}
                aria-label={`Hero image ${i + 1}`}
                style={{
                  width:      i === heroIdx ? 22 : 6,
                  height:     6,
                  borderRadius: 3,
                  background: i === heroIdx ? GOLD : 'rgba(255,255,255,0.35)',
                  border:     'none',
                  cursor:     'pointer',
                  padding:    0,
                  transition: 'all 0.4s ease',
                }}
              />
            ))}
          </div>
        )}
      </header>
      )}

      {/* ── Article body, theme-aware ──
          ref'd so GalleryLightbox can scope its DOM scan to the body
          (avoids matching .lwd-gallery elements outside the article). */}
      {/* Sentinel at the hero/body boundary, drives StickyArticleMeta reveal
          (works for the pinned cover-hero, which never leaves the viewport). */}
      <div id="lwd-hero-sentinel" aria-hidden="true" style={{ height: 1 }} />

      <div ref={articleBodyRef} style={{
        position: 'relative',
        zIndex: 1,
        paddingTop: 'clamp(56px, 7vw, 96px)',
        paddingBottom: 'clamp(48px, 6vw, 80px)',
        background: C.bg,
        transition: 'background 0.4s ease',
      }}>
        {/* ── body_wysiwyg block CSS ── Reader-side rendering for the
            Phase 1 inserter blocks (gallery, video, pullquote, cover,
            buttons, columns, spacer, social embeds). Mirrors the editor
            CSS so the page looks identical to what was edited. */}
        <style>{`
          .lwd-wysiwyg p { margin: 0 0 1.6em; }
          .lwd-wysiwyg h2 { margin: 2em 0 0.6em; font-size: clamp(24px, 2.8vw, 38px); font-weight: ${getEditorialTypo().h2Weight}; line-height: 1.15; font-family: ${getEditorialTypo().h2Font}; }
          .lwd-wysiwyg h3 { margin: 1.6em 0 0.4em; font-size: clamp(19px, 2vw, 26px); font-weight: ${getEditorialTypo().h3Weight}; font-family: ${getEditorialTypo().h3Font}; }
          .lwd-wysiwyg blockquote {
            border-left: 2px solid ${GOLD};
            margin: 2.4em 0;
            padding: 0.4em 0 0.4em 1.4em;
            font-style: italic;
            color: ${C.text};
          }
          .lwd-wysiwyg .lwd-pullquote {
            border-left: 3px solid ${GOLD};
            margin: 2em 0;
            padding: 0.6em 0 0.6em 1.6em;
            font-style: italic;
            font-size: clamp(22px, 2.4vw, 32px);
            line-height: 1.4;
            color: ${C.text};
          }
          /* Inline images get a soft drop-shadow elevation so they read as
             magazine plates rather than flat blog inserts. Excluded from
             full-bleed / parallax / immersive / cover / fullwidth breakout
             layouts (those are deliberately edge-to-edge with no shadow). */
          .lwd-wysiwyg img {
            max-width: 100%;
            height: auto;
            display: block;
            border-radius: 2px;
            margin: 1.6em 0;
            box-shadow: 0 8px 36px rgba(0,0,0,0.18), 0 1px 4px rgba(0,0,0,0.08);
          }
          .lwd-wysiwyg .lwd-fullwidth img,
          .lwd-wysiwyg .lwd-parallax img,
          .lwd-wysiwyg .lwd-immersive img,
          .lwd-wysiwyg .lwd-cover img,
          .lwd-wysiwyg .lwd-gallery img,
          .lwd-wysiwyg .lwd-slider img {
            box-shadow: none;
          }
          .lwd-wysiwyg .lwd-video-figure { margin: 2em 0; }
          .lwd-wysiwyg .lwd-video-figure video { width: 100%; display: block; border-radius: 2px; }
          .lwd-wysiwyg .lwd-gallery img { margin: 0; height: 100%; object-fit: cover; }
          .lwd-wysiwyg .lwd-spacer { height: var(--lwd-spacer-h, 48px); }
          .lwd-wysiwyg .lwd-embed { box-shadow: 0 4px 24px rgba(0,0,0,0.25); border-radius: 2px; overflow: hidden; }
          .lwd-wysiwyg .lwd-cover { box-shadow: 0 6px 32px rgba(0,0,0,0.4); }

          /* ── Wider YouTube + uploaded video, responsive 16:9, full body width ── */
          .lwd-wysiwyg div[data-youtube-video],
          .lwd-wysiwyg .lwd-youtube-wrap {
            position: relative;
            width: 100%;
            aspect-ratio: 16 / 9;
            margin: 2em 0;
            background: #000;
            border-radius: 2px;
            overflow: hidden;
            box-shadow: 0 6px 28px rgba(0,0,0,0.3);
          }
          .lwd-wysiwyg iframe {
            width: 100%;
            height: 100%;
            border: 0;
          }
          .lwd-wysiwyg div[data-youtube-video] iframe,
          .lwd-wysiwyg iframe.lwd-youtube {
            position: absolute;
            inset: 0;
            width: 100%;
            height: 100%;
          }

          /* ── Full-width image, breakout from body column, crop tall photos ──
             Negative-margin breakout (more reliable than 100vw + translateX
             which overshoots when the page has a vertical scrollbar). The
             margin pulls the figure out to viewport edges of the editorial
             body container. object-fit:cover crops portrait photos so they
             don't dominate the viewport. */
          .lwd-wysiwyg .lwd-fullwidth {
            position: relative;
            margin-left:  calc(50% - 50vw) !important;
            margin-right: calc(50% - 50vw) !important;
            margin-top:    64px;
            margin-bottom: 64px;
            width: auto !important;
            max-width: none !important;
            padding: 0;
            background: #0a0a0a;
            overflow: hidden;
          }
          .lwd-wysiwyg .lwd-fullwidth img {
            width: 100%;
            object-fit: cover;
            object-position: center;
            display: block;
            margin: 0;
            border-radius: 0;
          }
          /* Height-preset bands, pick via the toolbar height buttons or
             insert with a default. Each preset uses clamp() so the height
             scales sensibly across viewport sizes. 'auto' falls back to
             the image's natural aspect ratio (no crop). */
          .lwd-wysiwyg .lwd-fullwidth-compact   img { height: clamp(220px, 38vh, 480px); }
          .lwd-wysiwyg .lwd-fullwidth-standard  img { height: clamp(320px, 58vh, 720px); }
          .lwd-wysiwyg .lwd-fullwidth-tall      img { height: clamp(420px, 78vh, 920px); }
          .lwd-wysiwyg .lwd-fullwidth-auto      img { height: auto; object-fit: contain; }
          /* Default for any element without a preset class (legacy content) */
          .lwd-wysiwyg .lwd-fullwidth:not([class*="lwd-fullwidth-"]) img,
          .lwd-wysiwyg .lwd-fullwidth img:not([data-height]) { height: clamp(320px, 58vh, 720px); }

          /* ── Parallax image, fixed-attachment background, full-bleed ── */
          .lwd-wysiwyg .lwd-parallax {
            position: relative;
            margin-left:  calc(50% - 50vw) !important;
            margin-right: calc(50% - 50vw) !important;
            margin-top:    64px;
            margin-bottom: 64px;
            width: auto !important;
            background-size: cover;
            background-position: center;
            background-attachment: fixed;
          }
          /* iOS Safari ignores fixed-attachment; fall back to scroll so the
             image still shows rather than appearing broken. */
          @supports (-webkit-touch-callout: none) {
            .lwd-wysiwyg .lwd-parallax { background-attachment: scroll; }
          }

          /* ── Immersive, full-bleed cinematic image with subtle gradients ── */
          .lwd-wysiwyg .lwd-immersive {
            position: relative;
            margin-left:  calc(50% - 50vw);
            margin-right: calc(50% - 50vw);
            margin-top:    96px;
            margin-bottom: 96px;
            width: auto;
            height: 90vh;
            min-height: 520px;
            background: #0a0a0a;
            overflow: hidden;
          }
          .lwd-wysiwyg .lwd-immersive img {
            position: absolute; inset: 0;
            width: 100%; height: 100%;
            object-fit: cover;
            margin: 0;
            border-radius: 0;
          }

          /* ── Slider gallery, horizontal scroll-snap, click → lightbox ──
             Slides snap to centre with a peek of the next/prev image so the
             user knows there's more to see. Cursor turns into zoom-in over
             each image because GalleryLightbox attaches a click handler.
             Hover-zoom on each slide adds depth without feeling animated. */
          .lwd-wysiwyg .lwd-slider {
            scrollbar-width: none;
            -ms-overflow-style: none;
            scroll-padding: 0 10vw;
            padding: 0 8vw 16px;
            margin-left:  calc(50% - 50vw);
            margin-right: calc(50% - 50vw);
            width: auto;
          }
          .lwd-wysiwyg .lwd-slider::-webkit-scrollbar { display: none; }
          .lwd-wysiwyg .lwd-slider-slide {
            flex: 0 0 70%;
            max-width: 880px;
            scroll-snap-align: center;
            margin: 0;
            position: relative;
            overflow: hidden;
            border-radius: 2px;
          }
          .lwd-wysiwyg .lwd-slider-slide img {
            margin: 0;
            border-radius: 2px;
            transition: transform 0.6s cubic-bezier(0.2, 0.8, 0.2, 1);
            cursor: zoom-in;
          }
          .lwd-wysiwyg .lwd-slider-slide:hover img {
            transform: scale(1.025);
          }

          /* ── Grid gallery, luxe hover, zoom cursor ── */
          .lwd-wysiwyg .lwd-gallery {
            margin: 48px 0;
          }
          .lwd-wysiwyg .lwd-gallery-item {
            margin: 0;
            position: relative;
            overflow: hidden;
            aspect-ratio: 4 / 5;
            border-radius: 2px;
            background: #0a0a0a;
          }
          .lwd-wysiwyg .lwd-gallery-item img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            margin: 0;
            transition: transform 0.6s cubic-bezier(0.2, 0.8, 0.2, 1), opacity 0.3s;
            cursor: zoom-in;
          }
          .lwd-wysiwyg .lwd-gallery-item:hover img {
            transform: scale(1.05);
          }
          .lwd-wysiwyg ul, .lwd-wysiwyg ol { padding-left: 1.6em; margin: 0 0 1.6em; }
          .lwd-wysiwyg li { margin-bottom: 0.3em; }
          /* Tiptap wraps each <li> child in a <p>, strip its margin so
             list items don't look double-spaced on the published page. */
          .lwd-wysiwyg li > p { margin: 0; }
          .lwd-wysiwyg a { color: ${GOLD}; text-decoration: underline; }
          .lwd-wysiwyg hr { border: none; border-top: 1px solid rgba(245,240,232,0.1); margin: 2.4em 0; }
        `}</style>
        <ArticleBody body={bodyBlocks} C={C} post={post} />
      </div>

      {/* Gallery lightbox, scans the article body for .lwd-gallery and
          .lwd-slider elements and wires click-to-open on each image. */}
      <GalleryLightbox scopeRef={articleBodyRef} key={`gallery-${blocks?.length || 0}`} />


      {/* All post-body sections wrapped in one opaque z-index:1 layer so the
          pinned (sticky) cover-hero never peeks through at the foot of the
          article. Positioned-sticky paints above later *static* siblings, so
          these tail sections need their own stacking context to cover it. */}
      <div style={{ position: 'relative', zIndex: 1, background: C.bg }}>

      {/* ── Author bio ── */}
      <div style={{ background: C.bg, transition: 'background 0.4s ease' }}>
        <ArticleAuthor post={post} C={C} masthead={isDirectoryFeature} />
      </div>

      {/* ── Tags (all: tags + location_tags + style_tags) ── */}
      {allTags.length > 0 && (
        <div style={{
          padding: '0 clamp(20px, 4vw, 56px) clamp(28px, 3vw, 40px)',
          maxWidth: 1080,
          margin: '0 auto',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 7,
          alignItems: 'center',
          background: C.bg,
          transition: 'background 0.4s ease',
        }}>
          <span style={{
            fontFamily: FU, fontSize: 8, letterSpacing: '0.26em',
            textTransform: 'uppercase', color: C.subtle, marginRight: 6,
          }}>
            Filed under
          </span>
          {allTags.map(tag => {
            const catId = resolveTagCategory(tag);
            return (
              <button
                key={tag}
                onClick={() => catId ? onNavigateCategory(catId) : undefined}
                style={{
                  fontFamily: FU, fontSize: 8, letterSpacing: '0.16em',
                  textTransform: 'uppercase',
                  color: catId ? GOLD : C.muted,
                  background: 'none',
                  border: `1px solid ${catId ? GOLD + '35' : C.border}`,
                  padding: '4px 10px', cursor: catId ? 'pointer' : 'default',
                  borderRadius: 2, transition: 'border-color 0.2s, color 0.2s',
                }}
                onMouseEnter={e => { if (catId) { e.currentTarget.style.borderColor = GOLD + '88'; } }}
                onMouseLeave={e => { if (catId) { e.currentTarget.style.borderColor = GOLD + '35'; } }}
              >
                {tag}
              </button>
            );
          })}
        </div>
      )}

      {/* ── Social share ── */}
      <div style={{ background: C.bg, transition: 'background 0.4s ease' }}>
        <SocialShare post={post} C={C} />
      </div>

      {/* ── Venue enquiry form, final CTA, below the author / filed-under / share.
            Full-bleed (no max-width wrapper), as it was when it lived in the body. ── */}
      {venueContactBlock && <VenueContactBlock block={venueContactBlock} C={C} />}

      {/* ── Subscribe, same NewsletterEditorial as the homepage, mounted with
            source='article-foot' for analytics. Hidden on the article foot for
            now (it dominated the end of the piece); still lives on the homepage
            and magazine home. Flip SHOW_ARTICLE_FOOT_NEWSLETTER to bring it back,
            or replace with a slide-out drawer later. ── */}
      {!sealed && SHOW_ARTICLE_FOOT_NEWSLETTER && <NewsletterEditorial source="article-foot" />}

      {/* ── Prev/Next, same category, immediate neighbours only ── */}
      {!sealed && (
        <PrevNextNav
          prev={prevArticle}
          next={nextArticle}
          onNavigateArticle={onNavigateArticle}
          C={C}
        />
      )}

      {/* ── Recently Viewed + You May Also Like ── cross-article links out of the
          feature; hidden in the sealed preview reading view. */}
      {!sealed && (
        <RecentlyViewed
          items={recentlyViewed}
          onNavigateArticle={onNavigateArticle}
          C={C}
        />
      )}

      {!sealed && (
        <AlsoInThisIssue
          posts={related}
          onNavigateArticle={onNavigateArticle}
          C={C}
        />
      )}

      {!sealed && <RelatedVenues venues={relatedVenues} C={C} />}

      </div>

      {/* Slide-out newsletter, replaces the full-height article-foot block */}
      {!sealed && <NewsletterDrawer />}

    </article>
  );
}

// ── Template router ───────────────────────────────────────────────────────────

function TemplateRouter({ post, related, ...props }) {
  // All templates fall through to Cinematic until others are built
  return <CinematicTemplate post={post} related={related} {...props} />;
}

// ── EditorialArticlePage (main export) ───────────────────────────────────────

export default function EditorialArticlePage({
  slug,
  onNavigateArticle,
  onNavigateHome,
  onNavigateCategory,
  footerNav,
}) {
  const [post,    setPost]    = useState(null);
  const [related, setRelated] = useState([]);
  const [relatedVenues, setRelatedVenues] = useState([]);
  const [prevArticle, setPrevArticle] = useState(null);
  const [nextArticle, setNextArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(false);
  // Lights toggle, required by Masthead. The hook returns
  // [isDark, setLights, toggleLights]. Was previously only called inside
  // CinematicTemplate, but Masthead lives at this top-level component
  // where isDark and toggleLights need to be in scope. Without this hook,
  // line ~1746 throws "isDark is not defined" and the whole page crashes
  // (visible as a black screen on /the-edit/<slug>).
  const [isDark, , toggleLights] = useEditorialLights();
  usePrerenderReady(!loading && !!post);

  // Sealed reading view: when this article is opened as the "proof" link inside a
  // private review (?preview=1, or a live preview session), suppress the site nav,
  // the editorial masthead and the cross-article footer so a prospect reads the
  // feature without any path out into the rest of the directory.
  const sealed = (() => {
    if (typeof window === 'undefined') return false;
    try { if (sessionStorage.getItem('preview_session')) return true; } catch { /* partitioned */ }
    try { return new URLSearchParams(window.location.search).get('preview') === '1'; } catch { return false; }
  })();

  useEffect(() => {
    if (!slug) { setError(true); setLoading(false); return; }
    setLoading(true);
    setError(false);

    async function load() {
      let data = null;
      let err  = null;

      try {
        let query = supabase
          .from('magazine_posts')
          .select(`
            id, slug, title, standfirst, excerpt, cover_image, cover_images,
            category_slug, category_label, tags,
            template_type, featured_label, hero_style,
            editorial_priority, published, published_at, created_at,
            reading_time, location_tags, style_tags, mentions_json,
            author_data, hero_video_url, ai_metadata,
            seo_title, meta_description, cover_image_alt, tone,
            related_listing_ids
          `)
          .eq('slug', slug);
        // Public route serves published only. The sealed private-preview reading
        // view (?preview=1, or an active preview session) is how a cold-call
        // prospect is shown their not-yet-published feature as proof-of-concept —
        // same any-status-behind-a-slug pattern already used for magazine issues
        // (see PublicationsEmbedPage / fetchIssueBySlug). Never relax this outside
        // the sealed branch: that would make every draft article publicly readable.
        if (!sealed) query = query.eq('published', true);
        const res = await query.maybeSingle();
        data = res.data;
        err  = res.error;
      } catch (e) {
        err = e;
      }

      if (err) console.error('[EditorialArticlePage] query error', slug, err);
      if (!data && !err) console.warn('[EditorialArticlePage] no row found for slug:', slug);

      if (err || !data) {
        setError(true);
        setLoading(false);
        return;
      }

      // Related content (LWD Linking System, Tier 2) is below the fold and only needs the
      // post, kick it off concurrently and let it stream in. It must NOT gate first paint.
      fetchRelatedForPost(data, { editLimit: 10, venueLimit: 6 })
        .then(({ relatedEdits, relatedVenues: rv }) => { setRelated(relatedEdits); setRelatedVenues(rv); })
        .catch((e) => console.warn('[EditorialArticlePage] related content failed', e));

      // Same-category prev/next (2026-09-12, approved plan: Option 1 — same
      // category, editorial_priority then published_at, immediate neighbors
      // only, omit the missing side rather than crossing categories, never
      // site-wide chronological, no manual curation). Same ordering the
      // category hub itself uses (EditorialHomePage.jsx), so "next" here is
      // literally the next card on that hub — one source of truth for order.
      setPrevArticle(null);
      setNextArticle(null);
      if (data.category_slug) {
        supabase
          .from('magazine_posts')
          .select('id, slug, title, cover_image, cover_image_alt')
          .eq('category_slug', data.category_slug)
          .eq('published', true)
          .order('editorial_priority', { ascending: true, nullsFirst: false })
          .order('published_at', { ascending: false })
          .then(({ data: siblings, error: sibErr }) => {
            if (sibErr || !siblings) return;
            const i = siblings.findIndex(p => p.id === data.id);
            if (i === -1) return;
            setPrevArticle(i > 0 ? siblings[i - 1] : null);
            setNextArticle(i < siblings.length - 1 ? siblings[i + 1] : null);
          })
          .catch((e) => console.warn('[EditorialArticlePage] prev/next fetch failed', e));
      }

      // Fetch content blocks from magazine_blocks (needed to render the article).
      const { data: blocks } = await supabase
        .from('magazine_blocks')
        .select('id, block_type, block_content, block_order')
        .eq('post_id', data.id)
        .order('block_order', { ascending: true });

      const postData = {
        ...data,
        content: (blocks || []).map(b => ({ type: b.block_type, _blockId: b.id, ...b.block_content })),
      };
      setPost(postData);
      track('article_view', {
        vendor_id: null,
        category:  data.category_label || data.category_slug || null,
        article_id: data.id,
        title:      data.title || null,
        reading_time: data.reading_time || null,
      });

      // Article is ready, drop the spinner now; related content fills in when it resolves.
      setLoading(false);
    }

    load();
  }, [slug, sealed]);

  if (loading) {
    return (
      <div style={{
        minHeight: '100svh', background: DARK.bg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: FU, fontSize: 10, letterSpacing: '0.3em',
        textTransform: 'uppercase', color: DARK.subtle,
      }}>
        Loading
      </div>
    );
  }

  if (error || !post) {
    // A not-found / unpublished editorial slug (drafts, deleted, or the m2 prototype
    // fixtures) must never be indexed. State declared explicitly: 200 + noindex,follow,
    // and these slugs are already out of the sitemap. See Architecture Manual §7 (route
    // states — every route declares indexable / non-indexable / redirected / non-existent).
    return (
      <>
      <Helmet>
        <title>Article not found · The LWD Edit</title>
        <meta name="robots" content="noindex,follow" />
      </Helmet>
      <div style={{
        minHeight: '100svh', background: DARK.bg,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: 20,
      }}>
        <p style={{ fontFamily: FD, fontSize: 28, color: DARK.muted, margin: 0 }}>
          Article not found
        </p>
        <button
          onClick={onNavigateHome}
          style={{
            fontFamily: FU, fontSize: 9, letterSpacing: '0.24em',
            textTransform: 'uppercase', color: GOLD,
            background: 'none', border: `1px solid ${GOLD}44`,
            padding: '10px 22px', cursor: 'pointer',
          }}
        >
          ← Back to The LWD Edit
        </button>
      </div>
      </>
    );
  }

  // One canonical identity for editorial articles: /lwd-edit/{slug} (the real route).
  // No category segment (that path 301s away), matching the sitemap, internal links and
  // the JSON-LD url. See Architecture/LWD_Architecture_Manual.md §7 (URL rules).
  const editorialCanonical = post.slug
    ? `${SITE_URL}/lwd-edit/${post.slug}`
    : undefined;

  // ── Search Identity Engine (Pass 3): single source of truth. Honours the article's
  //    headline (never truncated — matches the H1), enforces the brand + length rules. ──
  const _eng = resolveIdentity(`/lwd-edit/${post.slug || ''}`, {
    cms: { seo_title: post.seo_title, seo_description: post.meta_description || post.seo_description },
    entity: { title: post.title, standfirst: post.standfirst, excerpt: post.excerpt },
  });

  // GEO/AEO: the article's `faq` block(s) already render as a visible Q&A list
  // (see the `type === 'faq'` case in renderArticleBlock) — mirror the same
  // question/answer pairs into FAQPage JSON-LD so AI answer engines can extract
  // them as structured facts, not just prose. Same items array shape the
  // renderer reads: block.items, or a single {question, answer} pair.
  const faqSchema = buildFaqPageSchema(
    (Array.isArray(post.content) ? post.content : [])
      .filter(b => b?.type === 'faq')
      .flatMap(b => b.items || (b.question ? [{ question: b.question, answer: b.answer }] : []))
  );

  return (
    <>
      <Helmet>
        <title>{_eng.title}</title>
        <meta name="description" content={_eng.description || post.meta_description || post.seo_description || post.standfirst || post.excerpt || ''} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={_eng.ogTitle} />
        {(post.meta_description || post.seo_description || post.standfirst || post.excerpt) && (
          <meta property="og:description" content={post.meta_description || post.seo_description || post.standfirst || post.excerpt} />
        )}
        {post.cover_image && <meta property="og:image" content={imgUrl(post.cover_image, { width: 1200, resize: 'contain' })} />}
        {editorialCanonical && <meta property="og:url" content={editorialCanonical} />}
        {editorialCanonical && <link rel="canonical" href={editorialCanonical} />}
        <meta name="twitter:card"  content="summary_large_image" />
        <meta name="twitter:title" content={post.seo_title || post.title} />
        {(post.meta_description || post.seo_description || post.standfirst || post.excerpt) && (
          <meta name="twitter:description" content={post.meta_description || post.seo_description || post.standfirst || post.excerpt} />
        )}
        {post.cover_image && <meta name="twitter:image" content={imgUrl(post.cover_image, { width: 1200, resize: 'contain' })} />}
      </Helmet>
      <JsonLd schema={buildArticleSchema({
        ...post,
        hero_image: post.cover_image,
        category_slug: post.category_slug,
        slug: post.slug,
      })} />
      {faqSchema && <JsonLd schema={faqSchema} />}

      {/* ── Directory layer (commerce / wayfinding, sticky always) ──
          Hidden in the sealed preview reading view so the prospect cannot
          navigate out into the wider directory. */}
      {!sealed && <DirectoryNav onNavigateHome={onNavigateHome} />}

      {/* The LWD Edit masthead, same canonical header as /the-edit:
          LWD · THE LWD EDIT (issue label) · [☀/☾] ← Directory
          with the category nav strip below (All / Destinations / Venues / …).
          The lights toggle is article-only (passed via props) so it appears
          beside ← Directory only on individual articles, not on the index.
          Suppressed in the sealed preview reading view (its category strip +
          ← Directory link are exits out of the review). */}
      {!sealed && (
        <Masthead
          activeId={post.category_slug}
          onNavigateHome={onNavigateHome}
          onNavigateCategory={onNavigateCategory}
          isDark={isDark}
          onToggleLights={toggleLights}
        />
      )}

      <ReadingProgress />

      <TemplateRouter
        post={post}
        related={sealed ? [] : related}
        relatedVenues={sealed ? [] : relatedVenues}
        prevArticle={sealed ? null : prevArticle}
        nextArticle={sealed ? null : nextArticle}
        sealed={sealed}
        onNavigateArticle={onNavigateArticle}
        onNavigateHome={onNavigateHome}
        onNavigateCategory={onNavigateCategory}
      />
    </>
  );
}
